import React, { useState, useEffect, createContext, useContext } from 'react';
import './App.css';
import './LandingPage.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import LandingPage from './LandingPage';
import PublicMenu from './PublicMenu';
import KanbanBoard from './components/KanbanBoard';
import PixPaymentModal from './components/PixPaymentModal';
import PDVScreen from './PDVScreen';
import { OccupationTimer, TablesDashboard, TableFilters } from './TableManagementImproved';
import CouponsContent from './CouponsContent';
import AppearanceSettings from './AppearanceSettings';
// import WhatsAppCRM from './WhatsAppCRM'; // Removido - funcionalidade inacabada
import GreetingMessageSettings from './components/GreetingMessageSettings';
import AwayMessageSettings from './components/AwayMessageSettings';
import { useWhatsAppIntegration, generateWhatsAppLink } from './hooks/useWhatsAppIntegration';
import { 
  MasterRestaurantsContent, 
  MasterAnalyticsContent, 
  MasterReportsContent, 
  MasterTemplatesContent, 
  MasterActionLogsContent,
  MasterSystemSettingsContent,
  MasterAIErrorLogsContent,
  MasterGalleryContent,
  MasterProspectionContent,
  MasterUazAPIContent
} from './MasterPanel';

// Componentes extraídos (refatoração)
import { MyPlanContent, OrdersContent } from './components/content';
import DeliveryDriversContent from './components/content/DeliveryDriversContent';
import CashRegisterContent from './components/content/CashRegisterContent';

// Subdomain detection utility
import { extractSubdomain, isStoreSubdomain } from './utils/subdomain';

import { 
  ChefHat, 
  Zap, 
  ShoppingCart, 
  Package, 
  Tag, 
  Plus, 
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Search,
  Sun,
  Moon,
  Waves,
  Bell,
  User,
  LogOut,
  PlusCircle,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Filter,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  Palette,
  ExternalLink,
  Star,
  Download,
  Upload,
  FileSpreadsheet,
  FileText,
  Truck,
  MapPin,
  Store,
  Shield,
  CheckSquare,
  TrendingUp,
  MoreVertical,
  Printer,
  Copy,
  Sliders,
  UserCog,
  QrCode,
  Users,
  ArrowLeft,
  CreditCard,
  Banknote,
  Smartphone,
  UserCircle,
  UsersRound,
  Ticket,
  MessageSquare,
  Send,
  Phone,
  Wallet,
  Home,
  RefreshCw,
  RefreshCcw,
  Bot,
  Calendar,
  X,
  Layers,
  Check,
  Key,
  Image as ImageIcon,
  Bike,
  AlertTriangle,
  Navigation,
  Sparkles,
  Target
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './components/ui/dialog';
import { Switch } from './components/ui/switch';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './components/ui/dropdown-menu';

// Detectar automaticamente a URL do backend baseado no hostname
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  
  // Se estiver em meucardapiozap.top, usar essa URL
  if (hostname === 'meucardapiozap.top' || hostname === 'www.meucardapiozap.top') {
    return 'https://meucardapiozap.top';
  }
  
  // Se estiver em preview do Emergent
  if (hostname.includes('.preview.emergentagent.com')) {
    return `https://${hostname}`;
  }
  
  // Se estiver em produção do Emergent (.emergent.host)
  if (hostname.includes('.emergent.host')) {
    // Força usar meucardapiozap.top em produção
    return 'https://meucardapiozap.top';
  }
  
  // Fallback para variável de ambiente ou localhost
  return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Debug: Log da URL da API
console.log('🔧 BACKEND_URL:', BACKEND_URL);
console.log('🔧 API:', API);
console.log('🔧 Hostname:', window.location.hostname);

// Interceptor de requisição - SEMPRE adiciona o token mais recente do localStorage
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Color options for categories
const COLOR_OPTIONS = [
  { value: '#ef4444', label: 'Vermelho', color: '#ef4444' },
  { value: '#f59e0b', label: 'Laranja', color: '#f59e0b' },
  { value: '#eab308', label: 'Amarelo', color: '#eab308' },
  { value: '#22c55e', label: 'Verde', color: '#22c55e' },
  { value: '#06b6d4', label: 'Ciano', color: '#06b6d4' },
  { value: '#2ECC71', label: 'Azul', color: '#2ECC71' },
  { value: '#8b5cf6', label: 'Roxo', color: '#8b5cf6' },
  { value: '#ec4899', label: 'Rosa', color: '#ec4899' },
  { value: '#6b7280', label: 'Cinza', color: '#6b7280' }
];

// Auth Context
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateAndRestoreSession = async () => {
      const token = localStorage.getItem('token');
      const savedRestaurant = localStorage.getItem('restaurant');
      
      if (token && savedRestaurant) {
        try {
          // Set token in axios headers
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Para master admin, usar os dados salvos sem validar com /me
          // (porque /me exige restaurant_id que master não tem)
          const restaurantData = JSON.parse(savedRestaurant);
          if (restaurantData.is_master) {
            setUser({ token });
            setRestaurant(restaurantData);
            console.log('✅ Sessão master restaurada (sem validação /me)');
            setLoading(false);
            return;
          }
          
          // Para usuários normais, validar com /me
          try {
            const response = await axios.get(`${API}/me`);
            setUser({ token });
            setRestaurant(response.data);
            console.log('✅ Sessão restaurada com sucesso');
          } catch (meError) {
            // Se /me falhar, usar dados salvos (pode estar offline ou servidor ocupado)
            console.log('⚠️ /me falhou, usando dados salvos:', meError.message);
            setUser({ token });
            setRestaurant(restaurantData);
          }
        } catch (error) {
          // Erro ao parsear dados salvos - limpar tudo
          console.log('⚠️ Erro ao restaurar sessão:', error.message);
          localStorage.removeItem('token');
          localStorage.removeItem('restaurant');
          localStorage.removeItem('isImpersonating');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };
    
    validateAndRestoreSession();
  }, []);

  const login = async (email, password) => {
    try {
      console.log('Attempting login with:', email);
      const response = await axios.post(`${API}/login`, { email, password });
      console.log('Login response:', response.data);
      const { access_token, restaurant } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('restaurant', JSON.stringify(restaurant));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser({ token: access_token });
      setRestaurant(restaurant);
      console.log('Login successful, redirecting...');
      return true;
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  };

  const register = async (name, subdomain, email, password, isMaster = false) => {
    try {
      console.log('Attempting registration with:', { name, subdomain, email, is_master: isMaster });
      const response = await axios.post(`${API}/register`, { 
        name, subdomain, email, password, is_master: isMaster
      });
      console.log('Registration response:', response.data);
      const { access_token, restaurant } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('restaurant', JSON.stringify(restaurant));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser({ token: access_token });
      setRestaurant(restaurant);
      console.log('Registration successful, redirecting...');
      return true;
    } catch (error) {
      console.error('Register error:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('restaurant');
    localStorage.removeItem('isImpersonating');  // Remove impersonation flag
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setRestaurant(null);
  };

  // Auto-persist restaurant data to localStorage whenever it changes
  useEffect(() => {
    if (restaurant) {
      localStorage.setItem('restaurant', JSON.stringify(restaurant));
      console.log('💾 Dados do restaurante salvos automaticamente');
    }
  }, [restaurant]);

  // Refresh restaurant data periodically and on focus to keep it in sync
  useEffect(() => {
    if (!user?.token) return;

    const refreshRestaurantData = async () => {
      try {
        const response = await axios.get(`${API}/me`);
        setRestaurant(response.data);
        console.log('🔄 Dados do restaurante atualizados automaticamente');
      } catch (error) {
        console.log('⚠️ Erro ao atualizar dados do restaurante:', error.message);
      }
    };

    // Initial refresh on mount
    refreshRestaurantData();

    // Refresh restaurant data every 2 minutes
    const interval = setInterval(refreshRestaurantData, 2 * 60 * 1000);

    // Refresh when window gains focus (user returns to tab)
    const handleFocus = () => {
      console.log('👀 Janela ganhou foco, atualizando dados...');
      refreshRestaurantData();
    };
    
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user]);

  // Heartbeat to keep session alive - ping server every 30 minutes
  useEffect(() => {
    if (!user?.token) return;

    const heartbeat = async () => {
      try {
        await axios.get(`${API}/me`);
        console.log('💓 Heartbeat: sessão mantida ativa');
      } catch (error) {
        console.log('💔 Heartbeat falhou, sessão pode ter expirado');
      }
    };

    // Run heartbeat every 30 minutes
    const interval = setInterval(heartbeat, 30 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      restaurant, 
      setUser,
      setRestaurant,
      login, 
      register, 
      logout, 
      loading 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Public Menu Route Component (path-based: /menu/:subdomain)
const PublicMenuRoute = () => {
  const { subdomain } = useParams();
  
  // You can fetch restaurant data based on subdomain here
  // For now, we'll use the PublicMenu component directly
  
  return <PublicMenu subdomain={subdomain} />;
};

// Subdomain-based Public Menu Component (for {slug}.meucardapiozap.shop)
const SubdomainMenuRoute = () => {
  const subdomain = extractSubdomain();
  
  if (!subdomain) {
    // Shouldn't happen, but fallback to landing page
    return <Navigate to="/" replace />;
  }
  
  return <PublicMenu subdomain={subdomain} />;
};

// Login Component
const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    email: '',
    password: '',
    isMaster: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [masterAvailable, setMasterAvailable] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Verificar se master está disponível
  useEffect(() => {
    const checkMasterAvailable = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/check-master-available`);
        setMasterAvailable(response.data.available);
      } catch (error) {
        console.error('Erro ao verificar master:', error);
        setMasterAvailable(false);
      }
    };
    
    if (!isLogin) {
      checkMasterAvailable();
    }
  }, [isLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await register(formData.name, formData.subdomain, formData.email, formData.password, formData.isMaster);
      }
      
      // Navigate to dashboard after successful login/register
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao processar solicitação');
    }
    
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)',
      padding: '16px'
    }}>
      
      <div style={{
        background: 'white',
        padding: '48px',
        borderRadius: '16px',
        boxShadow: '0 20px 48px rgba(0, 0, 0, 0.2)',
        width: '100%',
        maxWidth: '450px',
        position: 'relative'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <div style={{ position: 'relative' }}>
              <ChefHat style={{ width: '48px', height: '48px', color: '#2ECC71' }} />
              <Zap style={{ width: '24px', height: '24px', color: '#fbbf24', position: 'absolute', top: '-4px', right: '-4px' }} />
            </div>
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>
            {isLogin ? 'Bem-vindo ao Meucardapiozap!' : 'Criar Conta'}
          </h1>
          <p style={{ color: '#6b7280' }}>
            {isLogin ? 'Acesse seu painel de controle' : 'Comece agora gratuitamente'}
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {!isLogin && (
            <>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  Nome do Restaurante
                </label>
                <input
                  type="text"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '15px'
                  }}
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Seu Restaurante"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                  Subdomínio
                </label>
                <input
                  type="text"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '15px'
                  }}
                  value={formData.subdomain}
                  onChange={(e) => setFormData({...formData, subdomain: e.target.value})}
                  placeholder="meurestaurante"
                />
                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                  Seu cardápio: /menu/{formData.subdomain || 'seu-subdominio'}
                </p>
              </div>
            </>
          )}
          
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              Email
            </label>
            <input
              type="email"
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '15px'
              }}
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="seu@email.com"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              Senha
            </label>
            <input
              type="password"
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '15px'
              }}
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              placeholder="••••••••"
            />
          </div>

          {!isLogin && masterAvailable && (
            <div style={{
              padding: '16px',
              background: '#fef3c7',
              border: '2px solid #f59e0b',
              borderRadius: '8px',
              marginTop: '8px'
            }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                gap: '12px'
              }}>
                <input
                  type="checkbox"
                  checked={formData.isMaster}
                  onChange={(e) => setFormData({...formData, isMaster: e.target.checked})}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer'
                  }}
                />
                <div>
                  <div style={{ 
                    fontSize: '15px', 
                    fontWeight: '600', 
                    color: '#92400e',
                    marginBottom: '4px'
                  }}>
                    🔑 Criar como Gestor Master
                  </div>
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#92400e'
                  }}>
                    Primeira conta com acesso total ao sistema. Disponível apenas uma vez!
                  </div>
                </div>
              </label>
            </div>
          )}

          {error && (
            <div style={{
              padding: '12px',
              background: '#fee2e2',
              color: '#991b1b',
              borderRadius: '8px',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: loading ? '#9ca3af' : '#2ECC71',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s'
            }}
          >
            {loading ? 'Processando...' : (isLogin ? 'Entrar' : 'Criar Conta')}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <button
            onClick={() => setIsLogin(!isLogin)}
            style={{
              color: '#2ECC71',
              fontWeight: '500',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'color 0.2s'
            }}
          >
            {isLogin ? 'Não tem conta? Registre-se' : 'Já tem conta? Faça login'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  // FORCE CACHE INVALIDATION 20260120
  // IMPORTANT: useAuth must be the first hook call to ensure user/restaurant are available
  const { user, logout, restaurant, setRestaurant, setUser } = useAuth();
  
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [showPixPaymentModal, setShowPixPaymentModal] = useState(false); // Use existing PixPaymentModal
  const [subscriptionPrice, setSubscriptionPrice] = useState(99.00); // Preço da assinatura do sistema
  const [isCloseOrderModalOpen, setIsCloseOrderModalOpen] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false); // 🆕 Elevado para Dashboard
  const [isPDVOpen, setIsPDVOpen] = useState(false);
  
  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [masterData, setMasterData] = useState(null); // Store master admin data when impersonating

  // Check if user is master admin
  const isMasterAdmin = restaurant?.is_master === true;
  
  // Check if restaurant is expired or suspended
  const isExpired = restaurant?.is_expired === true;
  const isSuspended = restaurant?.is_suspended === true;
  const isRestricted = isExpired || isSuspended;

  // Store status (open/closed manually)
  const [storeStatus, setStoreStatus] = useState({
    manually_closed: false,
    is_open: true,
    loading: false
  });

  // AI Quick Toggle status (atalho no header)
  const [aiQuickToggle, setAiQuickToggle] = useState({
    enabled: false,
    loading: false
  });

  // WhatsApp Integration Hook
  const whatsappIntegration = useWhatsAppIntegration();

  // Set default active section based on user type
  const [activeSection, setActiveSection] = useState(isMasterAdmin ? 'restaurants' : 'orders');

  const menuItems = isMasterAdmin ? [
    { id: 'restaurants', label: 'Gestão de Lojas', icon: Store, gradient: 'from-emerald-500 to-cyan-500' },
    { id: 'analytics', label: 'Análises & Métricas', icon: TrendingUp, gradient: 'from-emerald-500 to-teal-500' },
    { id: 'reports', label: 'Relatórios Financeiros', icon: FileText, gradient: 'from-orange-500 to-amber-500' },
    { id: 'ai-errors', label: 'Erros da IA', icon: AlertTriangle, gradient: 'from-red-500 to-orange-500' },
    { id: 'templates', label: 'Templates', icon: CheckSquare, gradient: 'from-cyan-500 to-emerald-500' },
    { id: 'gallery', label: 'Galeria de Imagens', icon: ImageIcon, gradient: 'from-pink-500 to-rose-500' },
    { id: 'prospection', label: 'Prospecção Ativa', icon: Target, gradient: 'from-violet-500 to-purple-500' },
    { id: 'uazapi', label: 'UazAPI WhatsApp', icon: MessageSquare, gradient: 'from-green-500 to-teal-500' },
    { id: 'action-logs', label: 'Histórico', icon: Clock, gradient: 'from-indigo-500 to-emerald-600' },
    { id: 'system-settings', label: 'Configurações do Sistema', icon: Settings, gradient: 'from-purple-500 to-pink-500' }
  ] : [
    { id: 'orders', label: 'Pedidos', icon: ShoppingCart, gradient: 'from-orange-500 to-amber-500' },
    { id: 'cash-register', label: 'Caixa', icon: Wallet, gradient: 'from-green-500 to-emerald-500' },
    { id: 'tables', label: 'Controle de Mesas', icon: Store, gradient: 'from-indigo-500 to-violet-500' },
    { id: 'products', label: 'Produtos', icon: Package, gradient: 'from-emerald-500 to-teal-500' },
    { id: 'categories', label: 'Categorias', icon: Tag, gradient: 'from-violet-500 to-purple-500' },
    { id: 'additional-groups', label: 'Grupos de Adicionais', icon: Package, gradient: 'from-cyan-500 to-emerald-500' },
    { id: 'additionals', label: 'Adicionais', icon: Plus, gradient: 'from-amber-500 to-orange-500' },
    { id: 'delivery-zones', label: 'Bairros e Taxas', icon: Truck, gradient: 'from-teal-500 to-cyan-500' },
    { id: 'delivery-drivers', label: 'Entregadores', icon: Bike, gradient: 'from-purple-500 to-violet-500' },
    { id: 'customers', label: 'Clientes', icon: User, gradient: 'from-indigo-500 to-emerald-600' },
    { id: 'cashback', label: 'Cashback', icon: Star, gradient: 'from-yellow-500 to-amber-500' },
    { id: 'coupons', label: 'Cupons', icon: Ticket, gradient: 'from-purple-500 to-pink-500' },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, gradient: 'from-green-500 to-emerald-500' },
    { id: 'appearance', label: 'Aparência', icon: Palette, gradient: 'from-pink-500 to-rose-500' },
    { id: 'my-plan', label: 'Meu Plano', icon: Star, gradient: 'from-emerald-500 to-indigo-500' },
    { id: 'reports', label: 'Relatórios', icon: TrendingUp, gradient: 'from-green-500 to-emerald-600' },
    { id: 'settings', label: 'Configurações', icon: Settings, gradient: 'from-slate-500 to-gray-600' }
  ];

  // Show expired modal when restaurant is expired/suspended
  useEffect(() => {
    if (!isMasterAdmin && isRestricted) {
      setShowExpiredModal(true);
    }
  }, [isRestricted, isMasterAdmin]);

  // Fetch subscription price when expired modal is shown
  useEffect(() => {
    const fetchSubscriptionPrice = async () => {
      if (showExpiredModal || showPixPaymentModal) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/restaurant/plan-info`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data?.subscription_price) {
            setSubscriptionPrice(response.data.subscription_price);
          }
        } catch (error) {
          console.error('Error fetching subscription price:', error);
          // Keep default 99.00 on error
        }
      }
    };
    fetchSubscriptionPrice();
  }, [showExpiredModal, showPixPaymentModal]);

  // Handle successful payment - refresh restaurant data
  const handlePaymentSuccess = async () => {
    try {
      const response = await axios.get(`${API}/me`);
      if (response.data) {
        setRestaurant(response.data.restaurant);
      }
      setShowExpiredModal(false);
      setShowPixPaymentModal(false);
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };
  
  // Axios interceptor to handle 401 (unauthorized) and 403 (expired/suspended) errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        // Handle 401 - Token expired or invalid
        if (error.response?.status === 401) {
          const requestUrl = error.config?.url || '';
          
          // NUNCA fazer logout automático para essas rotas
          const ignoredRoutes = [
            '/public/',
            '/login',
            '/register',
            '/check-master',
            '/whatsapp/status',  // Master admin não tem restaurant_id
            '/me'  // Pode falhar durante validação inicial
          ];
          
          const shouldIgnore = ignoredRoutes.some(route => requestUrl.includes(route));
          
          if (shouldIgnore) {
            console.log('⏭️ Ignorando 401 para rota:', requestUrl);
            return Promise.reject(error);
          }
          
          // Para outras rotas, apenas logar mas NÃO fazer logout automático
          // O usuário será redirecionado para login se tentar acessar área protegida
          console.log('⚠️ 401 em rota protegida:', requestUrl);
          return Promise.reject(error);
        }
        
        // Handle 403 - Expired/Suspended restaurant (não para master admin)
        if (error.response?.status === 403 && !isMasterAdmin) {
          const message = error.response?.data?.detail || 'Acesso negado';
          if (message.includes('expirado') || message.includes('suspensa')) {
            setShowExpiredModal(true);
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [isMasterAdmin]);

  const handleMenuClick = (sectionId) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false); // Close mobile menu when item is selected
  };

  const createSampleData = async () => {
    try {
      const response = await axios.post(`${API}/sample-data`);
      const data = response.data;
      
      // Show detailed success message
      const summary = data.summary;
      const message = `✅ Dados demonstrativos criados com sucesso!\n\n` +
        `📋 Resumo:\n` +
        `• ${summary.categories} Categorias\n` +
        `• ${summary.products} Produtos\n` +
        `• ${summary.additional_groups} Grupos de Adicionais\n` +
        `• ${summary.additionals} Adicionais\n` +
        `• ${summary.orders} Pedidos\n\n` +
        `Você pode recriar os dados a qualquer momento clicando novamente no botão.`;
      
      alert(message);
    } catch (error) {
      console.error('Error creating sample data:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao criar dados de exemplo';
      alert(`❌ Erro: ${errorMsg}`);
    }
  };

  // Store status functions (open/close manually)
  const fetchStoreStatus = async () => {
    if (isMasterAdmin && !isImpersonating) return;
    try {
      const response = await axios.get(`${API}/restaurant/store-status`);
      setStoreStatus({
        manually_closed: response.data.manually_closed,
        is_open: response.data.is_open,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching store status:', error);
    }
  };

  const toggleStoreStatus = async () => {
    if (storeStatus.loading) return;
    setStoreStatus(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await axios.post(`${API}/restaurant/toggle-store-status`);
      setStoreStatus({
        manually_closed: response.data.manually_closed,
        is_open: response.data.is_open,
        loading: false
      });
      
      toast.success(response.data.message, {
        description: response.data.manually_closed 
          ? 'A loja foi fechada manualmente. Clientes não poderão fazer pedidos.'
          : 'A loja foi aberta. Clientes podem fazer pedidos normalmente.',
        duration: 4000
      });
    } catch (error) {
      console.error('Error toggling store status:', error);
      toast.error('Erro ao alterar status da loja', {
        description: error.response?.data?.detail || 'Tente novamente',
        duration: 4000
      });
      setStoreStatus(prev => ({ ...prev, loading: false }));
    }
  };

  // Fetch store status on load
  useEffect(() => {
    if (user && (!isMasterAdmin || isImpersonating)) {
      fetchStoreStatus();
      fetchAiQuickToggleStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isMasterAdmin, isImpersonating]);

  // Fetch AI status for quick toggle
  const fetchAiQuickToggleStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/ai-config`);
      setAiQuickToggle(prev => ({
        ...prev,
        enabled: response.data?.enabled || false
      }));
    } catch (error) {
      console.error('Error fetching AI status:', error);
    }
  };

  // Toggle AI on/off (quick shortcut in header)
  const toggleAiQuick = async () => {
    if (aiQuickToggle.loading) return;
    setAiQuickToggle(prev => ({ ...prev, loading: true }));
    
    try {
      const newStatus = !aiQuickToggle.enabled;
      await axios.put(`${API}/whatsapp/ai-config`, { enabled: newStatus });
      
      setAiQuickToggle({
        enabled: newStatus,
        loading: false
      });
      
      toast.success(
        newStatus ? '🤖 IA Atendente ATIVADA!' : '🤖 IA Atendente desativada',
        {
          description: newStatus 
            ? 'A IA está respondendo clientes no WhatsApp' 
            : 'A IA não responderá mais automaticamente',
          duration: 3000
        }
      );
    } catch (error) {
      console.error('Error toggling AI:', error);
      toast.error('Erro ao alterar status da IA', {
        description: error.response?.data?.detail || 'Tente novamente'
      });
      setAiQuickToggle(prev => ({ ...prev, loading: false }));
    }
  };

  // Impersonation functions
  const handleImpersonate = (impersonationData) => {
    // Save current master admin data
    setMasterData({
      token: localStorage.getItem('token'),
      restaurant: restaurant
    });
    
    // Switch to restaurant view
    const { access_token, restaurant: restaurantData } = impersonationData;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('restaurant', JSON.stringify(restaurantData));
    localStorage.setItem('isImpersonating', 'true');
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    setUser({ token: access_token });
    setRestaurant(restaurantData);
    setIsImpersonating(true);
    setActiveSection('orders');
  };

  const handleExitImpersonate = () => {
    if (!masterData) {
      console.error('No master data found');
      return;
    }
    
    // Restore master admin data
    localStorage.setItem('token', masterData.token);
    localStorage.setItem('restaurant', JSON.stringify(masterData.restaurant));
    localStorage.removeItem('isImpersonating');
    axios.defaults.headers.common['Authorization'] = `Bearer ${masterData.token}`;
    
    setUser({ token: masterData.token });
    setRestaurant(masterData.restaurant);
    setIsImpersonating(false);
    setMasterData(null);
    setActiveSection('restaurants');
  };

  // Check if we're returning from impersonation (page reload)
  useEffect(() => {
    const wasImpersonating = localStorage.getItem('isImpersonating');
    // Only set impersonating if there's a valid session and token
    if (wasImpersonating === 'true' && localStorage.getItem('token')) {
      setIsImpersonating(true);
    } else {
      // Clean up stale impersonation flag if no valid session
      localStorage.removeItem('isImpersonating');
      setIsImpersonating(false);
    }
  }, []);

  return (
    <div className="h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 flex overflow-hidden">
      <div className="floating-elements">
        <div className="floating-circle floating-circle-1"></div>
        <div className="floating-circle floating-circle-2"></div>
        <div className="floating-circle floating-circle-3"></div>
      </div>

      {/* Mobile backdrop */}
      {mobileMenuOpen && !isCloseOrderModalOpen && !isOrderModalOpen && !isPDVOpen && (
        <div 
          className="mobile-backdrop visible md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Oculta quando modal de pedidos, finalizar pedido ou PDV estiver aberto */}
      {!isCloseOrderModalOpen && !isOrderModalOpen && !isPDVOpen && (
        <div className={`sidebar ${collapsed ? 'sidebar-collapsed' : 'sidebar-expanded'} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="flex items-center">
            <div className="relative">
              {isMasterAdmin ? (
                <Shield className="w-8 h-8 text-purple-500" />
              ) : (
                <>
                  <ChefHat className="w-8 h-8 text-orange-500" />
                  <Zap className="w-4 h-4 text-yellow-400 absolute -top-1 -right-1" />
                </>
              )}
            </div>
            {(!collapsed || mobileMenuOpen) && (
              <div className="ml-3">
                <h2 className="text-xl font-bold text-white">Meucardapiozap</h2>
                <p className="text-xs text-gray-400">
                  {isMasterAdmin ? 'Painel Master' : 'Painel Digital'}
                </p>
              </div>
            )}
          </div>
          
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="collapse-button hidden md:flex"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>

          {/* Mobile close button */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="collapse-button md:hidden"
          >
            <ChevronLeft size={16} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMenuClick(item.id)}
              className={`menu-item ${activeSection === item.id ? 'menu-item-active' : ''}`}
              title={collapsed && !mobileMenuOpen ? item.label : ''}
            >
              <div className={`menu-icon bg-gradient-to-r ${item.gradient}`}>
                <item.icon size={20} />
              </div>
              {(!collapsed || mobileMenuOpen) && <span>{item.label}</span>}
              {activeSection === item.id && <div className="active-indicator"></div>}
            </button>
          ))}
        </nav>
        </div>
      )}

      {/* Main Content */}
      <div className="main-content">
        {/* Header - Oculto quando modal de pedidos, finalizar pedido ou PDV estiver aberto */}
        {!isCloseOrderModalOpen && !isOrderModalOpen && !isPDVOpen && (
          <header className="header">
            <div className="flex items-center space-x-4">
              {/* Mobile hamburger menu */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="header-button mobile-menu-pulse md:hidden"
                data-testid="mobile-menu-button"
              >
                <ChevronRight size={18} />
              </button>

              <div className="search-container hidden sm:block">
                <Search className="search-icon" size={18} />
                <input
                  type="text"
                  placeholder="Buscar..."
                  className="search-input"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Store Status Toggle Button - Only for non-master or impersonating */}
              {(!isMasterAdmin || isImpersonating) && (
                <button
                  onClick={toggleStoreStatus}
                  disabled={storeStatus.loading}
                  className={`header-button relative transition-all duration-300 ${
                    storeStatus.loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title={storeStatus.manually_closed ? 'Loja fechada manualmente - Clique para abrir' : 'Loja aberta - Clique para fechar'}
                  data-testid="store-status-toggle"
                >
                  {storeStatus.loading ? (
                    <RefreshCw size={18} className="animate-spin text-gray-400" />
                  ) : (
                    <Store 
                      size={18} 
                      className={`transition-colors duration-300 ${
                        storeStatus.manually_closed 
                          ? 'text-red-400 hover:text-red-300' 
                          : 'text-green-400 hover:text-green-300'
                      }`}
                    />
                  )}
                  {!storeStatus.loading && (
                    <div 
                      className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ${
                        storeStatus.manually_closed ? 'bg-red-500' : 'bg-green-500'
                      } animate-pulse`}
                    />
                  )}
                </button>
              )}
              
              {/* AI Quick Toggle Button - Only for non-master or impersonating */}
              {(!isMasterAdmin || isImpersonating) && (
                <button
                  onClick={toggleAiQuick}
                  disabled={aiQuickToggle.loading}
                  className={`header-button relative transition-all duration-300 ${
                    aiQuickToggle.loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title={aiQuickToggle.enabled ? 'IA Atendente ATIVA - Clique para desativar' : 'IA Atendente desativada - Clique para ativar'}
                  data-testid="ai-quick-toggle"
                >
                  {aiQuickToggle.loading ? (
                    <RefreshCw size={18} className="animate-spin text-gray-400" />
                  ) : (
                    <Bot 
                      size={18} 
                      className={`transition-colors duration-300 ${
                        aiQuickToggle.enabled 
                          ? 'text-violet-400 hover:text-violet-300' 
                          : 'text-gray-500 hover:text-gray-400'
                      }`}
                    />
                  )}
                  {!aiQuickToggle.loading && (
                    <div 
                      className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ${
                        aiQuickToggle.enabled ? 'bg-violet-500 animate-pulse' : 'bg-gray-600'
                      }`}
                    />
                  )}
                </button>
              )}
              
              <div className="flex items-center space-x-2 text-white">
                {isMasterAdmin && !isImpersonating && <Shield size={18} className="text-purple-400" />}
                <span className="text-sm hidden sm:inline">{restaurant?.name}</span>
                <User size={18} />
              </div>
              
              <button onClick={logout} className="header-button text-red-400 hover:text-red-300">
                <LogOut size={18} />
              </button>
            </div>
          </header>
        )}

        {/* Impersonation Banner - Oculto quando modal de finalizar pedido ou PDV estiver aberto */}
        {isImpersonating && !isCloseOrderModalOpen && !isPDVOpen && (
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 flex items-center justify-between shadow-lg">
            <div className="flex items-center space-x-3">
              <Shield size={20} className="animate-pulse" />
              <div>
                <p className="font-semibold">Modo Master: Acessando painel da loja</p>
                <p className="text-sm text-purple-100">Você está visualizando como: {restaurant?.name}</p>
              </div>
            </div>
            <button
              onClick={handleExitImpersonate}
              className="flex items-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors backdrop-blur-sm"
              data-testid="exit-impersonate-button"
            >
              <ChevronLeft size={18} />
              <span className="font-medium">Voltar ao Painel Master</span>
            </button>
          </div>
        )}

        {/* Content Area */}
        <main className="content-area">
          {isMasterAdmin && !isImpersonating ? (
            // Master Admin Content
            <>
              {activeSection === 'restaurants' && <MasterRestaurantsContent onImpersonate={handleImpersonate} />}
              {activeSection === 'analytics' && <MasterAnalyticsContent />}
              {activeSection === 'reports' && <MasterReportsContent />}
              {activeSection === 'ai-errors' && <MasterAIErrorLogsContent />}
              {activeSection === 'templates' && <MasterTemplatesContent />}
              {activeSection === 'action-logs' && <MasterActionLogsContent />}
              {activeSection === 'gallery' && <MasterGalleryContent />}
              {activeSection === 'prospection' && <MasterProspectionContent token={localStorage.getItem('token')} />}
              {activeSection === 'uazapi' && <MasterUazAPIContent />}
              {activeSection === 'system-settings' && <MasterSystemSettingsContent />}
            </>
          ) : (
            // Restaurant Admin Content
            <>
              {activeSection === 'my-plan' && <MyPlanContent restaurant={restaurant} />}
              {activeSection === 'orders' && <OrdersContent isPDVOpen={isPDVOpen} setIsPDVOpen={setIsPDVOpen} />}
              {activeSection === 'cash-register' && <CashRegisterContent />}
              {activeSection === 'tables' && <TableManagementContent isCloseOrderModalOpen={isCloseOrderModalOpen} setIsCloseOrderModalOpen={setIsCloseOrderModalOpen} isOrderModalOpen={isOrderModalOpen} setIsOrderModalOpen={setIsOrderModalOpen} />}
              {activeSection === 'products' && <ProductsContent />}
              {activeSection === 'categories' && <CategoriesContent />}
              {activeSection === 'additional-groups' && <AdditionalGroupsContent />}
              {activeSection === 'additionals' && <AdditionalsContent />}
              {activeSection === 'delivery-zones' && <DeliveryZonesContent />}
              {activeSection === 'delivery-drivers' && <DeliveryDriversContent />}
              {activeSection === 'customers' && <CustomersContent />}
              {activeSection === 'cashback' && <CashbackContent />}
              {activeSection === 'coupons' && <CouponsContent />}
              {activeSection === 'whatsapp' && <WhatsAppContent />}
              {activeSection === 'appearance' && <AppearanceSettings />}
              {activeSection === 'reports' && <ReportsContent />}
              {activeSection === 'settings' && <SettingsContent />}
            </>
          )}
        </main>
      </div>
      
      {/* Modal de Expiração */}
      {showExpiredModal && !isMasterAdmin && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
            overflow: 'auto'
          }}
        >
          <div 
            style={{
              backgroundColor: '#1f2937',
              borderRadius: '16px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              width: '100%',
              maxWidth: '400px',
              margin: 'auto',
              overflow: 'hidden',
              border: '1px solid rgba(249, 115, 22, 0.3)',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            {/* Header com ícone */}
            <div style={{
              background: 'linear-gradient(to right, #f97316, #ef4444)',
              padding: '24px 16px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px auto'
              }}>
                <svg style={{ width: '28px', height: '28px', color: 'white' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', marginBottom: '8px' }}>
                {isSuspended ? '🔒 Loja Suspensa' : '⏰ Período Expirado'}
              </h2>
              <p style={{ color: '#fed7aa', fontSize: '13px' }}>
                {isSuspended ? 'Sua loja foi temporariamente suspensa' : 'O período de uso da sua loja expirou'}
              </p>
            </div>

            {/* Conteúdo */}
            <div style={{ padding: '16px' }}>
              <div style={{
                backgroundColor: 'rgba(249, 115, 22, 0.1)',
                border: '1px solid rgba(249, 115, 22, 0.3)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px'
              }}>
                <p style={{ color: '#d1d5db', textAlign: 'center', fontSize: '13px', lineHeight: '1.5' }}>
                  Para continuar utilizando o sistema, 
                  <span style={{ fontWeight: 'bold', color: '#fb923c' }}> renove sua mensalidade</span>.
                </p>
              </div>

              <div style={{
                backgroundColor: 'rgba(55, 65, 81, 0.5)',
                borderRadius: '8px',
                padding: '12px',
                border: '1px solid #374151',
                marginBottom: '12px'
              }}>
                <h3 style={{ color: 'white', fontWeight: '600', marginBottom: '8px', fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#60a5fa', marginRight: '8px' }}>ℹ️</span>
                  Você ainda pode:
                </h3>
                <ul style={{ color: '#9ca3af', fontSize: '12px', marginLeft: '24px' }}>
                  <li>✓ Visualizar seu painel</li>
                  <li>✓ Ver pedidos e histórico</li>
                  <li>✓ Acessar relatórios</li>
                </ul>
              </div>

              <div style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '12px'
              }}>
                <h3 style={{ color: 'white', fontWeight: '600', marginBottom: '8px', fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#f87171', marginRight: '8px' }}>⚠️</span>
                  Atenção:
                </h3>
                <p style={{ color: '#9ca3af', fontSize: '12px', marginLeft: '24px' }}>
                  Sua loja está <span style={{ fontWeight: 'bold', color: '#f87171' }}>fechada</span> e clientes não podem fazer pedidos.
                </p>
              </div>
            </div>

            {/* Botões */}
            <div style={{ padding: '0 16px 16px 16px' }}>
              {/* Botão Pagar com PIX */}
              <button
                onClick={() => {
                  setShowExpiredModal(false);
                  setShowPixPaymentModal(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  background: 'linear-gradient(to right, #22c55e, #16a34a)',
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '14px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginBottom: '8px'
                }}
              >
                <svg style={{ width: '20px', height: '20px', marginRight: '8px' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93s3.06-7.44 7-7.93v15.86zm2 0V4.07c3.94.49 7 3.85 7 7.93s-3.06 7.44-7 7.93z"/>
                </svg>
                💳 Pagar com PIX
              </button>

              {/* Botão WhatsApp */}
              <a
                href={`https://wa.me/5538999034380?text=${encodeURIComponent('Olá! Preciso renovar a mensalidade da minha loja: ' + restaurant?.name)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: '2px solid #22c55e',
                  color: '#22c55e',
                  fontWeight: '500',
                  padding: '12px',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontSize: '13px',
                  marginBottom: '8px'
                }}
              >
                <svg style={{ width: '18px', height: '18px', marginRight: '8px' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Falar no WhatsApp
              </a>
              
              <button
                onClick={() => setShowExpiredModal(false)}
                style={{
                  width: '100%',
                  backgroundColor: '#374151',
                  color: 'white',
                  fontWeight: '500',
                  padding: '12px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Continuar Visualizando
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pagamento PIX - Reutiliza o componente existente */}
      <PixPaymentModal
        isOpen={showPixPaymentModal}
        onClose={() => {
          setShowPixPaymentModal(false);
          if (isRestricted) {
            setShowExpiredModal(true);
          }
        }}
        paymentData={{
          restaurant_id: restaurant?.id,
          months: 1,
          amount: subscriptionPrice
        }}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

// MyPlanContent foi extraído para ./components/content/MyPlanContent.js

// Categories Content Component
const CategoriesContent = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Dias da semana em português
  const WEEKDAYS = [
    { id: 'segunda', label: 'Segunda' },
    { id: 'terça', label: 'Terça' },
    { id: 'quarta', label: 'Quarta' },
    { id: 'quinta', label: 'Quinta' },
    { id: 'sexta', label: 'Sexta' },
    { id: 'sábado', label: 'Sábado' },
    { id: 'domingo', label: 'Domingo' }
  ];
  
  const ALL_DAYS = WEEKDAYS.map(d => d.id);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#2ECC71',
    position: '',
    available_days: ALL_DAYS
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar se ao menos um dia está selecionado
    if (!formData.available_days || formData.available_days.length === 0) {
      alert('Selecione ao menos um dia da semana');
      return;
    }
    
    try {
      const submitData = {
        ...formData,
        position: formData.position && formData.position.trim() !== '' ? parseInt(formData.position) : null
      };
      
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, submitData);
      } else {
        await axios.post(`${API}/categories`, submitData);
      }
      setIsModalOpen(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', color: '#2ECC71', position: '', available_days: ALL_DAYS });
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      alert(error.response?.data?.detail || 'Erro ao salvar categoria');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      description: category.description || '',
      color: category.color || '#FF6B35',
      position: category.position != null ? category.position.toString() : '',
      available_days: category.available_days || ALL_DAYS
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      try {
        await axios.delete(`${API}/categories/${categoryId}`);
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Erro ao excluir categoria. Verifique se não há produtos associados.');
      }
    }
  };

  const openModal = () => {
    setEditingCategory(null);
    setFormData({ name: '', description: '', color: '#2ECC71', position: '', available_days: ALL_DAYS });
    setIsModalOpen(true);
  };
  
  const toggleDay = (dayId) => {
    const currentDays = formData.available_days || [];
    if (currentDays.includes(dayId)) {
      // Não permitir remover se for o último dia
      if (currentDays.length === 1) {
        alert('A categoria deve estar disponível em pelo menos um dia');
        return;
      }
      setFormData({
        ...formData,
        available_days: currentDays.filter(d => d !== dayId)
      });
    } else {
      setFormData({
        ...formData,
        available_days: [...currentDays, dayId]
      });
    }
  };
  
  const selectAllDays = () => {
    setFormData({ ...formData, available_days: ALL_DAYS });
  };
  
  const selectWeekdays = () => {
    setFormData({ ...formData, available_days: ['segunda', 'terça', 'quarta', 'quinta', 'sexta'] });
  };
  
  const selectWeekend = () => {
    setFormData({ ...formData, available_days: ['sábado', 'domingo'] });
  };
  
  // Função para formatar os dias disponíveis para exibição
  const formatAvailableDays = (days) => {
    if (!days || days.length === 0) return 'Nenhum dia';
    if (days.length === 7) return 'Todos os dias';
    
    const weekdaysOnly = ['segunda', 'terça', 'quarta', 'quinta', 'sexta'];
    const weekendOnly = ['sábado', 'domingo'];
    
    if (days.length === 5 && weekdaysOnly.every(d => days.includes(d))) {
      return 'Dias úteis';
    }
    if (days.length === 2 && weekendOnly.every(d => days.includes(d))) {
      return 'Final de semana';
    }
    
    // Abreviar os dias
    const abbrev = {
      'segunda': 'Seg',
      'terça': 'Ter',
      'quarta': 'Qua',
      'quinta': 'Qui',
      'sexta': 'Sex',
      'sábado': 'Sáb',
      'domingo': 'Dom'
    };
    
    return days.map(d => abbrev[d] || d).join(', ');
  };

  if (loading) {
    return (
      <div className="glass-card">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">Categorias</h1>
        <button onClick={openModal} className="glass-button-primary">
          <PlusCircle size={18} className="mr-2" />
          Nova Categoria
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <div key={category.id} className="glass-card category-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: category.color }}
                >
                  <Tag size={24} className="text-white" />
                </div>
                {category.position && (
                  <div className="flex items-center justify-center w-8 h-8 bg-gray-700 rounded-full">
                    <span className="text-xs font-bold text-white">{category.position}</span>
                  </div>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(category)}
                  className="p-2 text-gray-400 hover:text-emerald-400 transition-colors"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => handleDelete(category.id)}
                  className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">{category.name}</h3>
            <p className="text-gray-400 text-sm mb-2">{category.description}</p>
            
            {/* Exibir dias disponíveis */}
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={14} className="text-emerald-400" />
              <span className="text-xs text-emerald-300">
                {formatAvailableDays(category.available_days)}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {category.products_count || 0} produtos
              </span>
              <span 
                className="px-2 py-1 rounded-full text-xs text-white"
                style={{ backgroundColor: category.color }}
              >
                Ativa
              </span>
            </div>
          </div>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="glass-card text-center py-12">
          <Tag size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Nenhuma categoria encontrada</h3>
          <p className="text-gray-400 mb-6">Crie sua primeira categoria para organizar seus produtos</p>
          <button onClick={openModal} className="glass-button-primary">
            Criar Primeira Categoria
          </button>
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="glass-modal max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white">
              {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-4 pr-2 -mr-2 pb-4" style={{ maxHeight: 'calc(90vh - 140px)', WebkitOverflowScrolling: 'touch' }}>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nome</label>
              <input
                type="text"
                required
                className="glass-input w-full"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Nome da categoria"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
              <textarea
                className="glass-input w-full resize-none"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descrição da categoria"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Posição <span className="text-xs text-gray-500">(opcional)</span>
              </label>
              <input
                type="number"
                className="glass-input w-full"
                value={formData.position}
                onChange={(e) => setFormData({...formData, position: e.target.value})}
                placeholder="Deixe vazio para adicionar ao final"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Cor</label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 ${
                      formData.color === color.value 
                        ? 'border-white scale-110' 
                        : 'border-gray-600 hover:border-gray-400'
                    } transition-all`}
                    style={{ backgroundColor: color.color }}
                    onClick={() => setFormData({...formData, color: color.value})}
                  />
                ))}
              </div>
            </div>
            
            {/* Dias da Semana Disponíveis */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Calendar size={16} className="inline mr-2" />
                Dias Disponíveis
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Em quais dias esta categoria estará visível
              </p>
              
              {/* Botões de atalho */}
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  onClick={selectAllDays}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                    formData.available_days?.length === 7
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Todos
                </button>
                <button
                  type="button"
                  onClick={selectWeekdays}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                    formData.available_days?.length === 5 && 
                    ['segunda', 'terça', 'quarta', 'quinta', 'sexta'].every(d => formData.available_days?.includes(d))
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Seg-Sex
                </button>
                <button
                  type="button"
                  onClick={selectWeekend}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                    formData.available_days?.length === 2 && 
                    ['sábado', 'domingo'].every(d => formData.available_days?.includes(d))
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Sáb-Dom
                </button>
              </div>
              
              {/* Checkboxes dos dias - Grid otimizado para mobile */}
              <div className="grid grid-cols-7 gap-1">
                {WEEKDAYS.map((day) => (
                  <button
                    key={day.id}
                    type="button"
                    onClick={() => toggleDay(day.id)}
                    className={`p-2 rounded-lg text-center transition-all ${
                      formData.available_days?.includes(day.id)
                        ? 'bg-green-600 text-white border-2 border-green-400'
                        : 'bg-gray-700 text-gray-400 border-2 border-gray-600'
                    }`}
                  >
                    <span className="text-[10px] sm:text-xs font-medium block">{day.label.slice(0, 3)}</span>
                  </button>
                ))}
              </div>
              
              {formData.available_days?.length === 0 && (
                <p className="text-xs text-red-400 mt-2">
                  ⚠️ Selecione ao menos um dia
                </p>
              )}
            </div>
            
            {/* Botões de ação */}
            <div className="flex justify-end space-x-3 pt-2 sticky bottom-0 bg-gray-900/95 backdrop-blur-sm py-3 -mx-2 px-2 border-t border-gray-700 mt-4">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button type="submit" className="glass-button-primary">
                {editingCategory ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ========================================
// TABLE MANAGEMENT CONTENT
// ========================================

const TableManagementContent = ({ isCloseOrderModalOpen, setIsCloseOrderModalOpen, isOrderModalOpen, setIsOrderModalOpen }) => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTableFormOpen, setIsTableFormOpen] = useState(false);
  const [isSplitBillModalOpen, setIsSplitBillModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isBulkCreateModalOpen, setIsBulkCreateModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tablesToDelete, setTablesToDelete] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [orderHistory, setOrderHistory] = useState([]);
  const [splitBillData, setSplitBillData] = useState(null);
  const [addingProduct, setAddingProduct] = useState(false);
  const [productAddSuccess, setProductAddSuccess] = useState(false);
  const [showProductImages, setShowProductImages] = useState(false);
  const [closeOrderForm, setCloseOrderForm] = useState({
    paymentMethod: 'dinheiro',
    splitPeople: 1,
    applyServiceFee: true,
    waiterName: '',
    cashReceived: ''
  });
  const [bulkCreateForm, setBulkCreateForm] = useState({
    prefix: 'Mesa',
    startNumber: 1,
    endNumber: 10,
    capacity: 4
  });
  const [tableForm, setTableForm] = useState({
    name: '',
    number: null,
    capacity: 4,
    status: 'livre'
  });
  
  // 🆕 NOVOS STATES PARA MELHORIAS UX
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchTables();
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tables`);
      // Sort tables by number in ascending order
      const sortedTables = response.data.sort((a, b) => {
        const numA = parseInt(a.number) || 0;
        const numB = parseInt(b.number) || 0;
        return numA - numB;
      });
      setTables(sortedTables);
    } catch (error) {
      console.error('Error fetching tables:', error);
      alert('Erro ao carregar mesas');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      // Cache-busting header to ensure fresh data
      const response = await axios.get(`${API}/products`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      // Suportar resposta paginada ou array direto
      const productsData = response.data.items || response.data;
      setProducts(Array.isArray(productsData) ? productsData : []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchOrderHistory = async () => {
    try {
      const response = await axios.get(`${API}/tables/orders/history`);
      setOrderHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleCreateTable = async () => {
    try {
      await axios.post(`${API}/tables`, tableForm);
      alert('Mesa criada com sucesso!');
      setIsTableFormOpen(false);
      setTableForm({ name: '', number: null, capacity: 4, status: 'livre' });
      fetchTables();
    } catch (error) {
      console.error('Error creating table:', error);
      alert('Erro ao criar mesa');
    }
  };

  const handleUpdateTable = async () => {
    try {
      await axios.put(`${API}/tables/${selectedTable.id}`, tableForm);
      toast.success('✅ Mesa atualizada com sucesso!', {
        description: `${tableForm.name} - Status: ${tableForm.status === 'livre' ? 'Livre' : tableForm.status === 'ocupada' ? 'Ocupada' : 'Reservada'}`,
        duration: 3000,
      });
      setIsTableFormOpen(false);
      setSelectedTable(null);
      setTableForm({ name: '', number: null, capacity: 4, status: 'livre' });
      fetchTables();
    } catch (error) {
      console.error('Error updating table:', error);
      toast.error('❌ Erro ao atualizar mesa', {
        description: error.response?.data?.detail || 'Tente novamente',
        duration: 3000,
      });
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta mesa?')) return;
    
    try {
      await axios.delete(`${API}/tables/${tableId}`);
      alert('Mesa excluída com sucesso!');
      fetchTables();
    } catch (error) {
      console.error('Error deleting table:', error);
      alert(error.response?.data?.detail || 'Erro ao excluir mesa');
    }
  };

  const handleOpenOrder = async (table) => {
    try {
      const response = await axios.post(`${API}/tables/${table.id}/open-order`, {
        table_id: table.id
      });
      setSelectedTable(table);
      setCurrentOrder(response.data.order);
      setSelectedCategory('all'); // Reset category filter
      setIsOrderModalOpen(true);
      fetchTables();
    } catch (error) {
      console.error('Error opening order:', error);
      alert(error.response?.data?.detail || 'Erro ao abrir comanda');
    }
  };

  const handleViewOrder = async (table) => {
    try {
      const response = await axios.get(`${API}/tables/${table.id}/current-order`);
      if (response.data.order) {
        setSelectedTable(table);
        setCurrentOrder(response.data.order);
        setSelectedCategory('all'); // Reset category filter
        setIsOrderModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    }
  };

  const handleAddItemToOrder = async (productId, quantity = 1) => {
    // Mostrar feedback visual imediatamente (UI otimista)
    setAddingProduct(true);
    setProductAddSuccess(false);
    
    try {
      const response = await axios.post(
        `${API}/tables/${selectedTable.id}/add-item`,
        {
          product_id: productId,
          quantity: quantity,
          additionals: [],
          notes: ''
        }
      );
      
      // Atualizar dados
      setCurrentOrder(response.data.order);
      fetchTables();
      
      // Mostrar feedback de sucesso
      setProductAddSuccess(true);
      
      // Esconder após mostrar sucesso
      setTimeout(() => {
        setAddingProduct(false);
        setProductAddSuccess(false);
      }, 1500);
    } catch (error) {
      console.error('Error adding item:', error);
      setAddingProduct(false);
      setProductAddSuccess(false);
      alert('Erro ao adicionar item');
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      const response = await axios.delete(
        `${API}/tables/${selectedTable.id}/remove-item/${itemId}`
      );
      setCurrentOrder(response.data.order);
      fetchTables();
    } catch (error) {
      console.error('Error removing item:', error);
      alert('Erro ao remover item');
    }
  };

  const handleCloseOrder = async () => {
    try {
      await axios.post(`${API}/tables/${selectedTable.id}/close-order`, {
        payment_method: closeOrderForm.paymentMethod,
        apply_service_fee: closeOrderForm.applyServiceFee,
        waiter_name: closeOrderForm.waiterName || undefined,
        split_people: parseInt(closeOrderForm.splitPeople)
      });
      
      const paymentMethodLabel = closeOrderForm.paymentMethod === 'dinheiro' ? 'Dinheiro' : closeOrderForm.paymentMethod === 'cartao' ? 'Cartão' : 'PIX';
      const changeAmount = closeOrderForm.paymentMethod === 'dinheiro' && closeOrderForm.cashReceived 
        ? (parseFloat(closeOrderForm.cashReceived) - ((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1))).toFixed(2)
        : null;
      
      toast.success('🎉 Comanda fechada com sucesso!', {
        description: changeAmount && parseFloat(changeAmount) > 0
          ? `Mesa ${selectedTable.number} - ${paymentMethodLabel} | Troco: R$ ${changeAmount}`
          : `Mesa ${selectedTable.number} - Pagamento via ${paymentMethodLabel}`,
        duration: 4000,
      });
      
      setIsCloseOrderModalOpen(false);
      setIsOrderModalOpen(false);
      setCurrentOrder(null);
      setSelectedTable(null);
      setCloseOrderForm({
        paymentMethod: 'dinheiro',
        splitPeople: 1,
        applyServiceFee: true,
        waiterName: '',
        cashReceived: ''
      });
      fetchTables();
    } catch (error) {
      console.error('Error closing order:', error);
      toast.error('❌ Erro ao fechar comanda', {
        description: error.response?.data?.detail || 'Tente novamente mais tarde',
        duration: 4000,
      });
    }
  };

  const handleSplitBill = async (numSplits) => {
    try {
      const response = await axios.post(
        `${API}/tables/${selectedTable.id}/split-bill`,
        { num_splits: parseInt(numSplits) }
      );
      setSplitBillData(response.data);
      setIsSplitBillModalOpen(true);
    } catch (error) {
      console.error('Error splitting bill:', error);
      alert('Erro ao dividir conta');
    }
  };

  const openEditTableForm = (table) => {
    setSelectedTable(table);
    setTableForm({
      name: table.name,
      number: table.number,
      capacity: table.capacity,
      status: table.status
    });
    setIsTableFormOpen(true);
  };

  const openNewTableForm = () => {
    setSelectedTable(null);
    setTableForm({ name: '', number: null, capacity: 4, status: 'livre' });
    setIsTableFormOpen(true);
  };

  const handleBulkCreateTables = async () => {
    try {
      const { prefix, startNumber, endNumber, capacity } = bulkCreateForm;
      
      if (startNumber > endNumber) {
        alert('O número inicial deve ser menor ou igual ao número final!');
        return;
      }

      if (endNumber - startNumber > 100) {
        alert('Você pode criar no máximo 100 mesas por vez!');
        return;
      }

      const tablesToCreate = [];
      for (let i = startNumber; i <= endNumber; i++) {
        tablesToCreate.push({
          name: `${prefix} ${i}`,
          number: i,
          capacity: parseInt(capacity),
          status: 'livre'
        });
      }

      // Create tables in batch
      await Promise.all(
        tablesToCreate.map(table => 
          axios.post(`${API}/tables`, table)
        )
      );

      alert(`${tablesToCreate.length} mesas criadas com sucesso!`);
      setIsBulkCreateModalOpen(false);
      setBulkCreateForm({
        prefix: 'Mesa',
        startNumber: 1,
        endNumber: 10,
        capacity: 4
      });
      fetchTables();
    } catch (error) {
      console.error('Error bulk creating tables:', error);
      alert('Erro ao criar mesas em massa');
    }
  };

  const handleBulkDeleteTables = async () => {
    if (tablesToDelete.length === 0) {
      alert('Selecione pelo menos uma mesa para excluir!');
      return;
    }

    const confirmMessage = `Você tem certeza que deseja excluir ${tablesToDelete.length} mesa(s)?\n\nEsta ação não pode ser desfeita!`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Delete tables in batch
      await Promise.all(
        tablesToDelete.map(tableId => 
          axios.delete(`${API}/tables/${tableId}`)
        )
      );

      alert(`${tablesToDelete.length} mesa(s) excluída(s) com sucesso!`);
      setIsBulkDeleteModalOpen(false);
      setTablesToDelete([]);
      fetchTables();
    } catch (error) {
      console.error('Error bulk deleting tables:', error);
      alert('Erro ao excluir mesas. Algumas mesas podem ter comandas ativas.');
    }
  };

  const toggleTableSelection = (tableId) => {
    setTablesToDelete(prev => {
      if (prev.includes(tableId)) {
        return prev.filter(id => id !== tableId);
      } else {
        return [...prev, tableId];
      }
    });
  };

  const selectAllTables = () => {
    if (tablesToDelete.length === tables.length) {
      setTablesToDelete([]);
    } else {
      setTablesToDelete(tables.map(t => t.id));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'livre':
        return 'bg-gradient-to-br from-green-500 to-emerald-600';
      case 'ocupada':
        return 'bg-gradient-to-br from-red-500 to-rose-600';
      case 'reservada':
        return 'bg-gradient-to-br from-yellow-500 to-amber-600';
      default:
        return 'bg-gradient-to-br from-gray-500 to-slate-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'livre':
        return '✓';
      case 'ocupada':
        return '🍽️';
      case 'reservada':
        return '🔒';
      default:
        return '○';
    }
  };

  // 🆕 Filtrar e buscar mesas
  const filteredTables = tables.filter(table => {
    // Filtro por status
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'livre' && !table.has_order && table.status === 'livre') ||
      (filterStatus === 'ocupada' && (table.has_order || table.status === 'ocupada')) ||
      (filterStatus === 'reservada' && table.status === 'reservada');
    
    // Busca por nome ou número
    const matchesSearch = !searchQuery || 
      table.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (table.number && table.number.toString().includes(searchQuery));
    
    return matchesStatus && matchesSearch;
  });

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f10]" data-testid="table-management-content">
      {/* 🆕 Header Clean e Compacto */}
      <div className="bg-[#0f0f10] px-4 py-3 md:px-6 md:py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <h1 className="text-xl md:text-2xl font-bold text-white">Mesas</h1>
          
          {/* Botões Discretos lado a lado */}
          <div className="flex items-center gap-2">
            {/* Botão Visão Geral - Discreto */}
            <button
              onClick={() => {
                const dashboard = document.querySelector('[data-testid="dashboard-toggle"]');
                if (dashboard) dashboard.click();
              }}
              className="p-2 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg transition-colors"
              title="Visão Geral"
            >
              <TrendingUp className="w-4 h-4 text-gray-400" />
            </button>

            {/* Dropdown Nova Mesa - Discreto */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-2 bg-gray-800/50 hover:bg-gray-700/50 text-gray-400 hover:text-white rounded-lg transition-colors flex items-center gap-1"
                  data-testid="new-table-dropdown"
                  title="Gerenciar Mesas"
                >
                  <Settings className="w-4 h-4" />
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-gray-800 border-gray-700">
                <DropdownMenuItem 
                  onClick={openNewTableForm}
                  className="cursor-pointer text-white hover:bg-gray-700 focus:bg-gray-700"
                  data-testid="single-table-option"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Criar Mesa Individual
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setIsBulkCreateModalOpen(true)}
                  className="cursor-pointer text-white hover:bg-gray-700 focus:bg-gray-700"
                  data-testid="bulk-table-option"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Criar Mesas em Massa
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => {
                    setTablesToDelete([]);
                    setIsBulkDeleteModalOpen(true);
                  }}
                  className="cursor-pointer text-red-400 hover:bg-red-500/10 focus:bg-red-500/10"
                  data-testid="delete-tables-option"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Excluir Mesas
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* 🆕 Busca e Filtros */}
      <div className="px-4 md:px-6 py-3">
        <TableFilters
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          onClear={handleClearFilters}
        />
      </div>

      {/* Tables Grid - Clean e Funcional */}
      <div className="px-4 pb-6 md:px-6">
        {/* 🆕 Results Counter */}
        {(searchQuery || filterStatus !== 'all') && filteredTables.length > 0 && (
          <div className="mb-4 flex items-center justify-between bg-gray-800/30 rounded-lg px-4 py-2 border border-gray-700/50">
            <span className="text-gray-400 text-sm">
              {filteredTables.length === tables.length 
                ? `${filteredTables.length} ${filteredTables.length === 1 ? 'mesa encontrada' : 'mesas encontradas'}`
                : `${filteredTables.length} de ${tables.length} ${filteredTables.length === 1 ? 'mesa' : 'mesas'}`
              }
            </span>
            <button
              onClick={handleClearFilters}
              className="text-orange-400 hover:text-orange-300 text-sm font-medium transition-colors flex items-center gap-1"
            >
              <XCircle className="w-4 h-4" />
              Limpar
            </button>
          </div>
        )}

        {/* 🆕 GRID CLEAN E FUNCIONAL - 3 colunas, cards quadrados */}
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
          {filteredTables.map((table) => {
            const isOccupied = table.status === 'ocupada' || table.has_order;
            
            return (
              <div
                key={table.id}
                className={`relative group bg-slate-800/30 backdrop-blur-sm rounded-xl p-4 cursor-pointer transition-all duration-200 hover:scale-[1.03] aspect-square flex flex-col items-center justify-between ${
                  isOccupied 
                    ? 'border-[3px] border-orange-500 shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/30' 
                    : 'border-[3px] border-green-500 shadow-md shadow-green-500/20 hover:shadow-lg hover:shadow-green-500/30'
                }`}
                onClick={() => {
                  if (table.has_order) {
                    handleViewOrder(table);
                  } else {
                    handleOpenOrder(table);
                  }
                }}
                data-testid={`table-card-${table.id}`}
              >
                {/* Número Grande */}
                <div className="flex-1 flex items-center justify-center w-full">
                  <div className="text-white text-4xl sm:text-5xl md:text-6xl font-black leading-none tracking-tight">
                    {table.number || table.name.match(/\d+/)?.[0] || table.name.charAt(0)}
                  </div>
                </div>

                {/* Capacidade - Compacta */}
                <div className="flex items-center gap-1 text-gray-400 text-xs mb-2">
                  <Users className="w-3 h-3" />
                  <span>{table.capacity}</span>
                </div>

                {/* Total (apenas se ocupada) */}
                {isOccupied && table.current_order && (
                  <div className="text-orange-400 font-bold text-sm mb-2">
                    R$ {table.current_order.total?.toFixed(2) || '0.00'}
                  </div>
                )}

                {/* Badge Status - Compacto */}
                <div className={`w-full py-1.5 px-2 rounded-lg text-xs font-bold text-center ${
                  isOccupied
                    ? 'bg-orange-500/80 text-white'
                    : 'bg-green-500/80 text-white'
                }`}>
                  {isOccupied ? 'Ocupada' : 'Livre'}
                </div>

                {/* Edit Button - Discreto */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditTableForm(table);
                  }}
                  className="absolute top-2 right-2 w-6 h-6 bg-slate-700/60 hover:bg-slate-600/80 rounded-md flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                  data-testid={`table-menu-${table.id}`}
                >
                  <Settings className="w-3 h-3 text-gray-400 group-hover:text-white" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Empty State - No tables at all */}
        {tables.length === 0 && (
          <div className="text-center py-20">
            <div className="bg-slate-800/30 rounded-full w-24 h-24 mx-auto mb-4 flex items-center justify-center">
              <Store className="w-12 h-12 text-gray-500" />
            </div>
            <h3 className="text-white font-bold text-xl mb-2">Nenhuma mesa cadastrada</h3>
            <p className="text-gray-400 mb-6">Comece criando suas primeiras mesas</p>
            <button
              onClick={openNewTableForm}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl transition-all font-semibold shadow-lg shadow-orange-500/30"
            >
              Criar Primeira Mesa
            </button>
          </div>
        )}

        {/* 🆕 Empty State - No results from filter/search */}
        {tables.length > 0 && filteredTables.length === 0 && (
          <div className="text-center py-20">
            <div className="bg-slate-800/30 rounded-full w-24 h-24 mx-auto mb-4 flex items-center justify-center">
              <Search className="w-12 h-12 text-gray-500" />
            </div>
            <h3 className="text-white font-bold text-xl mb-2">Nenhuma mesa encontrada</h3>
            <p className="text-gray-400 mb-6">
              {searchQuery 
                ? `Não encontramos mesas com "${searchQuery}"`
                : `Não há mesas com status "${filterStatus}"`
              }
            </p>
            <button
              onClick={handleClearFilters}
              className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl transition-all font-semibold shadow-lg shadow-emerald-500/30"
            >
              Limpar Filtros
            </button>
          </div>
        )}
      </div>

      {/* Bulk Create Tables Modal */}
      {isBulkCreateModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="bulk-create-modal">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-6 border border-gray-700">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white">Criar Mesas em Massa</h2>
                <p className="text-gray-400 text-sm mt-1">Crie várias mesas de uma só vez</p>
              </div>
              <button
                onClick={() => setIsBulkCreateModalOpen(false)}
                className="w-10 h-10 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center justify-center transition-colors"
                data-testid="close-bulk-modal-button"
              >
                <XCircle className="w-5 h-5 text-gray-300" />
              </button>
            </div>

            {/* Preview Card */}
            <div className="bg-gradient-to-r from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Copy className="w-4 h-4 text-orange-400" />
                <p className="text-orange-300 text-sm font-medium">Pré-visualização</p>
              </div>
              <p className="text-white font-bold">
                {bulkCreateForm.endNumber - bulkCreateForm.startNumber + 1} mesas serão criadas
              </p>
              <p className="text-gray-300 text-sm mt-1">
                De "{bulkCreateForm.prefix} {bulkCreateForm.startNumber}" até "{bulkCreateForm.prefix} {bulkCreateForm.endNumber}"
              </p>
            </div>

            <div className="space-y-4">
              {/* Prefix */}
              <div>
                <label className="text-white font-medium text-sm mb-2 block">
                  Prefixo do Nome
                </label>
                <input
                  type="text"
                  value={bulkCreateForm.prefix}
                  onChange={(e) => setBulkCreateForm({ ...bulkCreateForm, prefix: e.target.value })}
                  placeholder="Ex: Mesa, Salão"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                  data-testid="bulk-prefix-input"
                />
                <p className="text-gray-500 text-xs mt-1">Será usado como: {bulkCreateForm.prefix} 1, {bulkCreateForm.prefix} 2...</p>
              </div>

              {/* Number Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white font-medium text-sm mb-2 block">
                    Número Inicial
                  </label>
                  <input
                    type="number"
                    value={bulkCreateForm.startNumber}
                    onChange={(e) => setBulkCreateForm({ ...bulkCreateForm, startNumber: parseInt(e.target.value) || 1 })}
                    min="1"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                    data-testid="bulk-start-number-input"
                  />
                </div>
                <div>
                  <label className="text-white font-medium text-sm mb-2 block">
                    Número Final
                  </label>
                  <input
                    type="number"
                    value={bulkCreateForm.endNumber}
                    onChange={(e) => setBulkCreateForm({ ...bulkCreateForm, endNumber: parseInt(e.target.value) || 1 })}
                    min={bulkCreateForm.startNumber}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                    data-testid="bulk-end-number-input"
                  />
                </div>
              </div>

              {/* Capacity */}
              <div>
                <label className="text-white font-medium text-sm mb-2 block">
                  Capacidade (pessoas)
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setBulkCreateForm({ ...bulkCreateForm, capacity: Math.max(1, bulkCreateForm.capacity - 1) })}
                    className="w-10 h-10 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center justify-center transition-colors text-white font-bold text-lg"
                    data-testid="bulk-capacity-decrease"
                  >
                    −
                  </button>
                  <div className="flex-1 bg-gray-800 border border-gray-700 rounded-lg p-3 text-center">
                    <p className="text-white text-2xl font-bold">{bulkCreateForm.capacity}</p>
                    <p className="text-gray-400 text-xs">pessoas por mesa</p>
                  </div>
                  <button
                    onClick={() => setBulkCreateForm({ ...bulkCreateForm, capacity: bulkCreateForm.capacity + 1 })}
                    className="w-10 h-10 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center justify-center transition-colors text-white font-bold text-lg"
                    data-testid="bulk-capacity-increase"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Warning */}
            {(bulkCreateForm.endNumber - bulkCreateForm.startNumber + 1) > 50 && (
              <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-start gap-2">
                <Bell className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-300 text-sm font-medium">Atenção!</p>
                  <p className="text-yellow-200 text-xs mt-0.5">Você está criando muitas mesas. Isso pode demorar alguns segundos.</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setIsBulkCreateModalOpen(false)}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                data-testid="cancel-bulk-create-button"
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkCreateTables}
                disabled={!bulkCreateForm.prefix || bulkCreateForm.startNumber > bulkCreateForm.endNumber}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white rounded-lg transition-all font-bold shadow-lg shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="confirm-bulk-create-button"
              >
                <CheckCircle className="w-5 h-5" />
                Criar Mesas
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Tables Modal */}
      {isBulkDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="bulk-delete-modal">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-gray-700 flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-rose-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Excluir Mesas</h2>
                <p className="text-red-100 text-sm mt-1">Selecione as mesas que deseja remover</p>
              </div>
              <button
                onClick={() => {
                  setIsBulkDeleteModalOpen(false);
                  setTablesToDelete([]);
                }}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                data-testid="close-bulk-delete-modal-button"
              >
                <XCircle className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Selection Info */}
            <div className="px-6 py-4 bg-gray-800/50 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-red-500/20 border border-red-500/30 rounded-lg px-3 py-2">
                    <p className="text-red-300 text-sm font-medium">
                      {tablesToDelete.length} de {tables.length} selecionada(s)
                    </p>
                  </div>
                  <button
                    onClick={selectAllTables}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors font-medium"
                    data-testid="select-all-tables-button"
                  >
                    {tablesToDelete.length === tables.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
                  </button>
                </div>
              </div>
            </div>

            {/* Tables List */}
            <div className="flex-1 overflow-y-auto p-6">
              {tables.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-400">Nenhuma mesa cadastrada</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tables.map((table) => {
                    const isSelected = tablesToDelete.includes(table.id);
                    const hasActiveOrder = table.status === 'ocupada' || table.has_order;
                    
                    return (
                      <div
                        key={table.id}
                        onClick={() => !hasActiveOrder && toggleTableSelection(table.id)}
                        className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                          hasActiveOrder
                            ? 'bg-gray-800/50 border-gray-700 opacity-50 cursor-not-allowed'
                            : isSelected
                            ? 'bg-red-500/20 border-red-500 shadow-lg shadow-red-500/20'
                            : 'bg-gray-800 border-gray-700 hover:border-gray-600'
                        }`}
                        data-testid={`delete-table-item-${table.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                              isSelected
                                ? 'bg-red-500 border-red-500'
                                : 'border-gray-600'
                            }`}>
                              {isSelected && (
                                <CheckCircle className="w-4 h-4 text-white" />
                              )}
                            </div>
                            <div>
                              <p className={`font-bold ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                                {table.name}
                              </p>
                              <p className="text-gray-500 text-xs">
                                {table.capacity} pessoas
                              </p>
                            </div>
                          </div>
                          {hasActiveOrder && (
                            <div className="bg-orange-500/20 border border-orange-500/30 rounded px-2 py-1">
                              <p className="text-orange-400 text-xs font-medium">Em uso</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer with Actions */}
            <div className="px-6 py-4 bg-gray-800/50 border-t border-gray-700">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setIsBulkDeleteModalOpen(false);
                    setTablesToDelete([]);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                  data-testid="cancel-bulk-delete-button"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBulkDeleteTables}
                  disabled={tablesToDelete.length === 0}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white rounded-lg transition-all font-bold shadow-lg shadow-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="confirm-bulk-delete-button"
                >
                  <Trash2 className="w-5 h-5" />
                  Excluir {tablesToDelete.length > 0 ? `(${tablesToDelete.length})` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table Form Modal */}
      {isTableFormOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="table-form-modal">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-white mb-6">
              {selectedTable ? 'Editar Mesa' : 'Nova Mesa'}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Nome da Mesa *
                </label>
                <input
                  type="text"
                  value={tableForm.name}
                  onChange={(e) => setTableForm({ ...tableForm, name: e.target.value })}
                  placeholder="Ex: Mesa 1, Mesa VIP"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="table-name-input"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Número (opcional)
                </label>
                <input
                  type="number"
                  value={tableForm.number || ''}
                  onChange={(e) => setTableForm({ ...tableForm, number: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Ex: 1, 2, 3..."
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="table-number-input"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Capacidade (pessoas) *
                </label>
                <input
                  type="number"
                  value={tableForm.capacity}
                  onChange={(e) => setTableForm({ ...tableForm, capacity: parseInt(e.target.value) || 1 })}
                  min="1"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="table-capacity-input"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Status
                </label>
                <select
                  value={tableForm.status}
                  onChange={(e) => setTableForm({ ...tableForm, status: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="table-status-select"
                >
                  <option value="livre">Livre</option>
                  <option value="ocupada">Ocupada</option>
                  <option value="reservada">Reservada</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setIsTableFormOpen(false);
                  setSelectedTable(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                data-testid="cancel-table-form-button"
              >
                Cancelar
              </button>
              <button
                onClick={selectedTable ? handleUpdateTable : handleCreateTable}
                disabled={!tableForm.name || !tableForm.capacity}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-table-form-button"
              >
                {selectedTable ? 'Atualizar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Management Modal - Modern Digital Menu Style */}
      {isOrderModalOpen && currentOrder && selectedTable && (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900 z-[9999] flex flex-col" data-testid="order-modal">
          {/* Header - Compacto e Discreto */}
          <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-3 sm:px-5 py-2 sm:py-3 shadow-lg flex-shrink-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-1.5 flex-shrink-0">
                  <Store className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm sm:text-lg font-bold text-white truncate">{selectedTable.name}</h2>
                  <p className="text-orange-100 text-xs flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    <span className="hidden sm:inline">{selectedTable.capacity} lugares</span>
                    <span className="sm:hidden">{selectedTable.capacity}</span>
                  </p>
                </div>
              </div>
              
              {/* Quick Stats - Inline e Compacto */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-2 py-1">
                  <p className="text-orange-100 text-[10px] leading-none">Itens</p>
                  <p className="text-white font-bold text-sm leading-tight">{currentOrder.items?.length || 0}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-2 py-1">
                  <p className="text-orange-100 text-[10px] leading-none">Total</p>
                  <p className="text-white font-bold text-sm leading-tight">R$ {currentOrder.total?.toFixed(2) || '0.00'}</p>
                </div>
                <button
                  onClick={() => {
                    setIsOrderModalOpen(false);
                    setCurrentOrder(null);
                    setSelectedTable(null);
                  }}
                  className="w-8 h-8 sm:w-9 sm:h-9 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-all flex-shrink-0"
                  data-testid="close-order-modal-button"
                >
                  <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Content - Two Column Layout for Desktop */}
          <div className="flex-1 overflow-y-auto flex flex-col lg:flex-row">
            {/* Left Side - Menu/Cardápio */}
            <div className="flex-1 bg-gradient-to-b from-slate-800 to-slate-900 p-5">
              {/* Section Title */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-6 bg-orange-500 rounded-full"></div>
                  <h3 className="text-white font-bold text-xl">Meucardapiozap</h3>
                </div>
                
                {/* Toggle Image Visibility - Discreto */}
                <button
                  onClick={() => setShowProductImages(!showProductImages)}
                  className="p-2 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg transition-all group"
                  title={showProductImages ? "Ocultar imagens" : "Mostrar imagens"}
                  data-testid="toggle-product-images"
                >
                  {showProductImages ? (
                    <Eye className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                  )}
                </button>
              </div>
              
              {/* Category Pills - Horizontal scroll */}
              <div className="mb-5">
                <div className="flex gap-2 overflow-x-auto pb-3 hide-scrollbar">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-5 py-2.5 rounded-full whitespace-nowrap text-sm font-semibold transition-all flex-shrink-0 shadow-md ${
                      selectedCategory === 'all'
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-orange-500/50'
                        : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                    }`}
                    data-testid="category-all"
                  >
                    🍽️ Todos
                  </button>
                  {categories.filter(cat => cat.active).map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-5 py-2.5 rounded-full whitespace-nowrap text-sm font-semibold transition-all flex-shrink-0 shadow-md ${
                        selectedCategory === category.id
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-orange-500/50'
                          : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                      }`}
                      data-testid={`category-${category.id}`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Product Grid - Beautiful Cards */}
              <div className={`grid gap-4 pb-20 ${showProductImages ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                {products
                  .filter(p => p.available)
                  .filter(p => selectedCategory === 'all' || p.category_id === selectedCategory)
                  .map((product) => (
                  <div
                    key={product.id}
                    className={`bg-gradient-to-br from-slate-700 to-slate-800 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 cursor-pointer group border border-slate-600/50 ${!showProductImages ? 'flex items-center' : ''}`}
                    onClick={() => handleAddItemToOrder(product.id, 1)}
                    data-testid={`add-product-${product.id}`}
                  >
                    {/* Product Image Placeholder - Condicional */}
                    {showProductImages && (
                      <div className="h-32 bg-gradient-to-br from-orange-500/20 to-orange-600/20 relative overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-6xl opacity-30">🍽️</div>
                        </div>
                        {/* Quick Add Badge */}
                        <div className="absolute top-3 right-3">
                          <div className="bg-orange-500 text-white rounded-full p-2 shadow-lg group-hover:scale-110 transition-transform">
                            <Plus className="w-5 h-5" />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className={`p-4 ${!showProductImages ? 'flex items-center justify-between w-full gap-4' : ''}`}>
                      <div className="flex-1">
                        <h4 className="text-white font-bold text-lg mb-1 line-clamp-1">{product.name}</h4>
                        {product.description && (
                          <p className="text-gray-400 text-sm mb-3 line-clamp-2 leading-relaxed">{product.description}</p>
                        )}
                      </div>
                      
                      <div className={`flex items-center ${showProductImages ? 'justify-between pt-2 border-t border-slate-600' : 'gap-4'}`}>
                        <span className="text-orange-400 font-bold text-xl">R$ {product.price.toFixed(2)}</span>
                        {!showProductImages && (
                          <div className="bg-orange-500 text-white rounded-full p-2 shadow-lg group-hover:scale-110 transition-transform flex-shrink-0">
                            <Plus className="w-5 h-5" />
                          </div>
                        )}
                        {showProductImages && (
                          <span className="text-gray-400 text-xs bg-slate-600/50 px-3 py-1 rounded-full">
                            Toque para adicionar
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Order Summary (Pedido) */}
            <div className="lg:w-96 bg-slate-800 border-t lg:border-t-0 lg:border-l border-slate-700 flex flex-col">
              {/* Order Items Section */}
              {currentOrder.items && currentOrder.items.length > 0 ? (
                <>
                  <div className="p-5 border-b border-slate-700 flex-shrink-0">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-1 h-6 bg-green-500 rounded-full"></div>
                      <h3 className="text-white font-bold text-lg">Pedido Atual</h3>
                    </div>
                    <p className="text-gray-400 text-sm">{currentOrder.items.length} {currentOrder.items.length === 1 ? 'item' : 'itens'} adicionado{currentOrder.items.length === 1 ? '' : 's'}</p>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-4">
                    {currentOrder.items.map((item, index) => (
                      <div
                        key={item.id}
                        className="bg-slate-700/50 rounded-xl p-4 backdrop-blur-sm border border-slate-600/30 hover:border-slate-500 transition-all"
                        data-testid={`order-item-${item.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="bg-orange-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                                {index + 1}
                              </span>
                              <p className="text-white font-semibold">{item.product_name}</p>
                            </div>
                            <p className="text-gray-400 text-sm ml-8">
                              {item.quantity}x R$ {item.unit_price.toFixed(2)}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveItem(item.id);
                            }}
                            className="w-9 h-9 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg flex items-center justify-center transition-all hover:scale-110"
                            data-testid={`remove-item-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex justify-end">
                          <span className="text-orange-400 font-bold text-lg">R$ {item.subtotal.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Total Summary Card - Fixed at bottom */}
                  <div className="p-5 bg-slate-900 border-t border-slate-700 flex-shrink-0">
                    <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl p-4 mb-4 border border-slate-600">
                      <div className="space-y-2 mb-3">
                        <div className="flex justify-between text-gray-300 text-sm">
                          <span>Subtotal:</span>
                          <span data-testid="order-subtotal">R$ {currentOrder.subtotal?.toFixed(2) || '0.00'}</span>
                        </div>
                        <div className="flex justify-between text-gray-300 text-sm">
                          <span>Taxa de Serviço (10%):</span>
                          <span data-testid="order-service-fee">R$ {currentOrder.service_fee?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center pt-3 border-t border-slate-600">
                        <span className="text-white font-bold text-lg">Total:</span>
                        <span className="text-orange-400 font-bold text-2xl" data-testid="order-total">
                          R$ {currentOrder.total?.toFixed(2) || '0.00'}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                  <div className="bg-slate-700/30 rounded-full p-6 mb-4">
                    <ShoppingCart className="w-16 h-16 text-gray-500" />
                  </div>
                  <h3 className="text-white font-bold text-xl mb-2">Pedido Vazio</h3>
                  <p className="text-gray-400 text-sm">Selecione itens do cardápio para começar</p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons - Fixed at Bottom */}
          <div className="flex-shrink-0 bg-slate-900 border-t border-slate-700 px-5 py-4 shadow-2xl">
            <div className="flex gap-3 max-w-7xl mx-auto">
              <button
                onClick={() => {
                  setIsOrderModalOpen(false);
                  setCurrentOrder(null);
                  setSelectedTable(null);
                }}
                className="flex-1 px-6 py-4 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-all font-semibold flex items-center justify-center gap-2 shadow-lg"
                data-testid="back-to-tables-button"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-base">Voltar</span>
              </button>
              
              <button
                onClick={() => setIsCloseOrderModalOpen(true)}
                disabled={!currentOrder.items || currentOrder.items.length === 0}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl transition-all font-bold shadow-xl shadow-green-500/40 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="close-order-button"
              >
                <CheckCircle className="w-5 h-5" />
                <span className="text-base">Finalizar Pedido</span>
              </button>
            </div>
          </div>
          
          {/* Toast de Adicionando Produto - Atualizado com Estados de Loading e Sucesso */}
          {addingProduct && (
            <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-[10000] animate-slideUp" data-testid="adding-product-toast">
              <div className={`${
                productAddSuccess 
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                  : 'bg-gradient-to-r from-emerald-500 to-cyan-500'
              } text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 backdrop-blur-sm border border-white/20 transition-all duration-300`}>
                <div className="relative">
                  {productAddSuccess ? (
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center animate-bounce">
                      <CheckCircle className="w-7 h-7 text-green-500" />
                    </div>
                  ) : (
                    <>
                      <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <p className="font-bold text-base">
                    {productAddSuccess ? '✅ Produto adicionado!' : 'Adicionando produto...'}
                  </p>
                  <p className={`${productAddSuccess ? 'text-green-100' : 'text-emerald-100'} text-xs`}>
                    {productAddSuccess ? 'Item incluído na comanda' : 'Aguarde um momento'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Close Order Screen - Fullscreen */}
      {isCloseOrderModalOpen && currentOrder && (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900 z-[10000] flex flex-col" data-testid="close-order-modal">
          {/* Header com botão de voltar */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-4 sm:px-6 py-4 shadow-xl flex-shrink-0">
            <div className="flex items-center gap-4 max-w-4xl mx-auto">
              <button
                onClick={() => setIsCloseOrderModalOpen(false)}
                className="w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-all flex-shrink-0"
                data-testid="close-modal-button"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex-1">
                <h2 className="text-xl sm:text-2xl font-bold text-white">Finalizar Pedido</h2>
                <p className="text-green-100 text-sm">{selectedTable?.name}</p>
              </div>
            </div>
          </div>
          
          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="max-w-2xl mx-auto space-y-6">

              {/* Total Summary */}
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-5 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="text-gray-300 text-sm">Total a pagar</p>
                    <p className="text-white text-3xl sm:text-4xl font-bold mt-1">
                      R$ {((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1)).toFixed(2)}
                    </p>
                    {closeOrderForm.applyServiceFee && (
                      <p className="text-green-400 text-xs mt-1">Incluindo 10% de serviço</p>
                    )}
                  </div>
                  <div className="text-left sm:text-right">
                    {closeOrderForm.splitPeople > 1 && (
                      <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg px-4 py-3">
                        <p className="text-emerald-300 text-xs">Por pessoa</p>
                        <p className="text-emerald-200 text-xl font-bold">
                          R$ {(((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1)) / closeOrderForm.splitPeople).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-5 border border-gray-700">
                <label className="text-white font-semibold text-base mb-4 block flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-400" />
                  Forma de Pagamento
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setCloseOrderForm({ ...closeOrderForm, paymentMethod: 'dinheiro' })}
                    className={`p-5 rounded-xl border-2 transition-all ${
                      closeOrderForm.paymentMethod === 'dinheiro'
                        ? 'bg-green-500/20 border-green-500 shadow-lg shadow-green-500/20'
                        : 'bg-gray-800 border-gray-700 hover:border-gray-600'
                    }`}
                    data-testid="payment-cash-button"
                  >
                    <Banknote className={`w-7 h-7 mx-auto mb-2 ${closeOrderForm.paymentMethod === 'dinheiro' ? 'text-green-400' : 'text-gray-400'}`} />
                    <p className={`text-sm font-medium ${closeOrderForm.paymentMethod === 'dinheiro' ? 'text-white' : 'text-gray-400'}`}>Dinheiro</p>
                  </button>
                  
                  <button
                    onClick={() => setCloseOrderForm({ ...closeOrderForm, paymentMethod: 'cartao' })}
                    className={`p-5 rounded-xl border-2 transition-all ${
                      closeOrderForm.paymentMethod === 'cartao'
                        ? 'bg-emerald-500/20 border-emerald-500 shadow-lg shadow-emerald-500/20'
                        : 'bg-gray-800 border-gray-700 hover:border-gray-600'
                    }`}
                    data-testid="payment-card-button"
                  >
                    <CreditCard className={`w-7 h-7 mx-auto mb-2 ${closeOrderForm.paymentMethod === 'cartao' ? 'text-emerald-400' : 'text-gray-400'}`} />
                    <p className={`text-sm font-medium ${closeOrderForm.paymentMethod === 'cartao' ? 'text-white' : 'text-gray-400'}`}>Cartão</p>
                  </button>
                  
                  <button
                    onClick={() => setCloseOrderForm({ ...closeOrderForm, paymentMethod: 'pix' })}
                    className={`p-5 rounded-xl border-2 transition-all ${
                      closeOrderForm.paymentMethod === 'pix'
                        ? 'bg-purple-500/20 border-purple-500 shadow-lg shadow-purple-500/20'
                        : 'bg-gray-800 border-gray-700 hover:border-gray-600'
                    }`}
                    data-testid="payment-pix-button"
                  >
                    <Smartphone className={`w-7 h-7 mx-auto mb-2 ${closeOrderForm.paymentMethod === 'pix' ? 'text-purple-400' : 'text-gray-400'}`} />
                    <p className={`text-sm font-medium ${closeOrderForm.paymentMethod === 'pix' ? 'text-white' : 'text-gray-400'}`}>PIX</p>
                  </button>
                </div>

                {/* Cash Change Calculator - Appears only when cash is selected */}
                {closeOrderForm.paymentMethod === 'dinheiro' && (
                  <div className="mt-4 space-y-3 animate-in slide-in-from-top duration-300">
                    <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border border-green-600/30 rounded-lg p-4">
                      <label className="text-green-300 text-sm font-medium mb-2 block flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Valor recebido do cliente
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-green-400 font-bold text-lg">R$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={closeOrderForm.cashReceived}
                          onChange={(e) => setCloseOrderForm({ ...closeOrderForm, cashReceived: e.target.value })}
                          placeholder="0,00"
                          className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-green-600/40 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all text-lg font-semibold"
                          data-testid="cash-received-input"
                        />
                      </div>
                    </div>
                    
                    {closeOrderForm.cashReceived && parseFloat(closeOrderForm.cashReceived) > 0 && (
                      <div className={`p-4 rounded-lg border-2 transition-all ${
                        parseFloat(closeOrderForm.cashReceived) >= ((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1))
                          ? 'bg-green-500/10 border-green-500/40'
                          : 'bg-red-500/10 border-red-500/40'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium ${
                            parseFloat(closeOrderForm.cashReceived) >= ((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1))
                              ? 'text-green-300'
                              : 'text-red-300'
                          }`}>
                            {parseFloat(closeOrderForm.cashReceived) >= ((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1))
                              ? '💵 Troco a devolver'
                              : '⚠️ Valor insuficiente'
                            }
                          </span>
                          <span className={`text-2xl font-bold ${
                            parseFloat(closeOrderForm.cashReceived) >= ((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1))
                              ? 'text-green-400'
                              : 'text-red-400'
                          }`}>
                            R$ {(parseFloat(closeOrderForm.cashReceived) - ((currentOrder?.total || 0) * (closeOrderForm.applyServiceFee ? 1.1 : 1))).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Split Bill */}
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-5 border border-gray-700">
                <label className="text-white font-semibold text-base mb-4 block flex items-center gap-2">
                  <UsersRound className="w-5 h-5 text-emerald-400" />
                  Dividir Conta
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setCloseOrderForm({ ...closeOrderForm, splitPeople: Math.max(1, closeOrderForm.splitPeople - 1) })}
                    className="w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-xl flex items-center justify-center transition-colors text-white font-bold text-xl shadow-lg"
                    data-testid="split-decrease-button"
                  >
                    −
                  </button>
                  <div className="flex-1 bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-4 text-center">
                    <p className="text-white text-3xl font-bold">{closeOrderForm.splitPeople}</p>
                    <p className="text-gray-400 text-sm mt-1">{closeOrderForm.splitPeople === 1 ? 'pessoa' : 'pessoas'}</p>
                  </div>
                  <button
                    onClick={() => setCloseOrderForm({ ...closeOrderForm, splitPeople: closeOrderForm.splitPeople + 1 })}
                    className="w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-xl flex items-center justify-center transition-colors text-white font-bold text-xl shadow-lg"
                    data-testid="split-increase-button"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Service Fee Checkbox */}
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700 overflow-hidden">
                <div 
                  onClick={() => setCloseOrderForm({ ...closeOrderForm, applyServiceFee: !closeOrderForm.applyServiceFee })}
                  className="flex items-center justify-between p-5 cursor-pointer hover:bg-gray-800/70 transition-colors"
                  data-testid="service-fee-toggle"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all shadow-lg ${
                      closeOrderForm.applyServiceFee 
                        ? 'bg-green-500 border-green-500' 
                        : 'border-gray-600'
                    }`}>
                      {closeOrderForm.applyServiceFee && (
                        <CheckCircle className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-base">Taxa de serviço</p>
                      <p className="text-gray-400 text-sm">Adicionar 10% ao total</p>
                    </div>
                  </div>
                  <div className={`px-4 py-2 rounded-full text-sm font-bold ${
                    closeOrderForm.applyServiceFee 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    {closeOrderForm.applyServiceFee ? 'SIM' : 'NÃO'}
                  </div>
                </div>
              </div>

              {/* Waiter Name (Optional) */}
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-5 border border-gray-700">
                <label className="text-white font-semibold text-base mb-3 block flex items-center gap-2">
                  <UserCircle className="w-5 h-5 text-orange-400" />
                  Nome do Atendente <span className="text-gray-500 text-sm">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={closeOrderForm.waiterName}
                  onChange={(e) => setCloseOrderForm({ ...closeOrderForm, waiterName: e.target.value })}
                  placeholder="Digite o nome do atendente..."
                  className="w-full px-4 py-4 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all text-base"
                  data-testid="waiter-name-input"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons - Fixed at Bottom */}
          <div className="flex-shrink-0 bg-slate-900 border-t border-slate-700 px-4 sm:px-6 py-4 shadow-2xl">
            <div className="flex gap-3 max-w-2xl mx-auto">
              <button
                onClick={() => setIsCloseOrderModalOpen(false)}
                className="flex-1 px-6 py-4 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-all font-semibold flex items-center justify-center gap-2 shadow-lg"
                data-testid="cancel-close-order-button"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-base">Voltar</span>
              </button>
              <button
                onClick={handleCloseOrder}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl transition-all font-bold shadow-xl shadow-green-500/40 flex items-center justify-center gap-2"
                data-testid="confirm-close-order-button"
              >
                <CheckCircle className="w-5 h-5" />
                <span className="text-base">Confirmar Fechamento</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Split Bill Modal */}
      {isSplitBillModalOpen && splitBillData && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4" data-testid="split-bill-modal">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Conta Dividida</h2>
            <div className="space-y-3 mb-6">
              <div className="bg-gray-700 rounded-lg p-3">
                <p className="text-gray-400 text-sm">Total da Conta</p>
                <p className="text-white text-2xl font-bold">R$ {splitBillData.total.toFixed(2)}</p>
              </div>
              
              {splitBillData.splits.map((split) => (
                <div key={split.person} className="bg-gray-700 rounded-lg p-3 flex justify-between items-center">
                  <span className="text-white font-medium">Pessoa {split.person}</span>
                  <span className="text-indigo-400 text-lg font-bold">R$ {split.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
            
            <button
              onClick={() => {
                setIsSplitBillModalOpen(false);
                setSplitBillData(null);
              }}
              className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              data-testid="close-split-bill-modal-button"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* History Modal */}
      {isHistoryModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="history-modal">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full my-8">
            <div className="bg-gradient-to-r from-indigo-500 to-violet-500 px-6 py-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Histórico de Comandas</h2>
                <button
                  onClick={() => setIsHistoryModalOpen(false)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
                  data-testid="close-history-modal-button"
                >
                  <XCircle className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>

            <div className="p-6 max-h-[600px] overflow-y-auto">
              {orderHistory.length > 0 ? (
                <div className="space-y-3">
                  {orderHistory.map((order) => (
                    <div key={order.id} className="bg-gray-700 rounded-lg p-4" data-testid={`history-order-${order.id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="text-white font-bold">{order.table_name}</h3>
                          <p className="text-gray-400 text-sm">
                            {new Date(order.closed_at).toLocaleDateString('pt-BR')} às{' '}
                            {new Date(order.closed_at).toLocaleTimeString('pt-BR')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-bold text-lg">R$ {order.total?.toFixed(2)}</p>
                          <p className="text-gray-400 text-sm capitalize">{order.payment_method}</p>
                        </div>
                      </div>
                      <p className="text-gray-400 text-sm">
                        {order.items?.length || 0} {order.items?.length === 1 ? 'item' : 'itens'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">Nenhuma comanda fechada encontrada</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Products Content Component
const ProductsContent = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [additionalGroups, setAdditionalGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [isCreateCategoryModalOpen, setIsCreateCategoryModalOpen] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [showProductImages, setShowProductImages] = useState(false); // Toggle para mostrar/ocultar imagens
  
  // Estados para galeria de imagens
  const [showAutoLinkModal, setShowAutoLinkModal] = useState(false);
  const [autoLinkLoading, setAutoLinkLoading] = useState(false);
  const [autoLinkResult, setAutoLinkResult] = useState(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [galleryFilter, setGalleryFilter] = useState('');
  const [gallerySearch, setGallerySearch] = useState('');
  const [loadingGallery, setLoadingGallery] = useState(false);
  
  // Estados para sugestões IA
  const [showAISuggestionsModal, setShowAISuggestionsModal] = useState(false);
  const [aiSuggestionsLoading, setAiSuggestionsLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState({});
  const [applyingSuggestions, setApplyingSuggestions] = useState(false);
  
  const [newCategoryData, setNewCategoryData] = useState({
    name: '',
    description: '',
    color: '#2ECC71'
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    image_url: '',
    available: true,
    preparation_time: '',
    additional_groups: [],
    display_order: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [imagePreview, setImagePreview] = useState(null);
  const [creatingDemo, setCreatingDemo] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchAdditionalGroups();
  }, []);

  const fetchProducts = async () => {
    try {
      // Cache-busting header to ensure fresh data after group linking operations
      const response = await axios.get(`${API}/products`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      console.log('📦 Products API response:', response.data);
      console.log('📦 Response type:', typeof response.data);
      console.log('📦 Is array:', Array.isArray(response.data));
      console.log('📦 Response status:', response.status);
      
      // Verificar se response.data existe
      if (!response.data) {
        console.error('📦 ERROR: response.data is null/undefined');
        setProducts([]);
        setLoading(false);
        return;
      }
      
      // Suportar resposta paginada ou array direto
      const productsData = response.data.items || response.data;
      console.log('📦 Products data:', productsData);
      console.log('📦 Products data length:', productsData?.length);
      
      const finalProducts = Array.isArray(productsData) ? productsData : [];
      console.log('📦 Final products count:', finalProducts.length);
      
      if (finalProducts.length === 0) {
        console.warn('📦 WARNING: No products received from API');
        // Tentar debug endpoint
        try {
          const debugResp = await axios.get(`${API}/products/debug`);
          console.log('📦 DEBUG endpoint response:', debugResp.data);
        } catch (debugErr) {
          console.error('📦 DEBUG endpoint error:', debugErr);
        }
      }
      
      setProducts(finalProducts);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAdditionalGroups = async () => {
    try {
      const response = await axios.get(`${API}/additional-groups`);
      setAdditionalGroups(response.data);
    } catch (error) {
      console.error('Error fetching additional groups:', error);
    }
  };

  const handleImageFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Formato não suportado. Use JPG, PNG ou WEBP.');
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Arquivo muito grande. Tamanho máximo: 5MB');
        return;
      }
      
      setSelectedImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedImageFile) return;
    
    try {
      setUploadingImage(true);
      
      const formData = new FormData();
      formData.append('file', selectedImageFile);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/products/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        // Update form with uploaded image URL
        setFormData(prev => ({...prev, image_url: response.data.image_url}));
        setSelectedImageFile(null);
        alert('✅ Imagem enviada com sucesso!');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('❌ Erro ao fazer upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingImage(false);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        category_id: formData.category_id,
        image_url: formData.image_url,
        available: formData.available,
        preparation_time: formData.preparation_time ? parseInt(formData.preparation_time) : null,
        additional_groups: formData.additional_groups || [],
        display_order: formData.display_order && formData.display_order !== '' ? parseInt(formData.display_order) : null
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData);
      } else {
        await axios.post(`${API}/products`, productData);
      }
      
      setIsModalOpen(false);
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        category_id: '',
        image_url: '',
        available: true,
        preparation_time: '',
        additional_groups: [],
        display_order: ''
      });
      setSelectedImageFile(null);
      setImagePreview(null);
      setUploadingImage(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price != null ? product.price.toString() : '',
      category_id: product.category_id || '',
      image_url: product.image_url || '',
      available: product.available !== false,
      preparation_time: product.preparation_time != null ? product.preparation_time.toString() : '',
      additional_groups: product.additional_groups || [],
      display_order: product.display_order != null ? product.display_order.toString() : ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Tem certeza que deseja excluir este produto?')) {
      try {
        await axios.delete(`${API}/products/${productId}`);
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const toggleAvailability = async (productId) => {
    try {
      await axios.patch(`${API}/products/${productId}/toggle-availability`);
      fetchProducts();
    } catch (error) {
      console.error('Error toggling availability:', error);
    }
  };

  // Toggle featured status of a product
  const toggleFeatured = async (productId, currentFeatured) => {
    try {
      await axios.put(`${API}/products/${productId}/featured`);
      // Update local state immediately for better UX
      setProducts(prev => prev.map(p => 
        p.id === productId ? { ...p, featured: !currentFeatured } : p
      ));
    } catch (error) {
      console.error('Error toggling featured:', error);
      alert('Erro ao atualizar produto em destaque');
    }
  };

  const openModal = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      category_id: '',
      image_url: '',
      available: true,
      preparation_time: '',
      additional_groups: [],
      display_order: ''
    });
    setSelectedImageFile(null);
    setImagePreview(null);
    setUploadingImage(false);
    setIsModalOpen(true);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Categoria não encontrada';
  };


  const handleExport = async (format = 'csv') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/all?format=${format}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = format.toLowerCase();
      link.setAttribute('download', `cardapio_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Erro ao exportar dados. Verifique se você está logado.');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Selecione um arquivo CSV');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/import/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      setImportFile(null);
      
      // Reload data
      fetchProducts();
      fetchCategories();
      
      setTimeout(() => {
        setIsImportModalOpen(false);
        setImportResult(null);
      }, 3000);
    } catch (error) {
      console.error('Error importing data:', error);
      alert('Erro ao importar dados: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  // ===== FUNÇÕES DA GALERIA DE IMAGENS =====
  
  // Carregar imagens da galeria
  const loadGalleryImages = async (category = '', search = '') => {
    setLoadingGallery(true);
    try {
      let url = `${API}/gallery`;
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (search) params.append('search', search);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setGalleryImages(response.data);
    } catch (error) {
      console.error('Erro ao carregar galeria:', error);
    } finally {
      setLoadingGallery(false);
    }
  };

  // Carregar categorias da galeria
  const loadGalleryCategories = async () => {
    try {
      const response = await axios.get(`${API}/gallery/categories`);
      setGalleryCategories(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  // Abrir modal da galeria
  const openGalleryModal = () => {
    setShowGalleryModal(true);
    loadGalleryImages();
    loadGalleryCategories();
  };

  // Selecionar imagem da galeria
  const selectGalleryImage = (imageUrl) => {
    setFormData({ ...formData, image_url: imageUrl });
    setShowGalleryModal(false);
    toast.success('Imagem selecionada!');
  };

  // Vincular imagens automaticamente
  const autoLinkImages = async () => {
    setAutoLinkLoading(true);
    setAutoLinkResult(null);
    try {
      const response = await axios.post(`${API}/gallery/auto-link`);
      setAutoLinkResult(response.data);
      
      if (response.data.linked > 0) {
        toast.success(`${response.data.linked} produtos vinculados!`);
        fetchProducts(); // Recarregar produtos
      } else {
        toast.info('Nenhum produto foi vinculado');
      }
    } catch (error) {
      console.error('Erro ao vincular imagens:', error);
      toast.error('Erro ao vincular imagens');
    } finally {
      setAutoLinkLoading(false);
    }
  };

  // Buscar sugestões da IA para vincular imagens
  const fetchAISuggestions = async () => {
    setAiSuggestionsLoading(true);
    setAiSuggestions([]);
    setSelectedSuggestions({});
    
    try {
      const response = await axios.post(`${API}/gallery/ai-suggestions`);
      
      if (response.data.suggestions && response.data.suggestions.length > 0) {
        setAiSuggestions(response.data.suggestions);
        // Selecionar todas por padrão
        const selected = {};
        response.data.suggestions.forEach(s => {
          selected[s.product_id] = true;
        });
        setSelectedSuggestions(selected);
        setShowAISuggestionsModal(true);
      } else {
        toast.info(response.data.message || 'Nenhuma sugestão disponível');
      }
    } catch (error) {
      console.error('Erro ao buscar sugestões IA:', error);
      toast.error(error.response?.data?.detail || 'Erro ao buscar sugestões');
    } finally {
      setAiSuggestionsLoading(false);
    }
  };

  // Aplicar sugestões selecionadas
  const applyAISuggestions = async () => {
    const selectedList = aiSuggestions.filter(s => selectedSuggestions[s.product_id]);
    
    if (selectedList.length === 0) {
      toast.warning('Selecione pelo menos uma sugestão');
      return;
    }
    
    setApplyingSuggestions(true);
    
    try {
      // Garantir que cada item tem product_id e image_url
      const payload = selectedList.map(s => ({
        product_id: s.product_id,
        image_url: s.image_url
      }));
      
      console.log('Aplicando sugestões:', payload);
      
      const response = await axios.post(`${API}/gallery/apply-suggestions`, payload);
      
      console.log('Resposta:', response.data);
      
      if (response.data.applied > 0) {
        toast.success(`${response.data.applied} imagens vinculadas!`);
        // Forçar refresh dos produtos
        await fetchProducts();
        setShowAISuggestionsModal(false);
        setAiSuggestions([]);
        setSelectedSuggestions({});
      } else {
        toast.info('Nenhuma imagem foi vinculada');
      }
    } catch (error) {
      console.error('Erro ao aplicar sugestões:', error);
      toast.error('Erro ao aplicar sugestões');
    } finally {
      setApplyingSuggestions(false);
    }
  };

  // Toggle sugestão individual
  const toggleSuggestion = (productId) => {
    setSelectedSuggestions(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  // Toggle todas as sugestões
  const toggleAllSuggestions = (checked) => {
    const newSelected = {};
    aiSuggestions.forEach(s => {
      newSelected[s.product_id] = checked;
    });
    setSelectedSuggestions(newSelected);
  };

  const downloadTemplate = () => {
    const template = `tipo,nome,descricao,preco,categoria,categoria_cor,imagem_url,disponivel,tempo_preparo
categoria,Lanches,Hambúrgueres e sanduíches artesanais,,#ef4444,,,
categoria,Bebidas,Sucos e refrigerantes gelados,,#2ECC71,,,
categoria,Sobremesas,Doces e sobremesas deliciosas,,#22c55e,,,
produto,Hambúrguer Artesanal,Pão artesanal com carne 180g e queijo cheddar,25.90,Lanches,,https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=500,true,20
produto,X-Bacon,Hambúrguer com bacon crocante e queijo,22.90,Lanches,,https://images.unsplash.com/photo-1553979459-d2229ba7433b?w=500,true,15
produto,X-Salada,Hambúrguer com alface tomate e queijo,18.90,Lanches,,,true,15
produto,Refrigerante Lata,Coca-Cola Pepsi ou Guaraná 350ml,5.00,Bebidas,,,true,
produto,Suco Natural,Suco de laranja ou limão feito na hora,8.00,Bebidas,,,true,5
produto,Água Mineral,Água mineral sem gás 500ml,3.50,Bebidas,,,true,
produto,Pudim,Pudim de leite condensado caseiro,12.90,Sobremesas,,,true,
produto,Brownie,Brownie de chocolate com sorvete,15.90,Sobremesas,,,true,10
adicional,Bacon Extra,Porção extra de bacon crocante,3.50,,,,true,
adicional,Queijo Extra,Fatia adicional de queijo cheddar,2.50,,,,true,
adicional,Ovo,Ovo frito,2.00,,,,true,

INSTRUÇÕES DE PREENCHIMENTO:
=============================
1. NÃO remova a primeira linha (cabeçalho)
2. Tipo: use "categoria" "produto" ou "adicional"
3. Nome: obrigatório para todos
4. Preço: obrigatório para produtos e adicionais (use ponto como separador decimal)
5. Categoria: obrigatória para produtos (deve corresponder ao nome exato de uma categoria)
6. Categoria_cor: código hexadecimal (ex: #ef4444) - apenas para categorias
7. Disponível: true ou false (padrão: true)
8. Tempo_preparo: em minutos - opcional

DICAS:
- Crie as categorias ANTES dos produtos
- Use vírgula (,) como separador
- Não use vírgulas dentro dos campos
- URLs de imagens devem começar com http:// ou https://`;

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template_importacao.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleCreateCategoryInline = async (e) => {
    e.preventDefault();
    if (!newCategoryData.name.trim()) {
      alert('Por favor, insira o nome da categoria');
      return;
    }
    
    try {
      setCreatingCategory(true);
      const response = await axios.post(`${API}/categories`, newCategoryData);
      const newCategory = response.data;
      
      // Atualizar lista de categorias
      await fetchCategories();
      
      // Selecionar automaticamente a nova categoria
      setFormData({...formData, category_id: newCategory.id});
      
      // Resetar e fechar modal
      setNewCategoryData({ name: '', description: '', color: '#2ECC71' });
      setIsCreateCategoryModalOpen(false);
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Erro ao criar categoria: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingCategory(false);
    }
  };

  const openCreateCategoryModal = () => {
    setNewCategoryData({ name: '', description: '', color: '#2ECC71' });
    setIsCreateCategoryModalOpen(true);
  };

  const handleCreateDemoData = async () => {
    if (!window.confirm('Deseja criar dados demonstrativos?\n\nIsso criará:\n• 4 categorias (Hambúrgueres, Pizzas, Bebidas, Sobremesas)\n• 8 produtos de exemplo\n• 3 grupos de adicionais\n\nATENÇÃO: Só funciona se o restaurante ainda não tiver categorias.')) {
      return;
    }

    try {
      setCreatingDemo(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/demo/populate`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        alert(`✅ ${response.data.message}\n\n` +
              `• ${response.data.created.categories} categorias\n` +
              `• ${response.data.created.products} produtos\n` +
              `• ${response.data.created.additional_groups} grupos de adicionais`);
        
        // Recarregar dados
        await fetchProducts();
        await fetchCategories();
        await fetchAdditionalGroups();
      }
    } catch (error) {
      console.error('Error creating demo data:', error);
      alert('❌ Erro ao criar dados demonstrativos:\n' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingDemo(false);
    }
  };


  if (loading) {
    return (
      <div className="glass-card">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
          <span className="ml-3 text-gray-400">Carregando produtos...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Debug Info - Remover após corrigir */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-xs text-gray-400">
        <strong>Debug v2:</strong> products.length = {products.length} | 
        categories.length = {categories.length} | 
        API = {API?.substring(0, 50)}...
      </div>
      
      {/* Header - Responsivo */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Produtos</h1>
              <p className="text-sm text-gray-400 mt-1">
                {products.length} {products.length === 1 ? 'produto' : 'produtos'} no cardápio
              </p>
            </div>
            {/* Toggle para mostrar/ocultar imagens */}
            <button
              onClick={() => setShowProductImages(!showProductImages)}
              className="p-2 bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700 rounded-lg transition-all group"
              title={showProductImages ? "Ocultar imagens (modo compacto)" : "Mostrar imagens"}
              data-testid="toggle-product-images-list"
            >
              {showProductImages ? (
                <Eye className="w-5 h-5 text-emerald-400 group-hover:text-emerald-300 transition-colors" />
              ) : (
                <EyeOff className="w-5 h-5 text-gray-400 group-hover:text-gray-300 transition-colors" />
              )}
            </button>
            
            {/* Dropdown de Filtro por Categoria - Discreto e Elegante */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-2 text-gray-500 hover:text-gray-300 hover:bg-gray-800/50 rounded-lg transition-colors relative"
                  title="Filtrar por categoria"
                  data-testid="filter-dropdown-button"
                >
                  <Filter size={20} />
                  {selectedCategoryFilter !== 'all' && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 rounded-full"></span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-gray-900 border-gray-700 w-56">
                <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase">
                  Filtrar por Categoria
                </div>
                <DropdownMenuItem 
                  onClick={() => setSelectedCategoryFilter('all')}
                  className={`text-gray-300 hover:bg-gray-800 cursor-pointer ${
                    selectedCategoryFilter === 'all' ? 'bg-gray-800/50' : ''
                  }`}
                  data-testid="filter-all-dropdown"
                >
                  <Filter size={16} className="mr-2" />
                  Todos os Produtos
                  <span className="ml-auto text-xs text-gray-500">({products.length})</span>
                </DropdownMenuItem>
                
                {categories.length > 0 && (
                  <>
                    <div className="h-px bg-gray-700 my-1"></div>
                    {categories.map((category) => (
                      <DropdownMenuItem 
                        key={category.id}
                        onClick={() => setSelectedCategoryFilter(category.id)}
                        className={`text-gray-300 hover:bg-gray-800 cursor-pointer ${
                          selectedCategoryFilter === category.id ? 'bg-gray-800/50' : ''
                        }`}
                        data-testid={`filter-category-${category.id}`}
                      >
                        <div 
                          className="w-3 h-3 rounded-full mr-2" 
                          style={{ backgroundColor: category.color }}
                        ></div>
                        {category.name}
                        <span className="ml-auto text-xs text-gray-500">
                          ({products.filter(p => p.category_id === category.id).length})
                        </span>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                
                {categories.length === 0 && (
                  <>
                    <div className="h-px bg-gray-700 my-1"></div>
                    <div className="px-3 py-2 text-sm text-gray-500 italic">
                      Nenhuma categoria criada
                    </div>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <button 
            onClick={openModal} 
            className="glass-button-primary w-full sm:w-auto py-3 px-6 text-base font-medium"
            data-testid="new-product-button"
          >
            <PlusCircle size={20} className="mr-2" />
            Novo Produto
          </button>
        </div>
        
        {/* Import/Export Menu - Optimized */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-400 hover:text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-colors"
              data-testid="import-export-menu-button"
              title="Importar/Exportar"
            >
              <MoreVertical size={18} />
              <span className="text-sm font-medium">Opções</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-gray-900 border-gray-700 w-56">
            <DropdownMenuItem 
              onClick={() => handleExport('csv')}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="export-csv-button"
            >
              <Download size={16} className="mr-2" />
              Exportar como CSV
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => handleExport('xlsx')}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="export-xlsx-button"
            >
              <Download size={16} className="mr-2" />
              Exportar como XLSX
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setIsImportModalOpen(true)}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="import-button"
            >
              <Upload size={16} className="mr-2" />
              Importar CSV/XLSX
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={downloadTemplate}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="template-button"
            >
              <FileText size={16} className="mr-2" />
              Baixar Template
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-gray-700" />
            <DropdownMenuItem 
              onClick={fetchAISuggestions}
              disabled={aiSuggestionsLoading}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="ai-suggestions-button"
            >
              {aiSuggestionsLoading ? (
                <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Sparkles size={16} className="mr-2 text-purple-400" />
              )}
              Vincular com IA (sugestões)
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={autoLinkImages}
              disabled={autoLinkLoading}
              className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              data-testid="auto-link-images-button"
            >
              {autoLinkLoading ? (
                <div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <ImageIcon size={16} className="mr-2" />
              )}
              Vincular automático (rápido)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Grid de Produtos - Responsivo Otimizado */}
      <div className={`grid gap-3 sm:gap-4 ${
        showProductImages 
          ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' 
          : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
      }`}>
        {products
          .filter(product => 
            selectedCategoryFilter === 'all' || product.category_id === selectedCategoryFilter
          )
          .map((product) => (
          <div 
            key={product.id} 
            className={`glass-card product-card ${!showProductImages ? 'flex items-center gap-4' : ''}`} 
            style={{ padding: showProductImages ? '16px' : '12px 16px' }}
          >
            {/* Imagem - Só mostra quando showProductImages é true */}
            {showProductImages && product.image_url && (
              <div className="w-full h-40 rounded-lg overflow-hidden mb-3">
                <img 
                  src={product.image_url} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg";
                  }}
                />
              </div>
            )}
            
            {/* Conteúdo - Layout diferente baseado no modo */}
            <div className={`${!showProductImages ? 'flex-1 flex items-center justify-between gap-4' : ''}`}>
              <div className={`${!showProductImages ? 'flex-1 min-w-0' : 'flex items-start justify-between mb-2'}`}>
                <div className="flex-1 min-w-0">
                  <h3 className={`font-semibold text-white truncate ${showProductImages ? 'text-base mb-1' : 'text-sm'}`}>{product.name}</h3>
                  <p className="text-xs text-gray-400 mb-1">{getCategoryName(product.category_id)}</p>
                  {showProductImages && (
                    <p className="text-xl font-bold text-green-400">R$ {product.price.toFixed(2)}</p>
                  )}
                </div>
                
                {/* Preço e Botões no modo compacto ficam na mesma linha */}
                {!showProductImages && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <p className="text-lg font-bold text-green-400 whitespace-nowrap">R$ {product.price.toFixed(2)}</p>
                  </div>
                )}
              </div>
              
              {/* Botões de Ação */}
              <div className={`flex items-center gap-1 ${showProductImages ? '' : 'flex-shrink-0'}`}>
                {/* Botão de Destaque */}
                <button
                  onClick={() => toggleFeatured(product.id, product.featured)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    product.featured 
                      ? 'text-orange-400 bg-orange-400/20 hover:bg-orange-400/30' 
                      : 'text-gray-400 hover:bg-gray-700 hover:text-orange-400'
                  }`}
                  title={product.featured ? 'Remover destaque' : 'Adicionar aos destaques'}
                  data-testid={`toggle-featured-${product.id}`}
                >
                  <Star size={16} className={product.featured ? 'fill-current' : ''} />
                </button>
                <button
                  onClick={() => toggleAvailability(product.id)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    product.available 
                      ? 'text-green-400 hover:bg-green-400/20' 
                      : 'text-red-400 hover:bg-red-400/20'
                  }`}
                  title={product.available ? 'Disponível' : 'Indisponível'}
                  data-testid={`toggle-availability-${product.id}`}
                >
                  {product.available ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button
                  onClick={() => handleEdit(product)}
                  className="p-1.5 text-gray-400 hover:text-emerald-400 hover:bg-emerald-400/20 rounded-lg transition-colors"
                  title="Editar"
                  data-testid={`edit-product-${product.id}`}
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => handleDelete(product.id)}
                  className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-400/20 rounded-lg transition-colors"
                  title="Excluir"
                  data-testid={`delete-product-${product.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            {/* Descrição e badges - Só no modo com imagens */}
            {showProductImages && product.description && (
              <p className="text-gray-400 text-xs mb-2 line-clamp-2">{product.description}</p>
            )}

            {/* Badges de tempo e disponibilidade - Só no modo com imagens */}
            {showProductImages && (
              <div className="flex items-center justify-between flex-wrap gap-1.5">
                {product.preparation_time && (
                  <div className="flex items-center text-gray-400 text-xs">
                    <Clock size={12} className="mr-1" />
                    {product.preparation_time}min
                  </div>
                )}
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  product.available 
                    ? 'bg-green-400/20 text-green-400' 
                    : 'bg-red-400/20 text-red-400'
                }`}>
                  {product.available ? 'Disponível' : 'Indisponível'}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {products.length === 0 && (
        <div className="glass-card text-center py-12 px-4">
          <Package size={56} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Nenhum produto encontrado</h3>
          <p className="text-gray-400 mb-6">Adicione seus primeiros produtos ao cardápio</p>
          <button 
            onClick={openModal} 
            className="glass-button-primary py-3 px-6"
            data-testid="create-first-product-button"
          >
            <PlusCircle size={20} className="mr-2" />
            Criar Primeiro Produto
          </button>
        </div>
      )}

      {/* Modal - Otimizado para Mobile */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="glass-modal max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {editingProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Nome - Campo grande para mobile */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Nome do Produto *
              </label>
              <input
                type="text"
                required
                className="glass-input w-full text-base py-3 px-4"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ex: Hambúrguer Artesanal"
                data-testid="product-name-input"
              />
            </div>

            {/* Preço e Categoria - Layout responsivo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-base font-medium text-gray-300 mb-2">
                  Preço (R$) *
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-base">
                    R$
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    required
                    className="glass-input w-full text-base py-3 pl-12 pr-4"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="0.00"
                    inputMode="decimal"
                    data-testid="product-price-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-base font-medium text-gray-300 mb-2">
                  Categoria *
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <select
                      value={formData.category_id}
                      onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                      required
                      className="glass-input w-full text-base py-3 px-4 pr-10 appearance-none cursor-pointer"
                      data-testid="product-category-select"
                    >
                      <option value="">Selecionar</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <ChevronRight className="absolute right-3 top-1/2 transform -translate-y-1/2 rotate-90 text-gray-400 w-5 h-5 pointer-events-none" />
                  </div>
                  <button
                    type="button"
                    onClick={openCreateCategoryModal}
                    className="glass-button-primary px-4 py-3 flex items-center justify-center"
                    title="Criar nova categoria"
                    data-testid="create-category-inline-button"
                  >
                    <Plus size={20} />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Clique no "+" para criar uma nova categoria rapidamente
                </p>
              </div>
            </div>

            {/* Ordem de Exibição - Campo opcional */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Ordem de Exibição (opcional)
              </label>
              <div className="flex gap-3 items-start">
                <input
                  type="number"
                  min="1"
                  className="glass-input w-32 text-base py-3 px-4"
                  value={formData.display_order}
                  onChange={(e) => setFormData({...formData, display_order: e.target.value})}
                  placeholder="Ex: 1"
                  data-testid="product-display-order-input"
                />
                <p className="text-xs text-gray-500 flex-1 pt-3">
                  💡 Produtos com ordem definida aparecem primeiro no menu. Sem ordem = ordenado por preço.
                </p>
              </div>
            </div>
            
            {/* Descrição - Textarea maior */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Descrição
              </label>
              <textarea
                className="glass-input w-full resize-none text-base py-3 px-4"
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descreva os ingredientes, sabor, porção..."
                data-testid="product-description-input"
              />
            </div>
            
            {/* Imagem do Produto - Upload ou URL */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Imagem do Produto
              </label>
              
              {/* Upload Section */}
              <div className="space-y-3">
                {/* File Upload */}
                <div className="bg-gray-800/30 rounded-xl p-4 border-2 border-dashed border-gray-700 hover:border-gray-600 transition-colors">
                  <input
                    type="file"
                    id="product-image-file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImageFileSelect}
                    className="hidden"
                    data-testid="product-image-file-input"
                  />
                  <label 
                    htmlFor="product-image-file" 
                    className="flex flex-col items-center justify-center cursor-pointer py-3"
                  >
                    <Upload size={32} className="text-gray-400 mb-2" />
                    <span className="text-sm text-gray-300 font-medium mb-1">
                      Clique para fazer upload
                    </span>
                    <span className="text-xs text-gray-500">
                      JPG, PNG ou WEBP (máx. 5MB)
                    </span>
                  </label>
                </div>

                {/* Preview and Upload Button */}
                {(imagePreview || selectedImageFile) && (
                  <div className="space-y-2">
                    <div className="rounded-lg overflow-hidden border border-gray-700">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-48 object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleImageUpload}
                      disabled={uploadingImage}
                      className="w-full glass-button-primary py-3 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="upload-image-button"
                    >
                      {uploadingImage ? (
                        <>
                          <div className="loading-spinner inline-block mr-2" style={{width: '16px', height: '16px'}}></div>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Upload size={18} className="mr-2" />
                          Confirmar Upload
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-700"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-2 bg-gray-900 text-gray-500">ou use URL externa</span>
                  </div>
                </div>

                {/* URL Input */}
                <input
                  type="url"
                  className="glass-input w-full text-base py-3 px-4"
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="Cole o link da imagem aqui"
                  data-testid="product-image-url-input"
                />

                {/* Botão discreto para galeria */}
                <button
                  type="button"
                  onClick={openGalleryModal}
                  className="w-full text-xs text-gray-500 hover:text-gray-300 py-2 flex items-center justify-center gap-1 transition-colors"
                  data-testid="open-gallery-button"
                >
                  <ImageIcon size={12} />
                  <span>Usar imagem da galeria</span>
                </button>
                
                {/* Final Image Preview */}
                {formData.image_url && !imagePreview && (
                  <div className="mt-3 rounded-lg overflow-hidden border border-gray-700">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-40 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
              
              <p className="text-xs text-gray-500 mt-2">
                💡 Dica: Imagens de alta qualidade melhoram a apresentação do produto
              </p>
            </div>
            
            {/* Tempo de Preparo - Campo único em mobile */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Tempo de Preparo
              </label>
              <div className="relative">
                <input
                  type="number"
                  className="glass-input w-full text-base py-3 px-4 pr-16"
                  value={formData.preparation_time}
                  onChange={(e) => setFormData({...formData, preparation_time: e.target.value})}
                  placeholder="15"
                  inputMode="numeric"
                  data-testid="product-prep-time-input"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-base">
                  min
                </span>
              </div>
            </div>

            {/* Grupos de Adicionais */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Grupos de Adicionais
              </label>
              <div className="space-y-2 bg-gray-800/30 rounded-xl p-4">
                {additionalGroups.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-2">
                    Nenhum grupo criado. Crie grupos na seção "Grupos de Adicionais".
                  </p>
                ) : (
                  additionalGroups.map(group => (
                    <label key={group.id} className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800/70 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={(formData.additional_groups || []).includes(group.id)}
                        onChange={(e) => {
                          const currentGroups = formData.additional_groups || [];
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              additional_groups: [...currentGroups, group.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              additional_groups: currentGroups.filter(id => id !== group.id)
                            });
                          }
                        }}
                        className="w-5 h-5 text-emerald-500 border-gray-600 rounded focus:ring-emerald-500 focus:ring-offset-gray-900"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-300">{group.name}</div>
                        <div className="text-xs text-gray-500">
                          Min: {group.min_choices} | Max: {group.max_choices}
                          {group.is_required && <span className="ml-2 text-red-400">• Obrigatório</span>}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {group.additionals_count || 0} itens
                      </div>
                    </label>
                  ))
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Selecione os grupos de adicionais que os clientes poderão escolher neste produto
              </p>
            </div>
            
            {/* Switch de Disponibilidade - Maior para mobile */}
            <div className="bg-gray-800/30 rounded-xl p-4 flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="product-available" className="text-base font-medium text-gray-300 block cursor-pointer">
                  Produto Disponível
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Clientes podem ver e pedir este produto
                </p>
              </div>
              <Switch 
                id="product-available"
                checked={formData.available}
                onCheckedChange={(checked) => setFormData({...formData, available: checked})}
                data-testid="product-available-switch"
              />
            </div>
            
            {/* Botões - Touch-friendly */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-gray-800">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="w-full sm:w-auto px-6 py-3 text-base text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700 hover:border-gray-600"
                data-testid="product-cancel-button"
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                className="w-full sm:w-auto glass-button-primary py-3 px-8 text-base font-medium"
                data-testid="product-submit-button"
              >
                {editingProduct ? '✓ Salvar Alterações' : '+ Criar Produto'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      
      {/* Import Modal */}
      <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
        <DialogContent className="glass-modal max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Importar Cardápio</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Instructions */}
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-emerald-400 mb-2">📋 Instruções:</h3>
              <ul className="text-xs text-gray-300 space-y-1">
                <li>• Baixe o template CSV para ver o formato</li>
                <li>• Preencha com seus dados (produtos, categorias, adicionais)</li>
                <li>• Faça upload do arquivo CSV ou XLSX preenchido</li>
              </ul>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Selecionar Arquivo CSV ou XLSX
              </label>
              <input
                type="file"
                accept=".csv,.xlsx"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-500 file:text-white hover:file:bg-emerald-600 cursor-pointer"
                data-testid="import-file-input"
              />
              {importFile && (
                <p className="text-sm text-green-400 mt-2">
                  ✓ Arquivo selecionado: {importFile.name}
                </p>
              )}
            </div>

            {/* Template Button */}
            <button
              onClick={downloadTemplate}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-colors"
            >
              <FileText size={18} />
              <span>Baixar Template de Exemplo</span>
            </button>

            {/* Import Result */}
            {importResult && (
              <div className="space-y-3">
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-green-400 mb-2">✓ Importação Concluída!</h3>
                  <ul className="text-xs text-gray-300 space-y-1">
                    <li>• {importResult.categories_created} categorias criadas</li>
                    <li>• {importResult.products_created} produtos criados</li>
                    <li>• {importResult.additionals_created} adicionais criados</li>
                    <li className="font-semibold text-green-400 mt-2">
                      Total: {importResult.total_created || 0} itens importados
                    </li>
                  </ul>
                </div>
                
                {importResult.warnings && importResult.warnings.length > 0 && (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
                    <p className="text-xs font-semibold text-yellow-400 mb-2">⚠️ Avisos ({importResult.warnings.length}):</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {importResult.warnings.map((warn, i) => (
                        <p key={i} className="text-xs text-yellow-300">• {warn}</p>
                      ))}
                    </div>
                  </div>
                )}
                
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                    <p className="text-xs font-semibold text-red-400 mb-2">❌ Erros ({importResult.errors.length}):</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {importResult.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-300">• {err}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsImportModalOpen(false);
                  setImportFile(null);
                  setImportResult(null);
                }}
                className="flex-1 px-4 py-3 text-base text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700 hover:border-gray-600"
                disabled={importing}
              >
                Cancelar
              </button>
              <button
                onClick={handleImport}
                disabled={!importFile || importing}
                className="flex-1 glass-button-primary py-3 px-6 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="import-upload-button"
              >
                {importing ? 'Importando...' : 'Importar'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Vincular Imagens Automaticamente */}
      <Dialog open={showAutoLinkModal} onOpenChange={setShowAutoLinkModal}>
        <DialogContent className="glass-modal max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Vincular Imagens</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
              <p className="text-sm text-gray-300">
                Esta função vincula automaticamente imagens da galeria aos produtos que não possuem imagem, 
                baseado em palavras-chave do nome e descrição.
              </p>
            </div>

            {autoLinkResult && (
              <div className={`rounded-xl p-4 ${autoLinkResult.linked > 0 ? 'bg-green-500/10 border border-green-500/20' : 'bg-yellow-500/10 border border-yellow-500/20'}`}>
                <p className={`text-sm font-semibold ${autoLinkResult.linked > 0 ? 'text-green-400' : 'text-yellow-400'} mb-2`}>
                  {autoLinkResult.linked > 0 ? '✓' : '⚠️'} {autoLinkResult.message}
                </p>
                {autoLinkResult.details && autoLinkResult.details.length > 0 && (
                  <div className="max-h-40 overflow-y-auto space-y-1 mt-2">
                    {autoLinkResult.details.map((item, i) => (
                      <p key={i} className="text-xs text-gray-400">
                        • {item.product} → {item.image} ({item.score}% match)
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowAutoLinkModal(false);
                  setAutoLinkResult(null);
                }}
                className="flex-1 px-4 py-3 text-base text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700 hover:border-gray-600"
                disabled={autoLinkLoading}
              >
                Fechar
              </button>
              <button
                onClick={autoLinkImages}
                disabled={autoLinkLoading}
                className="flex-1 glass-button-primary py-3 px-6 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {autoLinkLoading ? 'Vinculando...' : 'Vincular Agora'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Galeria de Imagens */}
      <Dialog open={showGalleryModal} onOpenChange={setShowGalleryModal}>
        <DialogContent className="glass-modal max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Galeria de Imagens</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            {/* Filtros */}
            <div className="flex gap-3">
              <select
                value={galleryFilter}
                onChange={(e) => {
                  setGalleryFilter(e.target.value);
                  loadGalleryImages(e.target.value, gallerySearch);
                }}
                className="glass-input px-3 py-2 text-sm"
              >
                <option value="">Todas categorias</option>
                {galleryCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Buscar..."
                value={gallerySearch}
                onChange={(e) => {
                  setGallerySearch(e.target.value);
                  loadGalleryImages(galleryFilter, e.target.value);
                }}
                className="glass-input flex-1 px-3 py-2 text-sm"
              />
            </div>

            {/* Grid de Imagens */}
            <div className="flex-1 overflow-y-auto">
              {loadingGallery ? (
                <div className="flex items-center justify-center py-8">
                  <div className="loading-spinner"></div>
                </div>
              ) : galleryImages.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <ImageIcon size={48} className="mx-auto mb-2 opacity-50" />
                  <p>Nenhuma imagem encontrada</p>
                  <p className="text-xs mt-1">A galeria será preenchida pelo administrador</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {galleryImages.map(image => (
                    <div
                      key={image.id}
                      onClick={() => selectGalleryImage(image.image_url)}
                      className="group relative aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-green-500 cursor-pointer transition-all"
                    >
                      <img
                        src={image.image_url}
                        alt={image.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2">
                        <p className="text-white text-xs font-medium text-center line-clamp-2">{image.name}</p>
                        <p className="text-green-400 text-[10px] mt-1">Clique para usar</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Category Inline Modal */}
      <Dialog open={isCreateCategoryModalOpen} onOpenChange={setIsCreateCategoryModalOpen}>
        <DialogContent className="glass-modal max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Nova Categoria</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateCategoryInline} className="space-y-4">
            {/* Nome da Categoria */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Nome da Categoria *
              </label>
              <input
                type="text"
                required
                className="glass-input w-full text-base py-3 px-4"
                value={newCategoryData.name}
                onChange={(e) => setNewCategoryData({...newCategoryData, name: e.target.value})}
                placeholder="Ex: Lanches, Bebidas, Sobremesas..."
                data-testid="inline-category-name-input"
                autoFocus
              />
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Descrição (opcional)
              </label>
              <textarea
                className="glass-input w-full resize-none text-base py-3 px-4"
                rows={2}
                value={newCategoryData.description}
                onChange={(e) => setNewCategoryData({...newCategoryData, description: e.target.value})}
                placeholder="Breve descrição da categoria"
                data-testid="inline-category-description-input"
              />
            </div>

            {/* Seletor de Cor */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Cor da Categoria
              </label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`w-10 h-10 rounded-lg border-2 transition-all ${
                      newCategoryData.color === color.value 
                        ? 'border-white scale-110 shadow-lg' 
                        : 'border-gray-600 hover:border-gray-400 hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.color }}
                    onClick={() => setNewCategoryData({...newCategoryData, color: color.value})}
                    title={color.label}
                    data-testid={`inline-category-color-${color.label}`}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Cor selecionada: <span className="font-medium" style={{ color: newCategoryData.color }}>
                  {COLOR_OPTIONS.find(c => c.value === newCategoryData.color)?.label}
                </span>
              </p>
            </div>

            {/* Botões de Ação */}
            <div className="flex gap-3 pt-4 border-t border-gray-800">
              <button
                type="button"
                onClick={() => {
                  setIsCreateCategoryModalOpen(false);
                  setNewCategoryData({ name: '', description: '', color: '#2ECC71' });
                }}
                className="flex-1 px-4 py-3 text-base text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700 hover:border-gray-600"
                disabled={creatingCategory}
                data-testid="inline-category-cancel-button"
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                className="flex-1 glass-button-primary py-3 px-6 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={creatingCategory || !newCategoryData.name.trim()}
                data-testid="inline-category-submit-button"
              >
                {creatingCategory ? 'Criando...' : '✓ Criar Categoria'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Sugestões IA */}
      <Dialog open={showAISuggestionsModal} onOpenChange={setShowAISuggestionsModal}>
        <DialogContent className="bg-gray-900 border border-gray-800 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Sparkles size={22} className="text-purple-400" />
              Sugestões da IA para Vincular Imagens
            </DialogTitle>
            <p className="text-gray-400 text-sm">
              Analise as sugestões e selecione quais deseja aplicar
            </p>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-3 pr-2" style={{ maxHeight: '50vh' }}>
            {aiSuggestions.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Sparkles size={40} className="mx-auto mb-3 opacity-30" />
                <p>Nenhuma sugestão disponível</p>
              </div>
            ) : (
              <>
                {/* Toggle All */}
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                  <span className="text-gray-300 font-medium">Selecionar todas</span>
                  <input
                    type="checkbox"
                    checked={Object.values(selectedSuggestions).every(v => v)}
                    onChange={(e) => toggleAllSuggestions(e.target.checked)}
                    className="w-5 h-5 rounded bg-gray-700 border-gray-600 text-purple-500 focus:ring-purple-500"
                  />
                </div>

                {/* Lista de Sugestões */}
                {aiSuggestions.map((suggestion) => (
                  <div
                    key={suggestion.product_id}
                    className={`p-3 rounded-lg border transition-all cursor-pointer ${
                      selectedSuggestions[suggestion.product_id]
                        ? 'bg-purple-500/10 border-purple-500/50'
                        : 'bg-gray-800/30 border-gray-700 hover:border-gray-600'
                    }`}
                    onClick={() => toggleSuggestion(suggestion.product_id)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedSuggestions[suggestion.product_id] || false}
                        onChange={() => toggleSuggestion(suggestion.product_id)}
                        className="w-5 h-5 mt-1 rounded bg-gray-700 border-gray-600 text-purple-500 focus:ring-purple-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                      
                      {/* Imagem sugerida */}
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-800">
                        <img
                          src={suggestion.image_url}
                          alt={suggestion.image_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg";
                          }}
                        />
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium truncate">{suggestion.product_name}</span>
                          <span className="text-gray-500">→</span>
                          <span className="text-purple-400 truncate">{suggestion.image_name}</span>
                        </div>
                        {suggestion.product_description && (
                          <p className="text-gray-500 text-sm truncate mt-1">{suggestion.product_description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            suggestion.confidence >= 80 ? 'bg-green-500/20 text-green-400' :
                            suggestion.confidence >= 60 ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {suggestion.confidence}% confiança
                          </span>
                          {suggestion.reason && (
                            <span className="text-xs text-gray-500">{suggestion.reason}</span>
                          )}
                        </div>
                        {/* Tags */}
                        {suggestion.image_tags && suggestion.image_tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {suggestion.image_tags.slice(0, 4).map((tag, i) => (
                              <span key={i} className="text-xs px-1.5 py-0.5 bg-gray-700 text-gray-400 rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          
          {/* Rodapé com ações */}
          <div className="flex gap-3 pt-4 border-t border-gray-800">
            <button
              onClick={() => setShowAISuggestionsModal(false)}
              className="flex-1 px-4 py-2 text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={applyAISuggestions}
              disabled={applyingSuggestions || Object.values(selectedSuggestions).filter(v => v).length === 0}
              className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {applyingSuggestions ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Aplicando...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Aplicar {Object.values(selectedSuggestions).filter(v => v).length} selecionadas
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

// Additional Groups Content Component
const AdditionalGroupsContent = () => {
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isAdditionalsModalOpen, setIsAdditionalsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroupForCategories, setSelectedGroupForCategories] = useState(null);
  const [selectedGroupForAdditionals, setSelectedGroupForAdditionals] = useState(null);
  const [linkedCategories, setLinkedCategories] = useState([]);
  const [groupAdditionals, setGroupAdditionals] = useState([]);
  const [linkingCategory, setLinkingCategory] = useState(null);
  const [togglingAdditional, setTogglingAdditional] = useState(null);
  const [showNewAdditionalForm, setShowNewAdditionalForm] = useState(false);
  const [newAdditionalData, setNewAdditionalData] = useState({ name: '', price: '' });
  const [creatingAdditional, setCreatingAdditional] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    min_choices: '0',
    max_choices: '10',
    is_required: false,
    limit_one_per_item: false,
    cobrar_mais_caro: false
  });

  useEffect(() => {
    fetchGroups();
    fetchCategories();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/additional-groups`);
      setGroups(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchLinkedCategories = async (groupId) => {
    try {
      const response = await axios.get(`${API}/additional-groups/${groupId}/linked-categories`);
      setLinkedCategories(response.data);
    } catch (error) {
      console.error('Error fetching linked categories:', error);
      setLinkedCategories([]);
    }
  };

  const handleLinkCategory = async (categoryId, isLinked) => {
    if (!selectedGroupForCategories) return;
    
    setLinkingCategory(categoryId);
    try {
      if (isLinked) {
        // Unlink
        const response = await axios.delete(`${API}/additional-groups/${selectedGroupForCategories.id}/link-category/${categoryId}`);
        toast.success(`${response.data.unlinked_count} produto(s) desvinculado(s) da categoria ${response.data.category_name}`);
      } else {
        // Link
        const response = await axios.post(`${API}/additional-groups/${selectedGroupForCategories.id}/link-category`, {
          category_id: categoryId
        });
        toast.success(`${response.data.linked_count} produto(s) vinculado(s) à categoria ${response.data.category_name}`);
      }
      // Refresh linked categories
      await fetchLinkedCategories(selectedGroupForCategories.id);
    } catch (error) {
      console.error('Error linking/unlinking category:', error);
      toast.error(error.response?.data?.detail || 'Erro ao vincular/desvincular categoria');
    } finally {
      setLinkingCategory(null);
    }
  };

  const openCategoryModal = async (group) => {
    setSelectedGroupForCategories(group);
    await fetchLinkedCategories(group.id);
    setIsCategoryModalOpen(true);
  };

  // === Funções para gerenciar adicionais do grupo ===
  const fetchGroupAdditionals = async (groupId) => {
    try {
      const response = await axios.get(`${API}/additional-groups/${groupId}/additionals`);
      setGroupAdditionals(response.data.additionals || []);
    } catch (error) {
      console.error('Error fetching group additionals:', error);
      setGroupAdditionals([]);
    }
  };

  const openAdditionalsModal = async (group) => {
    setSelectedGroupForAdditionals(group);
    await fetchGroupAdditionals(group.id);
    setIsAdditionalsModalOpen(true);
  };

  const handleToggleAdditional = async (additionalId, isLinked) => {
    if (!selectedGroupForAdditionals) return;
    
    setTogglingAdditional(additionalId);
    try {
      const response = await axios.patch(
        `${API}/additionals/${additionalId}/toggle-group/${selectedGroupForAdditionals.id}`
      );
      
      if (response.data.is_linked) {
        toast.success(`"${response.data.additional_name}" vinculado ao grupo`);
      } else {
        toast.success(`"${response.data.additional_name}" desvinculado do grupo`);
      }
      
      // Refresh the list
      await fetchGroupAdditionals(selectedGroupForAdditionals.id);
      // Also refresh groups to update count
      fetchGroups();
    } catch (error) {
      console.error('Error toggling additional:', error);
      toast.error(error.response?.data?.detail || 'Erro ao alterar vinculação');
    } finally {
      setTogglingAdditional(null);
    }
  };

  const handleCreateQuickAdditional = async (e) => {
    e.preventDefault();
    if (!selectedGroupForAdditionals || !newAdditionalData.name || !newAdditionalData.price) return;
    
    setCreatingAdditional(true);
    try {
      // Create the additional already linked to this group
      await axios.post(`${API}/additionals`, {
        name: newAdditionalData.name,
        description: '',
        price: parseFloat(newAdditionalData.price),
        group_ids: [selectedGroupForAdditionals.id],
        available: true
      });
      
      toast.success(`"${newAdditionalData.name}" criado e vinculado ao grupo`);
      
      // Reset form and refresh
      setNewAdditionalData({ name: '', price: '' });
      setShowNewAdditionalForm(false);
      await fetchGroupAdditionals(selectedGroupForAdditionals.id);
      fetchGroups();
    } catch (error) {
      console.error('Error creating additional:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar adicional');
    } finally {
      setCreatingAdditional(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const groupData = {
        name: formData.name,
        description: formData.description,
        min_choices: parseInt(formData.min_choices),
        max_choices: parseInt(formData.max_choices),
        is_required: formData.is_required,
        limit_one_per_item: formData.limit_one_per_item,
        cobrar_mais_caro: formData.cobrar_mais_caro
      };

      if (editingGroup) {
        await axios.put(`${API}/additional-groups/${editingGroup.id}`, groupData);
      } else {
        await axios.post(`${API}/additional-groups`, groupData);
      }

      setIsModalOpen(false);
      setFormData({ name: '', description: '', min_choices: '0', max_choices: '10', is_required: false, limit_one_per_item: false, cobrar_mais_caro: false });
      setEditingGroup(null);
      fetchGroups();
    } catch (error) {
      console.error('Error saving group:', error);
      alert(error.response?.data?.detail || 'Erro ao salvar grupo');
    }
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name || '',
      description: group.description || '',
      min_choices: group.min_choices != null ? group.min_choices.toString() : '0',
      max_choices: group.max_choices != null ? group.max_choices.toString() : '1',
      is_required: group.is_required !== false,
      limit_one_per_item: group.limit_one_per_item || false,
      cobrar_mais_caro: group.cobrar_mais_caro || false
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (groupId) => {
    if (window.confirm('Tem certeza que deseja excluir este grupo?')) {
      try {
        await axios.delete(`${API}/additional-groups/${groupId}`);
        fetchGroups();
      } catch (error) {
        console.error('Error deleting group:', error);
        alert(error.response?.data?.detail || 'Erro ao excluir grupo');
      }
    }
  };

  const openModal = () => {
    setEditingGroup(null);
    setFormData({ name: '', description: '', min_choices: '0', max_choices: '10', is_required: false, limit_one_per_item: false, cobrar_mais_caro: false });
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="glass-card">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Grupos de Adicionais</h1>
          <p className="text-gray-400 mt-2">Organize seus adicionais em grupos com controle de escolhas mínimas e máximas</p>
        </div>
        <button onClick={openModal} className="glass-button-primary">
          <PlusCircle size={18} className="mr-2" />
          Novo Grupo
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="glass-card text-center py-12">
          <Package size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Nenhum grupo cadastrado</h3>
          <p className="text-gray-400 mb-6">Crie grupos para organizar seus adicionais com regras personalizadas</p>
          <button onClick={openModal} className="glass-button-primary">
            Criar Primeiro Grupo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.map((group) => (
            <div key={group.id} className="glass-card group-card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                    <Package size={24} className="text-white" />
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => openAdditionalsModal(group)}
                    className="p-2 text-gray-400 hover:text-amber-400 transition-colors"
                    title="Gerenciar Adicionais"
                  >
                    <Plus size={16} />
                  </button>
                  <button
                    onClick={() => openCategoryModal(group)}
                    className="p-2 text-gray-400 hover:text-green-400 transition-colors"
                    title="Vincular a Categorias"
                  >
                    <Layers size={16} />
                  </button>
                  <button
                    onClick={() => handleEdit(group)}
                    className="p-2 text-gray-400 hover:text-emerald-400 transition-colors"
                    title="Editar Grupo"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(group.id)}
                    className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                    title="Excluir Grupo"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-white mb-2">{group.name}</h3>
              {group.description && (
                <p className="text-gray-400 text-sm mb-4">{group.description}</p>
              )}

              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div className="flex items-center space-x-2 text-sm">
                    <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <span className="text-emerald-400 font-bold text-xs">MIN</span>
                    </div>
                    <span className="text-gray-300">Mínimo</span>
                  </div>
                  <span className="text-white font-bold text-lg">{group.min_choices}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <div className="flex items-center space-x-2 text-sm">
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                      <span className="text-green-400 font-bold text-xs">MAX</span>
                    </div>
                    <span className="text-gray-300">Máximo</span>
                  </div>
                  <span className="text-white font-bold text-lg">{group.max_choices}</span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                <div className="flex items-center space-x-2 text-gray-400 text-sm">
                  <Plus size={16} />
                  <span>{group.additionals_count || 0} adicionais</span>
                </div>
                <div className="flex items-center gap-2">
                  {group.is_required && (
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full font-medium">
                      Obrigatório
                    </span>
                  )}
                  {!group.is_required && (
                    <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded-full font-medium">
                      Opcional
                    </span>
                  )}
                  {group.limit_one_per_item && (
                    <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded-full font-medium">
                      1 por item
                    </span>
                  )}
                  {group.cobrar_mais_caro && (
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full font-medium">
                      Cobra só o mais caro
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="glass-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingGroup ? 'Editar Grupo' : 'Novo Grupo'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nome do Grupo *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="glass-input w-full"
                placeholder="Ex: Ponto da Carne, Molhos, Extras"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Descrição
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="glass-input w-full resize-none"
                placeholder="Descrição opcional do grupo"
                rows="2"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Mínimo de Escolhas
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.min_choices}
                  onChange={(e) => setFormData({ ...formData, min_choices: e.target.value })}
                  className="glass-input w-full"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">0 = opcional</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Máximo de Escolhas
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_choices}
                  onChange={(e) => setFormData({ ...formData, max_choices: e.target.value })}
                  className="glass-input w-full"
                  required
                />
              </div>
            </div>

            <div className="flex items-center p-4 bg-gray-800/30 rounded-lg">
              <input
                type="checkbox"
                id="is_required"
                checked={formData.is_required}
                onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 rounded focus:ring-orange-500"
              />
              <label htmlFor="is_required" className="ml-3 text-sm text-gray-300">
                Grupo obrigatório (cliente deve escolher no mínimo definido)
              </label>
            </div>

            <div className="flex items-center p-4 bg-gray-800/30 rounded-lg">
              <input
                type="checkbox"
                id="limit_one_per_item"
                checked={formData.limit_one_per_item}
                onChange={(e) => setFormData({ ...formData, limit_one_per_item: e.target.checked })}
                className="w-4 h-4 text-cyan-500 bg-gray-700 border-gray-600 rounded focus:ring-cyan-500"
              />
              <label htmlFor="limit_one_per_item" className="ml-3 text-sm text-gray-300">
                <span className="font-medium text-cyan-400">Limitar 1 unidade por item</span>
                <span className="block text-xs text-gray-400 mt-0.5">Cliente não pode repetir o mesmo adicional (ex: não pode pedir 3x Banana)</span>
              </label>
            </div>

            <div className="flex items-center p-4 bg-gray-800/30 rounded-lg">
              <input
                type="checkbox"
                id="cobrar_mais_caro"
                checked={formData.cobrar_mais_caro}
                onChange={(e) => setFormData({ ...formData, cobrar_mais_caro: e.target.checked })}
                className="w-4 h-4 text-emerald-500 bg-gray-700 border-gray-600 rounded focus:ring-emerald-500"
              />
              <label htmlFor="cobrar_mais_caro" className="ml-3 text-sm text-gray-300">
                <span className="font-medium text-emerald-400">Cobrar apenas o item mais caro</span>
                <span className="block text-xs text-gray-400 mt-0.5">Quando ativado, cobra apenas o adicional de maior valor selecionado neste grupo</span>
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingGroup(null);
                  setFormData({ name: '', description: '', min_choices: '0', max_choices: '10', is_required: false, limit_one_per_item: false, cobrar_mais_caro: false });
                }}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button type="submit" className="glass-button-primary">
                {editingGroup ? 'Salvar Alterações' : 'Criar Grupo'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Vinculação a Categorias */}
      <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
        <DialogContent className="glass-modal max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Layers size={20} className="text-green-400" />
              Vincular a Categorias
            </DialogTitle>
            {selectedGroupForCategories && (
              <p className="text-gray-400 text-sm mt-1">
                Grupo: <span className="text-white font-medium">{selectedGroupForCategories.name}</span>
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-gray-400 text-sm">
              Selecione as categorias para vincular este grupo de adicionais a <strong>todos os produtos</strong> da categoria.
            </p>
            
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
              {linkedCategories.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Carregando categorias...</p>
              ) : (
                linkedCategories.map((category) => {
                  const isFullyLinked = category.is_fully_linked || false;
                  const isPartiallyLinked = category.is_partially_linked || false;
                  const linkedCount = category.linked_products || 0;
                  const totalCount = category.total_products || 0;
                  const isLoading = linkingCategory === category.category_id;
                  
                  return (
                    <div 
                      key={category.category_id}
                      className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                        isFullyLinked 
                          ? 'bg-green-500/20 border border-green-500/30' 
                          : isPartiallyLinked
                          ? 'bg-yellow-500/20 border border-yellow-500/30'
                          : 'bg-gray-800/50 border border-gray-700/50 hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isFullyLinked ? 'bg-green-500/30' : isPartiallyLinked ? 'bg-yellow-500/30' : 'bg-gray-700'
                        }`}>
                          <Package size={18} className={
                            isFullyLinked ? 'text-green-400' : isPartiallyLinked ? 'text-yellow-400' : 'text-gray-400'
                          } />
                        </div>
                        <div>
                          <p className="text-white font-medium">{category.category_name}</p>
                          <p className="text-gray-400 text-xs">
                            {linkedCount} de {totalCount} produtos vinculados
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {isPartiallyLinked && (
                          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                            Parcial
                          </span>
                        )}
                        <button
                          onClick={() => handleLinkCategory(category.category_id, isFullyLinked || isPartiallyLinked)}
                          disabled={isLoading || totalCount === 0}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                            isLoading
                              ? 'bg-gray-600 text-gray-400 cursor-wait'
                              : isFullyLinked || isPartiallyLinked
                              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                              : totalCount === 0
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          }`}
                        >
                          {isLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                              Processando...
                            </>
                          ) : isFullyLinked || isPartiallyLinked ? (
                            <>
                              <X size={16} />
                              Desvincular
                            </>
                          ) : (
                            <>
                              <Check size={16} />
                              Vincular
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            <div className="flex justify-end pt-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setIsCategoryModalOpen(false);
                  setSelectedGroupForCategories(null);
                  setLinkedCategories([]);
                }}
                className="glass-button-primary"
              >
                Concluir
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para Gerenciar Adicionais do Grupo */}
      <Dialog open={isAdditionalsModalOpen} onOpenChange={setIsAdditionalsModalOpen}>
        <DialogContent className="glass-modal max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Plus size={20} className="text-amber-400" />
              Adicionais do Grupo
            </DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">
              Grupo: <strong className="text-white">{selectedGroupForAdditionals?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Botão para criar novo adicional */}
            {!showNewAdditionalForm ? (
              <button
                onClick={() => setShowNewAdditionalForm(true)}
                className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-green-500/30 rounded-lg text-green-400 hover:bg-green-500/10 hover:border-green-500/50 transition-all"
              >
                <Plus size={18} />
                Criar Novo Adicional
              </button>
            ) : (
              <form onSubmit={handleCreateQuickAdditional} className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg space-y-3">
                <div className="flex items-center gap-2 text-green-400 text-sm font-medium mb-2">
                  <Plus size={16} />
                  Novo Adicional
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Nome do adicional"
                    value={newAdditionalData.name}
                    onChange={(e) => setNewAdditionalData({...newAdditionalData, name: e.target.value})}
                    className="glass-input text-sm"
                    required
                    autoFocus
                  />
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0,00"
                      value={newAdditionalData.price}
                      onChange={(e) => setNewAdditionalData({...newAdditionalData, price: e.target.value})}
                      className="glass-input text-sm pl-10"
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewAdditionalForm(false);
                      setNewAdditionalData({ name: '', price: '' });
                    }}
                    className="flex-1 px-3 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={creatingAdditional || !newAdditionalData.name || !newAdditionalData.price}
                    className="flex-1 px-3 py-2 bg-green-500/20 text-green-400 rounded-lg text-sm font-medium hover:bg-green-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {creatingAdditional ? (
                      <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Check size={16} />
                        Criar e Vincular
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
            
            {/* Lista de adicionais com checklist */}
            <div className="space-y-2 max-h-[350px] overflow-y-auto pr-2">
              {groupAdditionals.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {showNewAdditionalForm ? 'Crie seu primeiro adicional acima' : 'Nenhum adicional cadastrado. Crie um novo!'}
                </p>
              ) : (
                groupAdditionals.map((additional) => {
                  const isLoading = togglingAdditional === additional.id;
                  
                  return (
                    <div 
                      key={additional.id}
                      className={`flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer ${
                        additional.is_linked 
                          ? 'bg-amber-500/20 border border-amber-500/30' 
                          : 'bg-gray-800/50 border border-gray-700/50 hover:border-gray-600'
                      }`}
                      onClick={() => !isLoading && handleToggleAdditional(additional.id, additional.is_linked)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                          additional.is_linked 
                            ? 'bg-amber-500 border-amber-500' 
                            : 'border-gray-500 hover:border-gray-400'
                        }`}>
                          {isLoading ? (
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : additional.is_linked ? (
                            <Check size={14} className="text-white" />
                          ) : null}
                        </div>
                        <div>
                          <p className={`font-medium ${additional.is_linked ? 'text-white' : 'text-gray-300'}`}>
                            {additional.name}
                          </p>
                          {additional.other_groups_count > 0 && !additional.is_linked && (
                            <p className="text-xs text-gray-500">
                              Em {additional.other_groups_count} outro(s) grupo(s)
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${additional.is_linked ? 'text-amber-400' : 'text-gray-400'}`}>
                          R$ {additional.price?.toFixed(2)}
                        </span>
                        {!additional.available && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                            Indisponível
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            <div className="flex justify-end pt-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setIsAdditionalsModalOpen(false);
                  setSelectedGroupForAdditionals(null);
                  setGroupAdditionals([]);
                  setShowNewAdditionalForm(false);
                  setNewAdditionalData({ name: '', price: '' });
                }}
                className="glass-button-primary"
              >
                Concluir
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Additionals Content Component
const AdditionalsContent = () => {
  const [additionals, setAdditionals] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAdditional, setEditingAdditional] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    group_ids: [],
    available: true
  });

  useEffect(() => {
    fetchAdditionals();
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/additional-groups`);
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchAdditionals = async () => {
    try {
      const response = await axios.get(`${API}/additionals`);
      setAdditionals(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching additionals:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const additionalData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        group_ids: formData.group_ids || [],
        available: formData.available
      };

      if (editingAdditional) {
        await axios.put(`${API}/additionals/${editingAdditional.id}`, additionalData);
      } else {
        await axios.post(`${API}/additionals`, additionalData);
      }
      
      setIsModalOpen(false);
      setEditingAdditional(null);
      setFormData({ name: '', description: '', price: '', group_ids: [], available: true });
      fetchAdditionals();
      fetchGroups(); // Refresh group counts
    } catch (error) {
      console.error('Error saving additional:', error);
    }
  };

  const handleEdit = (additional) => {
    setEditingAdditional(additional);
    // Handle both new group_ids and legacy group_id
    let groupIds = additional.group_ids || [];
    if (groupIds.length === 0 && additional.group_id) {
      groupIds = [additional.group_id];
    }
    setFormData({
      name: additional.name || '',
      description: additional.description || '',
      price: additional.price != null ? additional.price.toString() : '',
      group_ids: groupIds,
      available: additional.available !== false
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (additionalId) => {
    if (window.confirm('Tem certeza que deseja excluir este adicional?')) {
      try {
        await axios.delete(`${API}/additionals/${additionalId}`);
        fetchAdditionals();
        fetchGroups(); // Refresh group counts
      } catch (error) {
        console.error('Error deleting additional:', error);
      }
    }
  };

  const toggleAvailability = async (additionalId) => {
    try {
      await axios.patch(`${API}/additionals/${additionalId}/toggle-availability`);
      fetchAdditionals();
    } catch (error) {
      console.error('Error toggling availability:', error);
    }
  };

  const openModal = () => {
    setEditingAdditional(null);
    setFormData({ name: '', description: '', price: '', group_ids: [], available: true });
    setIsModalOpen(true);
  };

  // Helper function to get group names for an additional
  const getGroupNames = (additional) => {
    const groupIds = additional.group_ids || (additional.group_id ? [additional.group_id] : []);
    return groupIds.map(gid => {
      const group = groups.find(g => g.id === gid);
      return group ? group.name : null;
    }).filter(Boolean);
  };

  if (loading) {
    return (
      <div className="glass-card">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">Adicionais</h1>
        <button onClick={openModal} className="glass-button-primary">
          <PlusCircle size={18} className="mr-2" />
          Novo Adicional
        </button>
      </div>

      <div className="glass-card">
        {additionals.length > 0 ? (
          <div className="space-y-4">
            {additionals.map((additional) => {
              const groupNames = getGroupNames(additional);
              return (
              <div key={additional.id} className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                    <Plus size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{additional.name}</h3>
                    <p className="text-green-400 font-medium">R$ {additional.price.toFixed(2)}</p>
                    {groupNames.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {groupNames.map((name, idx) => (
                          <span key={idx} className="px-2 py-0.5 text-xs bg-cyan-500/20 text-cyan-300 rounded">
                            {name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500">Sem grupo</span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <span className={`px-3 py-1 rounded-full text-xs ${
                    additional.available 
                      ? 'bg-green-400/20 text-green-400' 
                      : 'bg-red-400/20 text-red-400'
                  }`}>
                    {additional.available ? 'Disponível' : 'Indisponível'}
                  </span>
                  
                  <button
                    onClick={() => toggleAvailability(additional.id)}
                    className={`p-2 rounded-lg transition-colors ${
                      additional.available 
                        ? 'text-green-400 hover:bg-green-400/20' 
                        : 'text-red-400 hover:bg-red-400/20'
                    }`}
                  >
                    {additional.available ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  
                  <button
                    onClick={() => handleEdit(additional)}
                    className="p-2 text-gray-400 hover:text-emerald-400 transition-colors"
                  >
                    <Edit size={16} />
                  </button>
                  
                  <button
                    onClick={() => handleDelete(additional.id)}
                    className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Plus size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Nenhum adicional encontrado</h3>
            <p className="text-gray-400 mb-6">Adicione opções extras para seus produtos</p>
            <button onClick={openModal} className="glass-button-primary">
              Criar Primeiro Adicional
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="glass-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingAdditional ? 'Editar Adicional' : 'Novo Adicional'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
              <input
                type="text"
                required
                className="glass-input w-full"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ex: Bacon, Cheddar, Molho especial"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
              <input
                type="text"
                className="glass-input w-full"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descrição opcional"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Grupos de Adicionais</label>
              {groups.length === 0 ? (
                <p className="text-gray-400 text-sm italic py-2">
                  Nenhum grupo criado. Crie grupos em "Grupos de Adicionais" primeiro.
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto p-3 bg-white/5 rounded-lg border border-white/10">
                  {groups.map(group => (
                    <label key={group.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(formData.group_ids || []).includes(group.id)}
                        onChange={(e) => {
                          const currentGroups = formData.group_ids || [];
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              group_ids: [...currentGroups, group.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              group_ids: currentGroups.filter(id => id !== group.id)
                            });
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-cyan-500"
                      />
                      <div className="flex-1">
                        <span className="text-white">{group.name}</span>
                        <span className="text-xs text-gray-400 ml-2">
                          ({group.additionals_count || 0} adicionais)
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-2">
                Selecione os grupos onde este adicional estará disponível. Pode selecionar múltiplos grupos.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Preço *</label>
              <input
                type="number"
                step="0.01"
                required
                className="glass-input w-full"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                placeholder="0.00"
              />
            </div>
            
            <div className="flex items-center space-x-3">
              <Switch 
                id="additional-available"
                checked={formData.available}
                onCheckedChange={(checked) => setFormData({...formData, available: checked})}
              />
              <Label htmlFor="additional-available" className="text-sm text-gray-300 cursor-pointer">
                Adicional disponível
              </Label>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button type="submit" className="glass-button-primary">
                {editingAdditional ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// OrdersContent foi extraído para ./components/content/OrdersContent.js

// Public Menu Content Component
const PublicMenuContent = ({ restaurant }) => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.filter(cat => cat.active));
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      // Cache-busting header to ensure fresh data
      const response = await axios.get(`${API}/products`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      // Suportar resposta paginada ou array direto
      const productsData = response.data.items || response.data;
      const productsArray = Array.isArray(productsData) ? productsData : [];
      setProducts(productsArray.filter(prod => prod.available));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const filteredProducts = selectedCategory 
    ? products.filter(product => product.category_id === selectedCategory)
    : products;

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Categoria';
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateCartQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(cart.map(item => 
      item.id === productId 
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartItemsCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleWhatsAppOrder = () => {
    if (cart.length === 0) return;
    
    let message = `🍽️ *Pedido - ${restaurant?.name}*\n\n`;
    cart.forEach(item => {
      message += `• ${item.quantity}x ${item.name} - R$ ${(item.price * item.quantity).toFixed(2)}\n`;
    });
    message += `\n💰 *Total: R$ ${cartTotal.toFixed(2)}*`;
    
    const whatsappUrl = `https://wa.me/5511999999999?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="glass-card">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Meucardapiozap</h1>
          <p className="text-gray-400">{restaurant?.name}</p>
        </div>
        <div className="flex items-center space-x-4">
          {cart.length > 0 && (
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative glass-button-primary"
            >
              <ShoppingCart size={18} className="mr-2" />
              Carrinho ({cartItemsCount})
              {cartItemsCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItemsCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => setSelectedCategory('')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            selectedCategory === '' 
              ? 'bg-orange-500 text-white' 
              : 'bg-white/10 text-gray-300 hover:bg-white/20'
          }`}
        >
          Todos
        </button>
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedCategory === category.id 
                ? 'bg-orange-500 text-white' 
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
            style={{
              backgroundColor: selectedCategory === category.id ? category.color : undefined
            }}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Products Grid - Otimizado */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {filteredProducts.map((product) => (
          <div key={product.id} className="glass-card product-card group" style={{ padding: '16px' }}>
            {product.image_url && (
              <div className="w-full h-40 rounded-lg overflow-hidden mb-3">
                <img 
                  src={product.image_url} 
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg";
                  }}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-base font-semibold text-white truncate">{product.name}</h3>
                  <span className="text-xs text-gray-400 bg-white/10 px-2 py-0.5 rounded-full whitespace-nowrap ml-1">
                    {getCategoryName(product.category_id)}
                  </span>
                </div>
                <p className="text-gray-400 text-xs mb-2 line-clamp-2">{product.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-green-400">
                    R$ {product.price.toFixed(2)}
                  </span>
                  {product.preparation_time && (
                    <div className="flex items-center text-gray-400 text-xs">
                      <Clock size={12} className="mr-1" />
                      {product.preparation_time}min
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <div className="flex items-center">
                  <Star className="w-3 h-3 text-yellow-400 fill-current" />
                  <span className="text-yellow-400 text-xs ml-1">4.8</span>
                </div>
                <button
                  onClick={() => addToCart(product)}
                  className="bg-green-500/20 text-green-400 hover:bg-green-500/30 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                >
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="glass-card text-center py-12">
          <Package size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {selectedCategory ? 'Nenhum produto nesta categoria' : 'Nenhum produto disponível'}
          </h3>
          <p className="text-gray-400">
            {selectedCategory 
              ? 'Tente selecionar outra categoria' 
              : 'Adicione produtos ao cardápio primeiro'
            }
          </p>
        </div>
      )}

      {/* Cart Modal */}
      <Dialog open={isCartOpen} onOpenChange={setIsCartOpen}>
        <DialogContent className="glass-modal max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center">
              <ShoppingCart size={24} className="mr-2" />
              Seu Pedido
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {cart.length > 0 ? cart.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div className="flex-1">
                  <h4 className="text-white font-medium">{item.name}</h4>
                  <p className="text-green-400">R$ {item.price.toFixed(2)} cada</p>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                    className="w-8 h-8 bg-red-500/20 text-red-400 rounded-full flex items-center justify-center"
                  >
                    -
                  </button>
                  <span className="text-white w-8 text-center">{item.quantity}</span>
                  <button
                    onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                    className="w-8 h-8 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center"
                  >
                    +
                  </button>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="ml-2 text-red-400 hover:text-red-300"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-gray-400">
                <ShoppingCart size={48} className="mx-auto mb-4" />
                <p>Seu carrinho está vazio</p>
              </div>
            )}
          </div>

          {cart.length > 0 && (
            <div className="border-t border-white/10 pt-4">
              <div className="flex justify-between items-center mb-4">
                <span className="text-xl font-bold text-white">Total:</span>
                <span className="text-2xl font-bold text-green-400">
                  R$ {cartTotal.toFixed(2)}
                </span>
              </div>
              <button
                onClick={handleWhatsAppOrder}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg transition-colors"
              >
                Enviar Pedido via WhatsApp
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Mercado Pago Settings Component
const MercadoPagoSettings = () => {
  const [mpSettings, setMpSettings] = useState({
    mp_access_token: '',
    mp_public_key: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [hasToken, setHasToken] = useState(false);
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/restaurant/mercadopago-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasToken(response.data.has_token);
    } catch (err) {
      console.error('Error loading MP settings:', err);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      if (!mpSettings.mp_access_token) {
        setError('Por favor, insira o Access Token');
        return;
      }

      const token = localStorage.getItem('token');
      await axios.put(`${API}/restaurant/mercadopago-settings`, mpSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Configurações do Mercado Pago salvas com sucesso!');
      setHasToken(true);
      setMpSettings({ mp_access_token: '', mp_public_key: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar configurações');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-500/20 border-2 border-green-500/50 text-green-400 px-5 py-4 rounded-xl text-sm font-medium">
          ✓ {success}
        </div>
      )}
      {error && (
        <div className="bg-red-500/20 border-2 border-red-500/50 text-red-400 px-5 py-4 rounded-xl text-sm font-medium">
          ✗ {error}
        </div>
      )}

      {/* Status Badge */}
      {hasToken && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-400" />
            <div>
              <p className="text-green-400 font-semibold">Mercado Pago Configurado</p>
              <p className="text-gray-400 text-sm">Você já pode receber pagamentos PIX</p>
            </div>
          </div>
        </div>
      )}

      {/* Access Token Input */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-300">
          Access Token *
        </label>
        <div className="relative">
          <input
            type={showToken ? 'text' : 'password'}
            value={mpSettings.mp_access_token}
            onChange={(e) => setMpSettings({ ...mpSettings, mp_access_token: e.target.value })}
            placeholder="APP_USR-..."
            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-emerald-500 transition-colors pr-12 font-mono text-sm"
            data-testid="mp-access-token-input"
          />
          <button
            type="button"
            onClick={() => setShowToken(!showToken)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
          >
            {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        <p className="text-xs text-gray-500">
          Este token será usado para processar pagamentos PIX na SUA conta
        </p>
      </div>

      {/* Public Key Input (Optional) */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-300">
          Public Key <span className="text-gray-500">(Opcional)</span>
        </label>
        <input
          type="text"
          value={mpSettings.mp_public_key}
          onChange={(e) => setMpSettings({ ...mpSettings, mp_public_key: e.target.value })}
          placeholder="APP_USR-..."
          className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-emerald-500 transition-colors font-mono text-sm"
          data-testid="mp-public-key-input"
        />
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={loading}
        className="w-full px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="mp-save-button"
      >
        {loading ? 'Salvando...' : hasToken ? 'Atualizar Configurações' : 'Salvar Configurações'}
      </button>

      {/* Warning */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <div className="text-yellow-400 text-xl mt-0.5">⚠️</div>
          <div className="flex-1 text-sm text-gray-300">
            <p className="font-semibold text-yellow-400 mb-1">Importante!</p>
            <p>Mantenha suas credenciais seguras. Nunca compartilhe seu Access Token com terceiros.</p>
          </div>
        </div>
      </div>
    </div>
  );
};


// Settings Content Component


// Customers Content Component

// Delivery Zones Content Component
const DeliveryZonesContent = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    fee: '',
    active: true
  });

  // ============ ESTADOS PARA MODO RAIO KM ============
  const [deliveryConfig, setDeliveryConfig] = useState({
    mode: 'neighborhood', // 'neighborhood' ou 'radius'
    store_address: {
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      latitude: null,
      longitude: null
    },
    radius_zones: [],
    max_delivery_radius: null
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeSuccess, setGeocodeSuccess] = useState(false);
  const [isRadiusZoneModalOpen, setIsRadiusZoneModalOpen] = useState(false);
  const [editingRadiusZone, setEditingRadiusZone] = useState(null);
  const [radiusZoneForm, setRadiusZoneForm] = useState({
    min_distance: '',
    max_distance: '',
    fee: ''
  });

  useEffect(() => {
    fetchZones();
    fetchDeliveryConfig();
  }, []);

  // Carregar configuração de entrega
  const fetchDeliveryConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/delivery-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setDeliveryConfig({
          mode: response.data.mode || 'neighborhood',
          store_address: response.data.store_address || {
            cep: '', street: '', number: '', complement: '',
            neighborhood: '', city: '', state: '', latitude: null, longitude: null
          },
          radius_zones: response.data.radius_zones || [],
          max_delivery_radius: response.data.max_delivery_radius
        });
        if (response.data.store_address?.latitude) {
          setGeocodeSuccess(true);
        }
      }
    } catch (error) {
      console.error('Error fetching delivery config:', error);
    }
  };

  // Salvar configuração de entrega
  const saveDeliveryConfig = async () => {
    try {
      setSavingConfig(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API}/delivery-config`, deliveryConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Configuração de entrega salva com sucesso!');
      fetchDeliveryConfig(); // Recarregar para pegar coordenadas atualizadas
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setSavingConfig(false);
    }
  };

  // Geocodificar endereço da loja
  const geocodeStoreAddress = async () => {
    try {
      setGeocoding(true);
      setGeocodeSuccess(false);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/delivery-config/geocode`, deliveryConfig.store_address, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDeliveryConfig(prev => ({
        ...prev,
        store_address: {
          ...prev.store_address,
          latitude: response.data.latitude,
          longitude: response.data.longitude
        }
      }));
      setGeocodeSuccess(true);
      alert(`Endereço localizado! Coordenadas: ${response.data.latitude.toFixed(6)}, ${response.data.longitude.toFixed(6)}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao localizar endereço. Verifique os dados informados.');
    } finally {
      setGeocoding(false);
    }
  };

  // Adicionar/Editar faixa de raio
  const openRadiusZoneModal = (zone = null) => {
    if (zone) {
      setEditingRadiusZone(zone);
      setRadiusZoneForm({
        min_distance: zone.min_distance,
        max_distance: zone.max_distance,
        fee: zone.fee
      });
    } else {
      setEditingRadiusZone(null);
      // Sugerir próxima faixa baseada nas existentes
      const existingZones = deliveryConfig.radius_zones;
      const lastMax = existingZones.length > 0 
        ? Math.max(...existingZones.map(z => z.max_distance)) 
        : 0;
      setRadiusZoneForm({
        min_distance: lastMax,
        max_distance: lastMax + 3,
        fee: ''
      });
    }
    setIsRadiusZoneModalOpen(true);
  };

  const handleRadiusZoneSubmit = (e) => {
    e.preventDefault();
    const newZone = {
      id: editingRadiusZone?.id || `rz-${Date.now()}`,
      min_distance: parseFloat(radiusZoneForm.min_distance),
      max_distance: parseFloat(radiusZoneForm.max_distance),
      fee: parseFloat(radiusZoneForm.fee)
    };

    // Validações
    if (newZone.min_distance >= newZone.max_distance) {
      alert('A distância mínima deve ser menor que a máxima');
      return;
    }
    if (newZone.fee < 0) {
      alert('A taxa não pode ser negativa');
      return;
    }

    setDeliveryConfig(prev => {
      const updatedZones = editingRadiusZone
        ? prev.radius_zones.map(z => z.id === editingRadiusZone.id ? newZone : z)
        : [...prev.radius_zones, newZone];
      
      // Ordenar por distância mínima
      updatedZones.sort((a, b) => a.min_distance - b.min_distance);
      
      return { ...prev, radius_zones: updatedZones };
    });

    setIsRadiusZoneModalOpen(false);
  };

  const deleteRadiusZone = (zoneId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta faixa de distância?')) return;
    setDeliveryConfig(prev => ({
      ...prev,
      radius_zones: prev.radius_zones.filter(z => z.id !== zoneId)
    }));
  };

  const fetchZones = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/delivery-zones`);
      setZones(response.data);
    } catch (error) {
      console.error('Error fetching zones:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (zone = null) => {
    if (zone) {
      setEditingZone(zone);
      setFormData({
        name: zone.name,
        fee: zone.fee,
        active: zone.available !== undefined ? zone.available : zone.active
      });
    } else {
      setEditingZone(null);
      setFormData({ name: '', fee: '', active: true });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingZone) {
        await axios.put(`${API}/delivery-zones/${editingZone.id}`, formData);
      } else {
        await axios.post(`${API}/delivery-zones`, formData);
      }
      setIsModalOpen(false);
      fetchZones();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao salvar bairro');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este bairro?')) return;
    
    try {
      await axios.delete(`${API}/delivery-zones/${id}`);
      fetchZones();
    } catch (error) {
      alert('Erro ao excluir bairro');
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/delivery-zones/export?format=${format}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = format.toLowerCase();
      link.setAttribute('download', `bairros_taxas_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Erro ao exportar bairros. Verifique se você está logado.');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Selecione um arquivo CSV');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/delivery-zones/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      setImportFile(null);
      fetchZones();
      
      setTimeout(() => {
        setIsImportModalOpen(false);
        setImportResult(null);
      }, 3000);
    } catch (error) {
      alert('Erro ao importar bairros: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = (format = 'csv') => {
    if (format === 'csv') {
      const template = `bairro,taxa
Centro,5.00
Jardim América,7.50
Vila Nova,10.00
São João,8.00
Santa Cruz,12.00
Bela Vista,6.50`;

      const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_bairros.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else {
      // For XLSX, we'll trigger download from backend
      const template = `bairro,taxa
Centro,5.00
Jardim América,7.50
Vila Nova,10.00
São João,8.00
Santa Cruz,12.00
Bela Vista,6.50`;
      
      // Create a simple XLSX using client-side generation
      // We'll use the same approach as export but with template data
      alert('Para template XLSX, baixe um export existente ou use o template CSV');
      downloadTemplate('csv');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Taxas de Entrega</h1>
            <p className="text-sm text-gray-400 mt-1">
              Configure como calcular a taxa de entrega
            </p>
          </div>
        </div>

        {/* Toggle: Modo de Entrega */}
        <div className="glass-card p-4">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Settings size={18} />
            Modo de Cálculo
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => setDeliveryConfig(prev => ({ ...prev, mode: 'neighborhood' }))}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                deliveryConfig.mode === 'neighborhood' 
                  ? 'border-green-500 bg-green-500/10' 
                  : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  deliveryConfig.mode === 'neighborhood' ? 'bg-green-500' : 'bg-gray-700'
                }`}>
                  <MapPin size={20} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">Por Bairro</p>
                  <p className="text-xs text-gray-400">Taxa fixa por bairro cadastrado</p>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => setDeliveryConfig(prev => ({ ...prev, mode: 'radius' }))}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                deliveryConfig.mode === 'radius' 
                  ? 'border-emerald-500 bg-emerald-500/10' 
                  : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  deliveryConfig.mode === 'radius' ? 'bg-emerald-500' : 'bg-gray-700'
                }`}>
                  <Navigation size={20} className="text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">Por Raio (KM)</p>
                  <p className="text-xs text-gray-400">Taxa calculada pela distância</p>
                </div>
              </div>
            </button>
          </div>
          
          {/* Botão Salvar Modo */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={saveDeliveryConfig}
              disabled={savingConfig}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all disabled:opacity-50"
            >
              {savingConfig ? 'Salvando...' : 'Salvar Configuração'}
            </button>
          </div>
        </div>
      </div>

      {/* ============ MODO POR BAIRRO ============ */}
      {deliveryConfig.mode === 'neighborhood' && (
        <>
          {/* Header Bairros */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-white">Bairros Cadastrados</h2>
              <p className="text-sm text-gray-400">
                {zones.length} {zones.length === 1 ? 'bairro' : 'bairros'}
              </p>
            </div>
            <button 
              onClick={() => openModal()} 
              className="glass-button-primary w-full sm:w-auto py-3 px-6 text-base font-medium"
              data-testid="new-zone-button"
            >
              <PlusCircle size={20} className="mr-2" />
              Novo Bairro
            </button>
          </div>
        
          {/* Import/Export Buttons */}
          <div className="flex flex-wrap gap-3">
            {/* Export Dropdown */}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-colors"
                data-testid="export-zones-button"
              >
                <Download size={18} />
                <span className="text-sm font-medium">Exportar</span>
                <ChevronDown size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-gray-900 border-gray-700">
              <DropdownMenuItem 
                onClick={() => handleExport('csv')}
                className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              >
                <FileText size={16} className="mr-2" />
                Exportar como CSV
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleExport('xlsx')}
                className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              >
                <FileText size={16} className="mr-2" />
                Exportar como XLSX
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Import Button */}
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-colors"
            data-testid="import-zones-button"
          >
            <Upload size={18} />
            <span className="text-sm font-medium">Importar CSV/XLSX</span>
          </button>
          
          {/* Template Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-colors"
                data-testid="template-zones-button"
              >
                <FileText size={18} />
                <span className="text-sm font-medium">Baixar Template</span>
                <ChevronDown size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-gray-900 border-gray-700">
              <DropdownMenuItem 
                onClick={() => downloadTemplate('csv')}
                className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              >
                <FileText size={16} className="mr-2" />
                Template CSV
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => downloadTemplate('xlsx')}
                className="text-gray-300 hover:bg-gray-800 cursor-pointer"
              >
                <FileText size={16} className="mr-2" />
                Template XLSX
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>

          {/* Zones List */}
          <div className="glass-card rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Bairro</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Taxa de Entrega</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Status</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/50">
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-gray-400">
                        Carregando bairros...
                      </td>
                    </tr>
                  ) : zones.length === 0 ? (
                    <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-400">
                    Nenhum bairro cadastrado
                  </td>
                </tr>
              ) : (
                zones.map((zone) => (
                  <tr 
                    key={zone.id} 
                    className="hover:bg-gray-800/30 transition-colors"
                    data-testid={`zone-row-${zone.id}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                          <MapPin className="text-white" size={20} />
                        </div>
                        <span className="text-white font-medium">{zone.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-green-400 font-semibold text-lg">
                        R$ {zone.fee.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                        zone.active ? 'bg-green-400/20 text-green-400' : 'bg-red-400/20 text-red-400'
                      }`}>
                        {zone.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openModal(zone)}
                          className="p-2 text-gray-400 hover:text-emerald-400 hover:bg-emerald-400/20 rounded-lg transition-colors"
                          title="Editar"
                          data-testid={`edit-zone-${zone.id}`}
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(zone.id)}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-400/20 rounded-lg transition-colors"
                          title="Excluir"
                          data-testid={`delete-zone-${zone.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="glass-modal max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {editingZone ? 'Editar Bairro' : 'Novo Bairro'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Nome do Bairro *
              </label>
              <input
                type="text"
                required
                className="glass-input w-full text-base py-3 px-4"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ex: Centro"
                data-testid="zone-name-input"
              />
            </div>

            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Taxa de Entrega (R$) *
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-base">
                  R$
                </span>
                <input
                  type="number"
                  step="0.01"
                  required
                  className="glass-input w-full text-base py-3 pl-12 pr-4"
                  value={formData.fee}
                  onChange={(e) => setFormData({...formData, fee: e.target.value})}
                  placeholder="0.00"
                  data-testid="zone-fee-input"
                />
              </div>
            </div>

            <div className="bg-gray-800/30 rounded-xl p-4 flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="zone-active" className="text-base font-medium text-gray-300 block cursor-pointer">
                  Bairro Ativo
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Disponível para entregas
                </p>
              </div>
              <Switch
                id="zone-active"
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({...formData, active: checked})}
                data-testid="zone-active-switch"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-gray-800">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="w-full sm:w-auto px-6 py-3 text-base text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700 hover:border-gray-600"
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                className="w-full sm:w-auto glass-button-primary py-3 px-8 text-base font-medium"
                data-testid="zone-submit-button"
              >
                {editingZone ? '✓ Salvar' : '+ Criar Bairro'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
        <DialogContent className="glass-modal max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Importar Bairros</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-emerald-400 mb-2">📋 Formato Aceito (CSV ou XLSX):</h3>
              <ul className="text-xs text-gray-300 space-y-1">
                <li>• <strong>Colunas obrigatórias:</strong> bairro, taxa</li>
                <li>• <strong>Exemplo:</strong></li>
                <li className="ml-4">bairro,taxa</li>
                <li className="ml-4">Centro,5.00</li>
                <li className="ml-4">Vila Nova,7.50</li>
                <li className="mt-2">• <strong>Comportamento:</strong> Se o bairro já existe, apenas o preço será atualizado</li>
              </ul>
            </div>

            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Selecionar Arquivo (CSV ou XLSX)
              </label>
              <input
                type="file"
                accept=".csv,.xlsx"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-teal-500 file:text-white hover:file:bg-teal-600 cursor-pointer"
                data-testid="import-zone-file-input"
              />
              {importFile && (
                <p className="text-sm text-green-400 mt-2">
                  ✓ Arquivo: {importFile.name}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800 transition-colors"
                  >
                    <FileText size={18} />
                    <span>Baixar Template</span>
                    <ChevronDown size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-gray-900 border-gray-700">
                  <DropdownMenuItem 
                    onClick={() => downloadTemplate('csv')}
                    className="text-gray-300 hover:bg-gray-800 cursor-pointer"
                  >
                    <FileText size={16} className="mr-2" />
                    Template CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => downloadTemplate('xlsx')}
                    className="text-gray-300 hover:bg-gray-800 cursor-pointer"
                  >
                    <FileText size={16} className="mr-2" />
                    Template XLSX
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {importResult && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-green-400 mb-2">✓ Importação Concluída!</h3>
                <ul className="text-xs text-gray-300 space-y-1">
                  <li>• {importResult.created} bairros criados</li>
                  <li>• {importResult.updated} bairros atualizados</li>
                </ul>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-red-400">Erros:</p>
                    {importResult.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-300">- {err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsImportModalOpen(false);
                  setImportFile(null);
                  setImportResult(null);
                }}
                className="flex-1 px-4 py-3 text-base text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700"
                disabled={importing}
              >
                Cancelar
              </button>
              <button
                onClick={handleImport}
                disabled={!importFile || importing}
                className="flex-1 glass-button-primary py-3 px-6 text-base font-medium disabled:opacity-50"
                data-testid="import-zone-upload-button"
              >
                {importing ? 'Importando...' : 'Importar'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
        </>
      )}

      {/* ============ MODO POR RAIO KM ============ */}
      {deliveryConfig.mode === 'radius' && (
        <div className="space-y-6">
          {/* Endereço da Loja */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Store size={20} />
              Endereço da Loja (Ponto de Referência)
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Este endereço será usado como ponto central para calcular a distância até o cliente.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">CEP</label>
                <input
                  type="text"
                  value={deliveryConfig.store_address?.cep || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, cep: e.target.value }
                  }))}
                  placeholder="00000-000"
                  className="glass-input w-full"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-1">Rua/Avenida *</label>
                <input
                  type="text"
                  value={deliveryConfig.store_address?.street || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, street: e.target.value }
                  }))}
                  placeholder="Nome da rua"
                  className="glass-input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Número *</label>
                <input
                  type="text"
                  value={deliveryConfig.store_address?.number || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, number: e.target.value }
                  }))}
                  placeholder="123"
                  className="glass-input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Complemento</label>
                <input
                  type="text"
                  value={deliveryConfig.store_address?.complement || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, complement: e.target.value }
                  }))}
                  placeholder="Sala, Loja, etc"
                  className="glass-input w-full"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Bairro *</label>
                <input
                  type="text"
                  value={deliveryConfig.store_address?.neighborhood || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, neighborhood: e.target.value }
                  }))}
                  placeholder="Nome do bairro"
                  className="glass-input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Cidade *</label>
                <input
                  type="text"
                  value={deliveryConfig.store_address?.city || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, city: e.target.value }
                  }))}
                  placeholder="Nome da cidade"
                  className="glass-input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Estado *</label>
                <select
                  value={deliveryConfig.store_address?.state || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    store_address: { ...prev.store_address, state: e.target.value }
                  }))}
                  className="glass-input w-full"
                  required
                >
                  <option value="">Selecione</option>
                  <option value="AC">AC</option>
                  <option value="AL">AL</option>
                  <option value="AP">AP</option>
                  <option value="AM">AM</option>
                  <option value="BA">BA</option>
                  <option value="CE">CE</option>
                  <option value="DF">DF</option>
                  <option value="ES">ES</option>
                  <option value="GO">GO</option>
                  <option value="MA">MA</option>
                  <option value="MT">MT</option>
                  <option value="MS">MS</option>
                  <option value="MG">MG</option>
                  <option value="PA">PA</option>
                  <option value="PB">PB</option>
                  <option value="PR">PR</option>
                  <option value="PE">PE</option>
                  <option value="PI">PI</option>
                  <option value="RJ">RJ</option>
                  <option value="RN">RN</option>
                  <option value="RS">RS</option>
                  <option value="RO">RO</option>
                  <option value="RR">RR</option>
                  <option value="SC">SC</option>
                  <option value="SP">SP</option>
                  <option value="SE">SE</option>
                  <option value="TO">TO</option>
                </select>
              </div>
            </div>
            
            {/* Botão Localizar + Status */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                onClick={geocodeStoreAddress}
                disabled={geocoding || !deliveryConfig.store_address?.street || !deliveryConfig.store_address?.city}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {geocoding ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Localizando...
                  </>
                ) : (
                  <>
                    <MapPin size={16} />
                    Localizar no Mapa
                  </>
                )}
              </button>
              
              {geocodeSuccess && deliveryConfig.store_address?.latitude && (
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <CheckCircle size={16} />
                  <span>
                    Localizado: {deliveryConfig.store_address.latitude.toFixed(4)}, {deliveryConfig.store_address.longitude.toFixed(4)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Faixas de Distância */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Navigation size={20} />
                Faixas de Distância e Taxas
              </h3>
              <button
                onClick={() => openRadiusZoneModal()}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-all flex items-center gap-2"
              >
                <PlusCircle size={16} />
                Nova Faixa
              </button>
            </div>
            
            {deliveryConfig.radius_zones.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Navigation size={40} className="mx-auto mb-3 opacity-50" />
                <p>Nenhuma faixa de distância configurada</p>
                <p className="text-sm">Clique em "Nova Faixa" para adicionar</p>
              </div>
            ) : (
              <div className="space-y-3">
                {deliveryConfig.radius_zones.map((zone, index) => (
                  <div
                    key={zone.id}
                    className="flex items-center justify-between p-4 bg-gray-800/50 rounded-xl border border-gray-700"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {zone.min_distance} km até {zone.max_distance} km
                        </p>
                        <p className="text-sm text-gray-400">
                          Taxa: R$ {zone.fee.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openRadiusZoneModal(zone)}
                        className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-all"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => deleteRadiusZone(zone.id)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Raio Máximo */}
            <div className="mt-6 pt-6 border-t border-gray-700">
              <label className="block text-sm text-gray-400 mb-2">
                Raio Máximo de Entrega (km) - Opcional
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  value={deliveryConfig.max_delivery_radius || ''}
                  onChange={(e) => setDeliveryConfig(prev => ({
                    ...prev,
                    max_delivery_radius: e.target.value ? parseFloat(e.target.value) : null
                  }))}
                  placeholder="Ex: 15"
                  className="glass-input w-32"
                  min="0"
                  step="0.5"
                />
                <span className="text-sm text-gray-400">
                  Endereços além deste raio serão recusados automaticamente
                </span>
              </div>
            </div>
          </div>
          
          {/* Botão Salvar */}
          <div className="flex justify-end">
            <button
              onClick={saveDeliveryConfig}
              disabled={savingConfig}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {savingConfig ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle size={18} />
                  Salvar Configuração
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Modal: Nova/Editar Faixa de Raio */}
      <Dialog open={isRadiusZoneModalOpen} onOpenChange={setIsRadiusZoneModalOpen}>
        <DialogContent className="glass-modal max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {editingRadiusZone ? 'Editar Faixa de Distância' : 'Nova Faixa de Distância'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRadiusZoneSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">De (km) *</label>
                <input
                  type="number"
                  value={radiusZoneForm.min_distance}
                  onChange={(e) => setRadiusZoneForm(prev => ({
                    ...prev,
                    min_distance: e.target.value
                  }))}
                  placeholder="0"
                  className="glass-input w-full"
                  min="0"
                  step="0.5"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Até (km) *</label>
                <input
                  type="number"
                  value={radiusZoneForm.max_distance}
                  onChange={(e) => setRadiusZoneForm(prev => ({
                    ...prev,
                    max_distance: e.target.value
                  }))}
                  placeholder="5"
                  className="glass-input w-full"
                  min="0"
                  step="0.5"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Taxa de Entrega (R$) *</label>
              <input
                type="number"
                value={radiusZoneForm.fee}
                onChange={(e) => setRadiusZoneForm(prev => ({
                  ...prev,
                  fee: e.target.value
                }))}
                placeholder="5.00"
                className="glass-input w-full"
                min="0"
                step="0.50"
                required
              />
            </div>
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setIsRadiusZoneModalOpen(false)}
                className="flex-1 px-4 py-3 text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 glass-button-primary py-3 px-6 font-medium"
              >
                {editingRadiusZone ? 'Salvar' : 'Adicionar'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};


const CustomersContent = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Auto Messages State
  const [activeTab, setActiveTab] = useState('customers'); // 'customers', 'auto-messages', 'bulk-messages'
  const [autoMessageSettings, setAutoMessageSettings] = useState({
    enabled: false,
    inactive_days: 7,
    message_template: 'Olá {nome}! 👋\n\nSentimos sua falta! Faz {dias} dias desde o seu último pedido.\n\nQue tal pedir algo gostoso hoje? 🍔\n\nAcesse nosso cardápio: {link}',
    min_interval_seconds: 27,
    total_sent: 0,
    auto_send_enabled: false,
    auto_send_min_interval: 45,
    auto_send_max_interval: 63,
    auto_send_status: 'idle'
  });
  const [inactiveCustomers, setInactiveCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [loadingInactive, setLoadingInactive] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0 });
  const [savingSettings, setSavingSettings] = useState(false);
  const [togglingAutoSend, setTogglingAutoSend] = useState(false);
  const [autoSendStatus, setAutoSendStatus] = useState({ enabled: false, status: 'stopped', sent_today: 0 });

  // Bulk Messages State (Disparos em Massa)
  const [bulkMessageConfig, setBulkMessageConfig] = useState({
    message1: 'Olá {nome}! 👋\n\nTemos novidades incríveis para você!\n\nConfira nosso cardápio: {link}',
    message2: 'Oi {nome}! 😊\n\nQue tal experimentar algo delicioso hoje?\n\nVeja nosso menu: {link}',
    minInterval: 27,
    maxInterval: 35,
    source: 'customers' // 'customers' or 'csv'
  });
  const [csvContacts, setCsvContacts] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [selectedBulkContacts, setSelectedBulkContacts] = useState([]);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ sent: 0, failed: 0, total: 0, currentPhone: '' });
  const [bulkLogs, setBulkLogs] = useState([]);
  
  // Scheduled Messages State (Agendamentos)
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleConfig, setScheduleConfig] = useState({
    name: '',
    scheduledDate: '',
    scheduledTime: ''
  });
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [bulkSubTab, setBulkSubTab] = useState('send'); // 'send' or 'schedules'

  useEffect(() => {
    loadCustomers();
    loadAutoMessageSettings();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (err) {
      console.error('Error loading customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAutoMessageSettings = async () => {
    try {
      const response = await axios.get(`${API}/auto-messages/settings`);
      setAutoMessageSettings(response.data);
      // Carregar clientes inativos com os dias corretos das configurações
      const days = response.data.inactive_days || 7;
      const customersResponse = await axios.get(`${API}/auto-messages/inactive-customers?days=${days}`);
      setInactiveCustomers(customersResponse.data.customers || []);
    } catch (err) {
      console.error('Error loading auto message settings:', err);
    }
  };

  const loadInactiveCustomers = async () => {
    try {
      setLoadingInactive(true);
      // Usar o valor atual de autoMessageSettings
      const days = autoMessageSettings.inactive_days || 7;
      console.log(`Carregando clientes inativos há ${days} dias...`);
      const response = await axios.get(`${API}/auto-messages/inactive-customers?days=${days}`);
      console.log(`Encontrados ${response.data.total} clientes inativos`);
      setInactiveCustomers(response.data.customers || []);
      setSelectedCustomers([]);
    } catch (err) {
      console.error('Error loading inactive customers:', err);
      alert('Erro ao carregar clientes inativos');
    } finally {
      setLoadingInactive(false);
    }
  };

  const saveAutoMessageSettings = async () => {
    try {
      setSavingSettings(true);
      await axios.post(`${API}/auto-messages/settings`, autoMessageSettings);
      alert('Configurações salvas com sucesso!');
      // Atualizar lista de clientes inativos após salvar
      loadInactiveCustomers();
    } catch (err) {
      console.error('Error saving settings:', err);
      alert(err.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSavingSettings(false);
    }
  };

  // Funções de envio automático
  const loadAutoSendStatus = async () => {
    try {
      const response = await axios.get(`${API}/auto-messages/auto-send/status`);
      setAutoSendStatus(response.data);
    } catch (err) {
      console.error('Error loading auto-send status:', err);
    }
  };

  const toggleAutoSend = async () => {
    try {
      setTogglingAutoSend(true);
      const newEnabled = !autoSendStatus.enabled;
      
      const response = await axios.post(`${API}/auto-messages/auto-send/toggle`, {
        enabled: newEnabled,
        min_interval: autoMessageSettings.auto_send_min_interval || 45,
        max_interval: autoMessageSettings.auto_send_max_interval || 63
      });
      
      setAutoSendStatus({
        ...autoSendStatus,
        enabled: newEnabled,
        status: response.data.status
      });
      
      if (newEnabled) {
        alert('✅ Envio automático habilitado!\n\nAs mensagens serão enviadas automaticamente durante o horário de funcionamento.');
      } else {
        alert('🛑 Envio automático desabilitado.');
      }
    } catch (err) {
      console.error('Error toggling auto-send:', err);
      alert(err.response?.data?.detail || 'Erro ao alterar envio automático');
    } finally {
      setTogglingAutoSend(false);
    }
  };

  // Carregar status do envio automático junto com os settings
  useEffect(() => {
    loadAutoSendStatus();
    const interval = setInterval(loadAutoSendStatus, 30000); // Atualizar a cada 30s
    return () => clearInterval(interval);
  }, []);

  const handleSendMessages = async () => {
    if (selectedCustomers.length === 0) {
      alert('Selecione pelo menos um cliente');
      return;
    }

    const confirmSend = window.confirm(
      `Enviar mensagem para ${selectedCustomers.length} cliente(s)?\n\nIntervalo: ${autoMessageSettings.min_interval_seconds}s entre mensagens\nTempo estimado: ~${Math.ceil(selectedCustomers.length * autoMessageSettings.min_interval_seconds / 60)} minutos`
    );

    if (!confirmSend) return;

    try {
      setSending(true);
      setSendProgress({ sent: 0, total: selectedCustomers.length });

      const response = await axios.post(`${API}/auto-messages/send`, {
        customer_phones: selectedCustomers
      });

      setSendProgress({ sent: response.data.sent, total: selectedCustomers.length });
      
      if (response.data.sent > 0) {
        alert(`✅ ${response.data.sent} mensagem(s) enviada(s) com sucesso!${response.data.failed > 0 ? `\n❌ ${response.data.failed} falha(s)` : ''}`);
      } else {
        alert('❌ Nenhuma mensagem foi enviada. Verifique se o WhatsApp está conectado.');
      }

      // Reload inactive customers and settings
      loadInactiveCustomers();
      loadAutoMessageSettings();
      setSelectedCustomers([]);
    } catch (err) {
      console.error('Error sending messages:', err);
      alert(err.response?.data?.detail || 'Erro ao enviar mensagens');
    } finally {
      setSending(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedCustomers.length === inactiveCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(inactiveCustomers.map(c => c.phone));
    }
  };

  const toggleCustomerSelection = (phone) => {
    if (selectedCustomers.includes(phone)) {
      setSelectedCustomers(selectedCustomers.filter(p => p !== phone));
    } else {
      setSelectedCustomers([...selectedCustomers, phone]);
    }
  };

  // ===== BULK MESSAGES FUNCTIONS =====
  
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setCsvFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      // Parse CSV - expect columns: phone, name (optional)
      const contacts = [];
      const header = lines[0].toLowerCase();
      const hasHeader = header.includes('telefone') || header.includes('phone') || header.includes('nome') || header.includes('name');
      
      const startIndex = hasHeader ? 1 : 0;
      
      for (let i = startIndex; i < lines.length; i++) {
        const parts = lines[i].split(/[,;]/).map(p => p.trim().replace(/"/g, ''));
        if (parts.length > 0 && parts[0]) {
          // Clean phone number
          let phone = parts[0].replace(/\D/g, '');
          if (phone.length >= 10) {
            contacts.push({
              phone: phone,
              name: parts[1] || 'Cliente'
            });
          }
        }
      }
      
      setCsvContacts(contacts);
      setSelectedBulkContacts(contacts.map(c => c.phone));
      alert(`✅ ${contacts.length} contato(s) carregado(s) do CSV`);
    };
    reader.readAsText(file);
  };

  const getRandomInterval = () => {
    const min = bulkMessageConfig.minInterval;
    const max = bulkMessageConfig.maxInterval;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const getRandomMessage = (contact) => {
    // Randomly choose message 1 or 2
    const template = Math.random() > 0.5 ? bulkMessageConfig.message1 : bulkMessageConfig.message2;
    
    // Get restaurant info from localStorage or use defaults
    const restaurant = JSON.parse(localStorage.getItem('restaurant') || '{}');
    const menuLink = `https://${restaurant.subdomain || 'loja'}.meucardapiozap.shop`;
    
    // Replace placeholders
    return template
      .replace(/{nome}/g, contact.name || 'Cliente')
      .replace(/{link}/g, menuLink)
      .replace(/{loja}/g, restaurant.name || 'Nossa Loja');
  };

  const handleBulkSend = async () => {
    const contacts = bulkMessageConfig.source === 'csv' 
      ? csvContacts.filter(c => selectedBulkContacts.includes(c.phone))
      : customers.filter(c => selectedBulkContacts.includes(c.phone));
    
    if (contacts.length === 0) {
      alert('Selecione pelo menos um contato');
      return;
    }

    if (!bulkMessageConfig.message1.trim() || !bulkMessageConfig.message2.trim()) {
      alert('Preencha as duas mensagens');
      return;
    }

    const estimatedTime = Math.ceil(contacts.length * ((bulkMessageConfig.minInterval + bulkMessageConfig.maxInterval) / 2) / 60);
    const confirmSend = window.confirm(
      `📤 Enviar mensagens para ${contacts.length} contato(s)?\n\n` +
      `⏱️ Intervalo: ${bulkMessageConfig.minInterval}s - ${bulkMessageConfig.maxInterval}s\n` +
      `📊 Tempo estimado: ~${estimatedTime} minutos\n\n` +
      `As mensagens serão enviadas em rodízio para evitar padrões.`
    );

    if (!confirmSend) return;

    setBulkSending(true);
    setBulkProgress({ sent: 0, failed: 0, total: contacts.length, currentPhone: '' });
    setBulkLogs([]);

    try {
      const response = await axios.post(`${API}/bulk-messages/send`, {
        contacts: contacts.map(c => ({ phone: c.phone, name: c.name || 'Cliente' })),
        message1: bulkMessageConfig.message1,
        message2: bulkMessageConfig.message2,
        minInterval: bulkMessageConfig.minInterval,
        maxInterval: bulkMessageConfig.maxInterval
      });

      setBulkProgress({
        sent: response.data.sent,
        failed: response.data.failed,
        total: contacts.length,
        currentPhone: 'Concluído'
      });

      if (response.data.sent > 0) {
        alert(`✅ Envio concluído!\n\n📤 Enviadas: ${response.data.sent}\n❌ Falhas: ${response.data.failed}`);
      } else {
        alert('❌ Nenhuma mensagem foi enviada. Verifique se o WhatsApp está conectado.');
      }

      setSelectedBulkContacts([]);
    } catch (err) {
      console.error('Error sending bulk messages:', err);
      alert(err.response?.data?.detail || 'Erro ao enviar mensagens');
    } finally {
      setBulkSending(false);
    }
  };

  // ====== SCHEDULED MESSAGES FUNCTIONS ======
  const loadScheduledMessages = async () => {
    try {
      setLoadingSchedules(true);
      const response = await axios.get(`${API}/bulk-messages/schedules`);
      setScheduledMessages(response.data || []);
    } catch (err) {
      console.error('Error loading scheduled messages:', err);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const handleScheduleBulkSend = async () => {
    const contacts = bulkMessageConfig.source === 'csv' 
      ? csvContacts.filter(c => selectedBulkContacts.includes(c.phone))
      : customers.filter(c => selectedBulkContacts.includes(c.phone));
    
    if (contacts.length === 0) {
      alert('Selecione pelo menos um contato');
      return;
    }

    if (!bulkMessageConfig.message1.trim() || !bulkMessageConfig.message2.trim()) {
      alert('Preencha as duas mensagens');
      return;
    }

    if (!scheduleConfig.scheduledDate || !scheduleConfig.scheduledTime) {
      alert('Selecione a data e hora do agendamento');
      return;
    }

    // Combine date and time
    const scheduledDatetime = `${scheduleConfig.scheduledDate}T${scheduleConfig.scheduledTime}:00`;
    
    try {
      setSavingSchedule(true);
      const response = await axios.post(`${API}/bulk-messages/schedule`, {
        contacts: contacts.map(c => ({ phone: c.phone, name: c.name || 'Cliente' })),
        message1: bulkMessageConfig.message1,
        message2: bulkMessageConfig.message2,
        minInterval: bulkMessageConfig.minInterval,
        maxInterval: bulkMessageConfig.maxInterval,
        scheduled_datetime: scheduledDatetime,
        name: scheduleConfig.name || `Disparo de ${new Date().toLocaleDateString('pt-BR')}`
      });

      alert(`✅ ${response.data.message}\n\n📊 ${contacts.length} contato(s) agendados`);
      
      setShowScheduleModal(false);
      setScheduleConfig({ name: '', scheduledDate: '', scheduledTime: '' });
      setSelectedBulkContacts([]);
      loadScheduledMessages();
    } catch (err) {
      console.error('Error scheduling bulk messages:', err);
      alert(err.response?.data?.detail || 'Erro ao agendar mensagens');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleCancelSchedule = async (scheduleId) => {
    if (!window.confirm('Deseja realmente cancelar este agendamento?')) return;
    
    try {
      await axios.delete(`${API}/bulk-messages/schedules/${scheduleId}`);
      alert('Agendamento cancelado com sucesso!');
      loadScheduledMessages();
    } catch (err) {
      console.error('Error cancelling schedule:', err);
      alert(err.response?.data?.detail || 'Erro ao cancelar agendamento');
    }
  };

  // Load schedules when bulk-messages tab is active
  useEffect(() => {
    if (activeTab === 'bulk-messages') {
      loadScheduledMessages();
    }
  }, [activeTab]);

  const toggleBulkSelectAll = () => {
    const contacts = bulkMessageConfig.source === 'csv' ? csvContacts : customers;
    if (selectedBulkContacts.length === contacts.length) {
      setSelectedBulkContacts([]);
    } else {
      setSelectedBulkContacts(contacts.map(c => c.phone));
    }
  };

  const toggleBulkContactSelection = (phone) => {
    if (selectedBulkContacts.includes(phone)) {
      setSelectedBulkContacts(selectedBulkContacts.filter(p => p !== phone));
    } else {
      setSelectedBulkContacts([...selectedBulkContacts, phone]);
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customers/export?format=${format}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = format.toLowerCase();
      link.setAttribute('download', `clientes_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting customers:', error);
      alert('Erro ao exportar clientes. Verifique se você está logado.');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Selecione um arquivo CSV ou XLSX');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/customers/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      setImportFile(null);
      
      // Reload customers
      loadCustomers();
      
      setTimeout(() => {
        setIsImportModalOpen(false);
        setImportResult(null);
      }, 3000);
    } catch (error) {
      console.error('Error importing customers:', error);
      alert('Erro ao importar clientes: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = `telefone,nome,total_pedidos,total_gasto,primeiro_pedido,ultimo_pedido
11999887766,João Silva,5,150.00,2024-01-15,2024-10-18
11888776655,Maria Santos,3,89.50,2024-02-20,2024-09-25
11777665544,Pedro Costa,8,245.90,2024-01-10,2024-10-19

INSTRUÇÕES DE PREENCHIMENTO:
=============================
1. NÃO remova a primeira linha (cabeçalho)
2. Telefone: número com DDD (obrigatório)
3. Nome: nome completo do cliente (obrigatório)
4. Total_pedidos: número de pedidos realizados (opcional)
5. Total_gasto: valor total gasto pelo cliente (opcional)
6. Primeiro_pedido: data do primeiro pedido no formato AAAA-MM-DD (opcional)
7. Ultimo_pedido: data do último pedido no formato AAAA-MM-DD (opcional)

NOTA: A importação irá atualizar os nomes dos clientes existentes (por telefone)
`;
    
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template_clientes.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const filteredCustomers = customers.filter(customer => 
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.includes(searchTerm)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Clientes</h2>
          <p className="text-gray-400">Gerencie sua base de clientes</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-white">{customers.length}</div>
          <div className="text-sm text-gray-400">Total de Clientes</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 sm:gap-2 border-b border-gray-700 pb-2 overflow-x-auto scrollbar-hide">
        <button
          onClick={() => setActiveTab('customers')}
          className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-t-lg font-medium transition-colors whitespace-nowrap text-xs sm:text-sm ${
            activeTab === 'customers'
              ? 'bg-gray-800 text-white border-b-2 border-emerald-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <User size={16} className="flex-shrink-0" />
          <span className="hidden sm:inline">Lista de Clientes</span>
          <span className="sm:hidden">Clientes</span>
        </button>
        <button
          onClick={() => {
            setActiveTab('auto-messages');
            // Sempre carregar a lista ao abrir a aba para garantir dados atualizados
            loadInactiveCustomers();
          }}
          className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-t-lg font-medium transition-colors whitespace-nowrap text-xs sm:text-sm ${
            activeTab === 'auto-messages'
              ? 'bg-gray-800 text-white border-b-2 border-green-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Clock size={16} className="flex-shrink-0" />
          <span className="hidden sm:inline">Clientes Inativos</span>
          <span className="sm:hidden">Inativos</span>
        </button>
        <button
          onClick={() => setActiveTab('bulk-messages')}
          className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-t-lg font-medium transition-colors whitespace-nowrap text-xs sm:text-sm ${
            activeTab === 'bulk-messages'
              ? 'bg-gray-800 text-white border-b-2 border-purple-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Send size={16} className="flex-shrink-0" />
          <span className="hidden sm:inline">Disparos em Massa</span>
          <span className="sm:hidden">Disparos</span>
        </button>
      </div>

      {activeTab === 'customers' ? (
        <>
          {/* Import/Export Menu - Optimized */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-2 px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-400 hover:text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-colors"
            data-testid="import-export-customers-menu-button"
            title="Importar/Exportar"
          >
            <MoreVertical size={18} />
            <span className="text-sm font-medium">Opções</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-gray-900 border-gray-700 w-56">
          <DropdownMenuItem 
            onClick={() => handleExport('csv')}
            className="text-gray-300 hover:bg-gray-800 cursor-pointer"
            data-testid="export-customers-csv-button"
          >
            <Download size={16} className="mr-2" />
            Exportar como CSV
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => handleExport('xlsx')}
            className="text-gray-300 hover:bg-gray-800 cursor-pointer"
            data-testid="export-customers-xlsx-button"
          >
            <Download size={16} className="mr-2" />
            Exportar como XLSX
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => setIsImportModalOpen(true)}
            className="text-gray-300 hover:bg-gray-800 cursor-pointer"
            data-testid="import-customers-button"
          >
            <Upload size={16} className="mr-2" />
            Importar CSV/XLSX
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={downloadTemplate}
            className="text-gray-300 hover:bg-gray-800 cursor-pointer"
            data-testid="template-customers-button"
          >
            <FileText size={16} className="mr-2" />
            Baixar Template
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Search Bar */}
      <div className="glassmorphism-card rounded-2xl p-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por nome ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
            data-testid="customer-search-input"
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="glassmorphism-card rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
              <User className="text-white" size={24} />
            </div>
            <div>
              <div className="text-sm text-gray-400">Total de Clientes</div>
              <div className="text-2xl font-bold text-white">{customers.length}</div>
            </div>
          </div>
        </div>

        <div className="glassmorphism-card rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
              <ShoppingCart className="text-white" size={24} />
            </div>
            <div>
              <div className="text-sm text-gray-400">Total de Pedidos</div>
              <div className="text-2xl font-bold text-white">
                {customers.reduce((sum, c) => sum + c.total_orders, 0)}
              </div>
            </div>
          </div>
        </div>

        <div className="glassmorphism-card rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
              <DollarSign className="text-white" size={24} />
            </div>
            <div>
              <div className="text-sm text-gray-400">Receita Total</div>
              <div className="text-2xl font-bold text-white">
                R$ {customers.reduce((sum, c) => sum + c.total_spent, 0).toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="glassmorphism-card rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Cliente</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Telefone</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Pedidos</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Total Gasto</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Último Pedido</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Dias sem Comprar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                    Carregando clientes...
                  </td>
                </tr>
              ) : filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                    {searchTerm ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado ainda'}
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer, index) => (
                  <tr 
                    key={index} 
                    className="hover:bg-gray-800/30 transition-colors"
                    data-testid={`customer-row-${index}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                          <span className="text-white font-semibold">
                            {customer.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="text-white font-medium">{customer.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-300">{customer.phone}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-500/20 text-emerald-400">
                        {customer.total_orders}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-green-400 font-semibold">
                        R$ {customer.total_spent.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-400 text-sm">
                      {formatDate(customer.last_order)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        customer.days_inactive >= 30 
                          ? 'bg-red-500/20 text-red-400' 
                          : customer.days_inactive >= 7 
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-green-500/20 text-green-400'
                      }`}>
                        {customer.days_inactive || 0} dias
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Import Modal */}
      <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
        <DialogContent className="glass-modal max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Importar Clientes</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Instructions */}
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-emerald-400 mb-2">📋 Instruções:</h3>
              <ul className="text-xs text-gray-300 space-y-1">
                <li>• Baixe o template CSV para ver o formato</li>
                <li>• Preencha com os dados dos clientes</li>
                <li>• Faça upload do arquivo CSV ou XLSX preenchido</li>
                <li>• A importação irá atualizar nomes de clientes existentes</li>
              </ul>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-base font-medium text-gray-300 mb-2">
                Selecionar Arquivo CSV ou XLSX
              </label>
              <input
                type="file"
                accept=".csv,.xlsx"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-500 file:text-white hover:file:bg-emerald-600 cursor-pointer"
                data-testid="import-customers-file-input"
              />
              {importFile && (
                <p className="text-sm text-green-400 mt-2">
                  ✓ Arquivo selecionado: {importFile.name}
                </p>
              )}
            </div>

            {/* Template Button */}
            <button
              onClick={downloadTemplate}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800 hover:border-gray-600 transition-colors"
            >
              <FileText size={18} />
              <span>Baixar Template de Exemplo</span>
            </button>

            {/* Import Result */}
            {importResult && (
              <div className="space-y-3">
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-green-400 mb-2">✓ Importação Concluída!</h3>
                  <ul className="text-xs text-gray-300 space-y-1">
                    <li>• {importResult.created} novos clientes</li>
                    <li>• {importResult.updated} clientes atualizados</li>
                  </ul>
                </div>
                
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                    <p className="text-xs font-semibold text-red-400 mb-2">❌ Erros ({importResult.errors.length}):</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {importResult.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-300">• {err}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsImportModalOpen(false);
                  setImportFile(null);
                  setImportResult(null);
                }}
                className="flex-1 px-4 py-3 text-base text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700 hover:border-gray-600"
                disabled={importing}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={!importFile || importing}
                className="flex-1 px-4 py-3 text-base bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                data-testid="confirm-import-customers-button"
              >
                {importing ? 'Importando...' : 'Importar'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
        </>
      ) : activeTab === 'auto-messages' ? (
        /* Auto Messages Tab */
        <div className="space-y-6">
          {/* Settings Card */}
          <div className="glassmorphism-card rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Settings size={24} />
              Configurações de Disparo
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Days of Inactivity */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Dias sem comprar
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={autoMessageSettings.inactive_days}
                    onChange={(e) => setAutoMessageSettings({
                      ...autoMessageSettings,
                      inactive_days: parseInt(e.target.value) || 7
                    })}
                    className="flex-1 px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white"
                    data-testid="inactive-days-input"
                  />
                  <button
                    onClick={loadInactiveCustomers}
                    disabled={loadingInactive}
                    className="px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 transition-all font-medium whitespace-nowrap"
                    data-testid="search-inactive-button"
                  >
                    {loadingInactive ? 'Buscando...' : '🔍 Buscar'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Clientes que não compram há X dias receberão a mensagem
                </p>
              </div>

              {/* Min Interval */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Intervalo entre mensagens (segundos)
                </label>
                <input
                  type="number"
                  min="27"
                  max="120"
                  value={autoMessageSettings.min_interval_seconds}
                  onChange={(e) => setAutoMessageSettings({
                    ...autoMessageSettings,
                    min_interval_seconds: Math.max(27, parseInt(e.target.value) || 27)
                  })}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Mínimo: 27 segundos (segurança do WhatsApp)
                </p>
              </div>
            </div>

            {/* Message Template */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Mensagem Personalizada
              </label>
              <textarea
                value={autoMessageSettings.message_template}
                onChange={(e) => setAutoMessageSettings({
                  ...autoMessageSettings,
                  message_template: e.target.value
                })}
                rows={5}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white resize-none"
                placeholder="Digite sua mensagem..."
                data-testid="message-template-input"
              />
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">{'{nome}'} = Nome do cliente</span>
                <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">{'{dias}'} = Dias sem comprar</span>
                <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">{'{link}'} = Link do cardápio</span>
                <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">{'{loja}'} = Nome da loja</span>
              </div>
            </div>

            {/* ========== ENVIO AUTOMÁTICO ========== */}
            <div className="mt-8 p-5 bg-gradient-to-br from-emerald-900/30 to-teal-900/30 border border-emerald-500/30 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-emerald-400 flex items-center gap-2">
                    🤖 Envio Automático
                    {autoSendStatus.status === 'running' && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full animate-pulse">
                        Ativo
                      </span>
                    )}
                  </h4>
                  <p className="text-sm text-gray-400 mt-1">
                    Envia mensagens automaticamente durante o horário de funcionamento
                  </p>
                </div>
                <button
                  onClick={toggleAutoSend}
                  disabled={togglingAutoSend}
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                    autoSendStatus.enabled ? 'bg-emerald-500' : 'bg-gray-600'
                  }`}
                  data-testid="auto-send-toggle"
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      autoSendStatus.enabled ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {autoSendStatus.enabled && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                  <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-400">{autoSendStatus.sent_today || 0}</p>
                    <p className="text-xs text-gray-400">Enviadas hoje</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-400">{autoMessageSettings.auto_send_min_interval || 45}s</p>
                    <p className="text-xs text-gray-400">Intervalo mín.</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-400">{autoMessageSettings.auto_send_max_interval || 63}s</p>
                    <p className="text-xs text-gray-400">Intervalo máx.</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                    <p className={`text-lg font-bold ${autoSendStatus.status === 'running' ? 'text-green-400' : 'text-gray-400'}`}>
                      {autoSendStatus.status === 'running' ? '🟢 Rodando' : '⏸️ Parado'}
                    </p>
                    <p className="text-xs text-gray-400">Status</p>
                  </div>
                </div>
              )}
              
              <div className="mt-4 p-3 bg-gray-800/30 rounded-xl">
                <p className="text-xs text-gray-400">
                  <strong className="text-emerald-400">Como funciona:</strong> O sistema enviará mensagens automaticamente para clientes inativos 
                  durante o horário de funcionamento da loja, respeitando um intervalo aleatório de {autoMessageSettings.auto_send_min_interval || 45}-{autoMessageSettings.auto_send_max_interval || 63} segundos 
                  entre cada mensagem para evitar bloqueios do WhatsApp.
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-400">
                Total de mensagens enviadas: <span className="text-green-400 font-bold">{autoMessageSettings.total_sent || 0}</span>
              </div>
              <button
                onClick={saveAutoMessageSettings}
                disabled={savingSettings}
                className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-indigo-500 text-white rounded-xl hover:from-emerald-600 hover:to-indigo-600 disabled:opacity-50 transition-all font-medium"
              >
                {savingSettings ? 'Salvando...' : 'Salvar Configurações'}
              </button>
            </div>
          </div>

          {/* Inactive Customers */}
          <div className="glassmorphism-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Clock size={24} />
                Clientes Inativos ({inactiveCustomers.length})
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={loadInactiveCustomers}
                  disabled={loadingInactive}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 transition-colors"
                >
                  {loadingInactive ? 'Buscando...' : 'Atualizar Lista'}
                </button>
                {selectedCustomers.length > 0 && (
                  <button
                    onClick={handleSendMessages}
                    disabled={sending}
                    className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 transition-all font-medium flex items-center gap-2"
                    data-testid="send-messages-button"
                  >
                    <Send size={18} />
                    {sending ? `Enviando... (${sendProgress.sent}/${sendProgress.total})` : `Enviar para ${selectedCustomers.length} cliente(s)`}
                  </button>
                )}
              </div>
            </div>

            {/* Select All */}
            {inactiveCustomers.length > 0 && (
              <div className="mb-4 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedCustomers.length === inactiveCustomers.length && inactiveCustomers.length > 0}
                  onChange={toggleSelectAll}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-emerald-500"
                />
                <span className="text-gray-300">Selecionar todos</span>
              </div>
            )}

            {/* Customer List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {loadingInactive ? (
                <div className="text-center py-8 text-gray-400">Buscando clientes inativos...</div>
              ) : inactiveCustomers.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Clock size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Nenhum cliente inativo há {autoMessageSettings.inactive_days} dias</p>
                  <p className="text-sm mt-2">Seus clientes estão ativos! 🎉</p>
                </div>
              ) : (
                inactiveCustomers.map((customer, index) => (
                  <div
                    key={index}
                    onClick={() => toggleCustomerSelection(customer.phone)}
                    className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-colors ${
                      selectedCustomers.includes(customer.phone)
                        ? 'bg-emerald-500/20 border border-emerald-500/50'
                        : 'bg-gray-800/50 hover:bg-gray-800'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCustomers.includes(customer.phone)}
                      onChange={() => {}}
                      className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-emerald-500"
                    />
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {(customer.name || 'C').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium">{customer.name || 'Cliente'}</div>
                      <div className="text-sm text-gray-400">{customer.phone}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-orange-400 font-bold">{customer.days_inactive} dias</div>
                      <div className="text-xs text-gray-500">sem comprar</div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-300">{customer.total_orders || 0} pedidos</div>
                      <div className="text-xs text-green-400">R$ {(customer.total_spent || 0).toFixed(2)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : activeTab === 'bulk-messages' && (
        /* Bulk Messages Tab - Disparos em Massa */
        <div className="space-y-6">
          {/* Sub-tabs: Enviar Agora vs Agendamentos */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setBulkSubTab('send')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                bulkSubTab === 'send'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              <Send size={18} />
              Enviar Agora
            </button>
            <button
              onClick={() => setBulkSubTab('schedules')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                bulkSubTab === 'schedules'
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              <Calendar size={18} />
              Agendamentos
              {scheduledMessages.filter(s => s.status === 'pending').length > 0 && (
                <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {scheduledMessages.filter(s => s.status === 'pending').length}
                </span>
              )}
            </button>
          </div>

          {bulkSubTab === 'schedules' ? (
            /* Schedules List */
            <div className="glassmorphism-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Calendar size={24} />
                  Disparos Agendados
                </h3>
                <button
                  onClick={loadScheduledMessages}
                  disabled={loadingSchedules}
                  className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <RefreshCcw size={18} className={`text-gray-400 ${loadingSchedules ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {loadingSchedules ? (
                <div className="text-center py-8 text-gray-400">
                  <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4" />
                  Carregando agendamentos...
                </div>
              ) : scheduledMessages.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Calendar size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Nenhum disparo agendado</p>
                  <p className="text-sm mt-2">Use a aba "Enviar Agora" e clique em "Agendar" para criar um</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {scheduledMessages.map((schedule) => {
                    const scheduledDate = new Date(schedule.scheduled_datetime);
                    const now = new Date();
                    const isPast = scheduledDate < now;
                    
                    return (
                      <div
                        key={schedule.id}
                        className={`p-4 rounded-xl border ${
                          schedule.status === 'pending' ? 'border-orange-500/50 bg-orange-500/10' :
                          schedule.status === 'running' ? 'border-emerald-500/50 bg-emerald-500/10' :
                          schedule.status === 'completed' ? 'border-green-500/50 bg-green-500/10' :
                          schedule.status === 'cancelled' ? 'border-gray-500/50 bg-gray-500/10' :
                          'border-red-500/50 bg-red-500/10'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-white">{schedule.name}</h4>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                schedule.status === 'pending' ? 'bg-orange-500/30 text-orange-300' :
                                schedule.status === 'running' ? 'bg-emerald-500/30 text-emerald-300' :
                                schedule.status === 'completed' ? 'bg-green-500/30 text-green-300' :
                                schedule.status === 'cancelled' ? 'bg-gray-500/30 text-gray-300' :
                                'bg-red-500/30 text-red-300'
                              }`}>
                                {schedule.status === 'pending' ? '⏳ Pendente' :
                                 schedule.status === 'running' ? '🚀 Enviando...' :
                                 schedule.status === 'completed' ? '✅ Concluído' :
                                 schedule.status === 'cancelled' ? '❌ Cancelado' :
                                 '⚠️ Falhou'}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-400">📅 Agendado para:</span>
                                <p className="text-white">
                                  {scheduledDate.toLocaleDateString('pt-BR')} às {scheduledDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-400">👥 Contatos:</span>
                                <p className="text-white">{schedule.contacts?.length || 0}</p>
                              </div>
                            </div>
                            
                            {schedule.status === 'completed' && (
                              <div className="mt-2 text-sm">
                                <span className="text-green-400">✅ {schedule.sent_count} enviadas</span>
                                {schedule.failed_count > 0 && (
                                  <span className="text-red-400 ml-3">❌ {schedule.failed_count} falhas</span>
                                )}
                              </div>
                            )}
                            
                            {schedule.status === 'failed' && schedule.error_message && (
                              <div className="mt-2 text-sm text-red-400">
                                Erro: {schedule.error_message}
                              </div>
                            )}
                          </div>
                          
                          {schedule.status === 'pending' && (
                            <button
                              onClick={() => handleCancelSchedule(schedule.id)}
                              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                              title="Cancelar agendamento"
                            >
                              <X size={20} />
                            </button>
                          )}
                          
                          {(schedule.status === 'completed' || schedule.status === 'failed' || schedule.status === 'cancelled') && (
                            <button
                              onClick={() => handleCancelSchedule(schedule.id)}
                              className="p-2 text-gray-400 hover:bg-gray-500/20 rounded-lg transition-colors"
                              title="Remover histórico"
                            >
                              <Trash2 size={20} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
          <>
          {/* Source Selection */}
          <div className="glassmorphism-card rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Users size={24} />
              Origem dos Contatos
            </h3>
            
            <div className="flex gap-4 mb-6">
              <button
                onClick={() => {
                  setBulkMessageConfig({ ...bulkMessageConfig, source: 'customers' });
                  setSelectedBulkContacts(customers.map(c => c.phone));
                }}
                className={`flex-1 p-4 rounded-xl border-2 transition-colors ${
                  bulkMessageConfig.source === 'customers'
                    ? 'border-emerald-500 bg-emerald-500/20 text-white'
                    : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                }`}
              >
                <User size={32} className="mx-auto mb-2" />
                <div className="font-medium">Meus Clientes</div>
                <div className="text-sm opacity-70">{customers.length} contatos</div>
              </button>
              
              <button
                onClick={() => setBulkMessageConfig({ ...bulkMessageConfig, source: 'csv' })}
                className={`flex-1 p-4 rounded-xl border-2 transition-colors ${
                  bulkMessageConfig.source === 'csv'
                    ? 'border-purple-500 bg-purple-500/20 text-white'
                    : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                }`}
              >
                <FileSpreadsheet size={32} className="mx-auto mb-2" />
                <div className="font-medium">Lista Externa (CSV)</div>
                <div className="text-sm opacity-70">{csvContacts.length} contatos</div>
              </button>
            </div>

            {/* CSV Upload */}
            {bulkMessageConfig.source === 'csv' && (
              <div className="border-2 border-dashed border-gray-700 rounded-xl p-6 text-center">
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleCsvUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload size={40} className="mx-auto mb-3 text-gray-400" />
                  <p className="text-white font-medium mb-1">
                    {csvFile ? csvFile.name : 'Clique para enviar arquivo CSV'}
                  </p>
                  <p className="text-sm text-gray-500">
                    Formato: telefone, nome (uma linha por contato)
                  </p>
                </label>
              </div>
            )}
          </div>

          {/* Messages Configuration */}
          <div className="glassmorphism-card rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <MessageSquare size={24} />
              Mensagens em Rodízio
              <span className="text-xs bg-purple-500/30 text-purple-300 px-2 py-1 rounded-full ml-2">
                Anti-padrão
              </span>
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Configure duas mensagens diferentes que serão enviadas alternadamente para evitar detecção de spam.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Message 1 */}
              <div>
                <label className="block text-sm font-medium text-green-400 mb-2">
                  📗 Mensagem 1
                </label>
                <textarea
                  value={bulkMessageConfig.message1}
                  onChange={(e) => setBulkMessageConfig({ ...bulkMessageConfig, message1: e.target.value })}
                  rows={5}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white resize-none"
                  placeholder="Digite a primeira mensagem..."
                />
              </div>

              {/* Message 2 */}
              <div>
                <label className="block text-sm font-medium text-emerald-400 mb-2">
                  📘 Mensagem 2
                </label>
                <textarea
                  value={bulkMessageConfig.message2}
                  onChange={(e) => setBulkMessageConfig({ ...bulkMessageConfig, message2: e.target.value })}
                  rows={5}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white resize-none"
                  placeholder="Digite a segunda mensagem..."
                />
              </div>
            </div>

            {/* Variables */}
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">{'{nome}'} = Nome</span>
              <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">{'{link}'} = Cardápio</span>
              <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">{'{loja}'} = Loja</span>
            </div>
          </div>

          {/* Timing Configuration */}
          <div className="glassmorphism-card rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Clock size={24} />
              Intervalo de Tempo
              <span className="text-xs bg-orange-500/30 text-orange-300 px-2 py-1 rounded-full ml-2">
                Variável
              </span>
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              O tempo entre mensagens irá variar aleatoriamente dentro do intervalo definido para evitar padrões.
            </p>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  ⏱️ Mínimo (segundos)
                </label>
                <input
                  type="number"
                  min="27"
                  value={bulkMessageConfig.minInterval}
                  onChange={(e) => setBulkMessageConfig({
                    ...bulkMessageConfig,
                    minInterval: Math.max(27, parseInt(e.target.value) || 27)
                  })}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Mínimo: 27s (segurança)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  ⏱️ Máximo (segundos)
                </label>
                <input
                  type="number"
                  min={bulkMessageConfig.minInterval}
                  value={bulkMessageConfig.maxInterval}
                  onChange={(e) => setBulkMessageConfig({
                    ...bulkMessageConfig,
                    maxInterval: Math.max(bulkMessageConfig.minInterval, parseInt(e.target.value) || 35)
                  })}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Recomendado: 30-45s</p>
              </div>
            </div>

            {/* Visual indicator */}
            <div className="mt-4 p-4 bg-gray-800/50 rounded-xl">
              <p className="text-sm text-gray-400">
                📊 Exemplo de envio: {bulkMessageConfig.minInterval}s → {Math.floor((bulkMessageConfig.minInterval + bulkMessageConfig.maxInterval) / 2)}s → {bulkMessageConfig.maxInterval}s → {bulkMessageConfig.minInterval + 2}s ...
              </p>
            </div>
          </div>

          {/* Contact Selection */}
          <div className="glassmorphism-card rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Users size={24} />
                Contatos Selecionados ({selectedBulkContacts.length})
              </h3>
              
              {selectedBulkContacts.length > 0 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowScheduleModal(true)}
                    disabled={bulkSending}
                    className="px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 transition-all font-medium flex items-center gap-2"
                  >
                    <Calendar size={18} />
                    Agendar
                  </button>
                  <button
                    onClick={handleBulkSend}
                    disabled={bulkSending}
                    className="px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 transition-all font-medium flex items-center gap-2"
                  >
                    <Send size={18} />
                    {bulkSending 
                      ? `Enviando... (${bulkProgress.sent}/${bulkProgress.total})`
                      : `Enviar Agora`
                    }
                  </button>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {bulkSending && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-400 mb-1">
                  <span>Progresso: {bulkProgress.sent + bulkProgress.failed}/{bulkProgress.total}</span>
                  <span>✅ {bulkProgress.sent} | ❌ {bulkProgress.failed}</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all"
                    style={{ width: `${((bulkProgress.sent + bulkProgress.failed) / bulkProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Select All */}
            <div className="mb-4 flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedBulkContacts.length === (bulkMessageConfig.source === 'csv' ? csvContacts : customers).length && (bulkMessageConfig.source === 'csv' ? csvContacts : customers).length > 0}
                onChange={toggleBulkSelectAll}
                className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-purple-500"
              />
              <span className="text-gray-300">Selecionar todos</span>
            </div>

            {/* Contact List */}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {(bulkMessageConfig.source === 'csv' ? csvContacts : customers).length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>{bulkMessageConfig.source === 'csv' ? 'Nenhum contato no CSV' : 'Nenhum cliente cadastrado'}</p>
                </div>
              ) : (
                (bulkMessageConfig.source === 'csv' ? csvContacts : customers).map((contact, index) => (
                  <div
                    key={index}
                    onClick={() => toggleBulkContactSelection(contact.phone)}
                    className={`flex items-center gap-4 p-3 rounded-xl cursor-pointer transition-colors ${
                      selectedBulkContacts.includes(contact.phone)
                        ? 'bg-purple-500/20 border border-purple-500/50'
                        : 'bg-gray-800/50 hover:bg-gray-800'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedBulkContacts.includes(contact.phone)}
                      onChange={() => {}}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-purple-500"
                    />
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">
                        {(contact.name || 'C').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium text-sm">{contact.name || 'Cliente'}</div>
                      <div className="text-xs text-gray-400">{contact.phone}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          </>
          )}

          {/* Schedule Modal */}
          <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
            <DialogContent className="bg-gray-900 border border-gray-800 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-400" />
                  Agendar Disparo
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nome do agendamento (opcional)
                  </label>
                  <input
                    type="text"
                    value={scheduleConfig.name}
                    onChange={(e) => setScheduleConfig({ ...scheduleConfig, name: e.target.value })}
                    placeholder="Ex: Promoção de Fim de Semana"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      📅 Data
                    </label>
                    <input
                      type="date"
                      value={scheduleConfig.scheduledDate}
                      onChange={(e) => setScheduleConfig({ ...scheduleConfig, scheduledDate: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      ⏰ Hora
                    </label>
                    <input
                      type="time"
                      value={scheduleConfig.scheduledTime}
                      onChange={(e) => setScheduleConfig({ ...scheduleConfig, scheduledTime: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white"
                    />
                  </div>
                </div>

                <div className="bg-gray-800/50 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Resumo</h4>
                  <div className="space-y-1 text-sm">
                    <p className="text-white">👥 {selectedBulkContacts.length} contato(s) selecionado(s)</p>
                    <p className="text-white">📝 2 mensagens em rodízio</p>
                    <p className="text-white">⏱️ Intervalo: {bulkMessageConfig.minInterval}s - {bulkMessageConfig.maxInterval}s</p>
                    {scheduleConfig.scheduledDate && scheduleConfig.scheduledTime && (
                      <p className="text-orange-400 font-medium mt-2">
                        📅 Será enviado em: {new Date(`${scheduleConfig.scheduledDate}T${scheduleConfig.scheduledTime}`).toLocaleString('pt-BR')}
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-sm text-yellow-300">
                  ⚠️ O WhatsApp deve estar conectado no momento do envio para que as mensagens sejam disparadas.
                </div>
              </div>

              <DialogFooter className="gap-2">
                <button
                  onClick={() => {
                    setShowScheduleModal(false);
                    setScheduleConfig({ name: '', scheduledDate: '', scheduledTime: '' });
                  }}
                  className="px-4 py-2 bg-gray-800 text-gray-300 rounded-xl hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleScheduleBulkSend}
                  disabled={savingSchedule || !scheduleConfig.scheduledDate || !scheduleConfig.scheduledTime}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl hover:from-orange-600 hover:to-amber-600 disabled:opacity-50 transition-all font-medium flex items-center gap-2"
                >
                  {savingSchedule ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Agendando...
                    </>
                  ) : (
                    <>
                      <Calendar size={18} />
                      Confirmar Agendamento
                    </>
                  )}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
};


// Cashback Content Component
const CashbackContent = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({
    enabled: false,
    percentage: 5.0,
    expiry_days: 30,
    minimum_order_value: 0.0
  });

  useEffect(() => {
    loadCashbackSettings();
  }, []);

  const loadCashbackSettings = async () => {
    try {
      const response = await axios.get(`${API}/cashback/settings`);
      setSettings(response.data);
    } catch (err) {
      console.error('Error loading cashback settings:', err);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put(`${API}/cashback/settings`, settings);
      setSuccess('Configurações de cashback salvas com sucesso!');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao salvar configurações de cashback');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Configurações de Cashback</h2>
          <p className="text-gray-400">Gerencie o programa de cashback para seus clientes</p>
        </div>
      </div>

      {/* Alerts */}
      {success && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="text-green-500" size={20} />
          <span className="text-green-400">{success}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center gap-3">
          <XCircle className="text-red-500" size={20} />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Settings Card */}
      <div className="glassmorphism-card rounded-2xl p-8 space-y-6">
        {/* Enable Cashback Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-xl">
          <div className="flex-1">
            <Label htmlFor="cashback-enabled" className="text-lg font-semibold text-white mb-1 block cursor-pointer">
              Ativar Cashback
            </Label>
            <p className="text-sm text-gray-400">Permite que clientes acumulem e usem cashback</p>
          </div>
          <Switch
            id="cashback-enabled"
            checked={settings.enabled}
            onCheckedChange={(checked) => setSettings({...settings, enabled: checked})}
            data-testid="cashback-enabled-toggle"
          />
        </div>

        {/* Percentage Input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Percentual de Cashback (%)
          </label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={settings.percentage}
            onChange={(e) => setSettings({...settings, percentage: parseFloat(e.target.value) || 0})}
            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-amber-500 transition-colors"
            placeholder="5.0"
            disabled={!settings.enabled}
            data-testid="cashback-percentage-input"
          />
          <p className="text-xs text-gray-500">
            Percentual do valor do pedido que será devolvido como cashback
          </p>
        </div>

        {/* Expiry Days Input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Validade do Cashback (dias)
          </label>
          <input
            type="number"
            min="1"
            step="1"
            value={settings.expiry_days}
            onChange={(e) => setSettings({...settings, expiry_days: parseInt(e.target.value) || 30})}
            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-amber-500 transition-colors"
            placeholder="30"
            disabled={!settings.enabled}
            data-testid="cashback-expiry-input"
          />
          <p className="text-xs text-gray-500">
            Número de dias até o cashback expirar
          </p>
        </div>

        {/* Minimum Order Value Input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Valor Mínimo do Pedido (R$)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={settings.minimum_order_value}
            onChange={(e) => setSettings({...settings, minimum_order_value: parseFloat(e.target.value) || 0})}
            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-amber-500 transition-colors"
            placeholder="0.00"
            disabled={!settings.enabled}
            data-testid="cashback-minimum-input"
          />
          <p className="text-xs text-gray-500">
            Valor mínimo do pedido para ganhar cashback (deixe 0 para sem mínimo)
          </p>
        </div>

        {/* Save Button */}
        <div className="pt-4">
          <button
            onClick={handleSave}
            disabled={loading}
            className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-xl hover:from-amber-600 hover:to-yellow-600 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="cashback-save-button"
          >
            {loading ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>

        {/* Info Box */}
        {settings.enabled && (
          <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
            <h4 className="text-sm font-semibold text-amber-400 mb-2">Como funciona:</h4>
            <ul className="text-xs text-gray-300 space-y-1">
              <li>• Clientes ganham {settings.percentage}% de cashback em cada pedido</li>
              <li>• O cashback expira após {settings.expiry_days} dias</li>
              {settings.minimum_order_value > 0 && (
                <li>• Pedido mínimo de R$ {settings.minimum_order_value.toFixed(2)} para ganhar cashback</li>
              )}
              <li>• Clientes podem usar o cashback em pedidos futuros</li>
              <li>• O cashback é identificado pelo telefone do cliente</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// Reports Content Component
const ReportsContent = () => {
  const { restaurant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('sales');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  
  // Estados dos relatórios
  const [salesData, setSalesData] = useState(null);
  const [productsData, setProductsData] = useState(null);
  const [categoriesData, setCategoriesData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);

  // Buscar dados de vendas
  const fetchSalesReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/sales`, {
        params: { start_date: dateRange.start, end_date: dateRange.end }
      });
      setSalesData(response.data);
    } catch (error) {
      console.error('Erro ao buscar relatório de vendas:', error);
    }
    setLoading(false);
  };

  // Buscar produtos mais vendidos
  const fetchProductsReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/top-products`, {
        params: { start_date: dateRange.start, end_date: dateRange.end, limit: 10 }
      });
      setProductsData(response.data);
    } catch (error) {
      console.error('Erro ao buscar produtos mais vendidos:', error);
    }
    setLoading(false);
  };

  // Buscar vendas por categoria
  const fetchCategoriesReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/by-category`, {
        params: { start_date: dateRange.start, end_date: dateRange.end }
      });
      setCategoriesData(response.data);
    } catch (error) {
      console.error('Erro ao buscar vendas por categoria:', error);
    }
    setLoading(false);
  };

  // Buscar comparativo mensal
  const fetchMonthlyReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/monthly-comparison`, {
        params: { months: 6 }
      });
      setMonthlyData(response.data);
    } catch (error) {
      console.error('Erro ao buscar comparativo mensal:', error);
    }
    setLoading(false);
  };

  // Exportar para CSV
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).join(','));
    const csv = [headers, ...rows].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${dateRange.start}_${dateRange.end}.csv`;
    a.click();
  };

  // Exportar para Excel
  const exportToExcel = (data, filename, sheetName = 'Relatório') => {
    if (!data || data.length === 0) return;
    
    // Criar workbook e worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    
    // Download do arquivo
    XLSX.writeFile(workbook, `${filename}_${dateRange.start}_${dateRange.end}.xlsx`);
  };

  useEffect(() => {
    if (activeTab === 'sales') fetchSalesReport();
    else if (activeTab === 'products') fetchProductsReport();
    else if (activeTab === 'categories') fetchCategoriesReport();
    else if (activeTab === 'monthly') fetchMonthlyReport();
  }, [activeTab, dateRange]);

  const tabs = [
    { id: 'sales', label: 'Vendas Gerais', icon: DollarSign },
    { id: 'products', label: 'Produtos Mais Vendidos', icon: Package },
    { id: 'categories', label: 'Por Categoria', icon: Tag },
    { id: 'monthly', label: 'Comparativo Mensal', icon: TrendingUp }
  ];

  return (
    <div className="space-y-4 md:space-y-6" data-testid="reports-content">
      {/* Mobile-Optimized Header */}
      <div className="glass-card p-4 md:p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
            <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-3xl font-bold text-white truncate">Relatórios</h1>
            <p className="text-xs md:text-sm text-gray-400 hidden sm:block">Análise de vendas</p>
          </div>
        </div>

        {/* Período compacto para mobile */}
        <div className="flex items-center gap-2 text-sm mb-3">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex-1 glass-button px-3 py-2 flex items-center justify-between gap-2"
          >
            <span className="text-gray-300 text-xs md:text-sm truncate">
              {new Date(dateRange.start).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} - {new Date(dateRange.end).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Date Picker Expandível */}
        {showDatePicker && (
          <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Início</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="glass-input w-full text-sm py-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Fim</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="glass-input w-full text-sm py-2"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setDateRange({
                    start: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
                    end: new Date().toISOString().split('T')[0]
                  });
                  setShowDatePicker(false);
                }}
                className="glass-button px-3 py-2 text-xs"
              >
                7 dias
              </button>
              <button
                onClick={() => {
                  setDateRange({
                    start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
                    end: new Date().toISOString().split('T')[0]
                  });
                  setShowDatePicker(false);
                }}
                className="glass-button px-3 py-2 text-xs"
              >
                30 dias
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile-Optimized Tabs */}
      <div className="glass-card overflow-hidden">
        <div className="flex overflow-x-auto scrollbar-hide p-2 gap-2 border-b border-white/10">
          <button
            onClick={() => setActiveTab('sales')}
            className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${
              activeTab === 'sales'
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <DollarSign size={16} />
            <span className="text-xs md:text-sm font-medium">Vendas</span>
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${
              activeTab === 'products'
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Package size={16} />
            <span className="text-xs md:text-sm font-medium">Produtos</span>
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${
              activeTab === 'categories'
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Tag size={16} />
            <span className="text-xs md:text-sm font-medium">Categorias</span>
          </button>
          <button
            onClick={() => setActiveTab('monthly')}
            className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${
              activeTab === 'monthly'
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <TrendingUp size={16} />
            <span className="text-xs md:text-sm font-medium">Mensal</span>
          </button>
        </div>

        <div className="p-4 md:p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-3"></div>
              <p className="text-sm text-gray-400">Carregando...</p>
            </div>
          ) : (
            <>
              {/* Sales Report */}
              {activeTab === 'sales' && salesData && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <h2 className="text-lg md:text-2xl font-bold text-white">Vendas Gerais</h2>
                    <div className="flex gap-2">
                      <button
                        onClick={() => exportToCSV([salesData], 'relatorio_vendas')}
                        className="glass-button-primary flex items-center justify-center gap-2 text-sm py-2"
                      >
                        <Download size={16} />
                        <span className="sm:inline">CSV</span>
                      </button>
                      <button
                        onClick={() => exportToExcel([salesData], 'relatorio_vendas', 'Vendas')}
                        className="glass-button-primary flex items-center justify-center gap-2 text-sm py-2"
                      >
                        <Download size={16} />
                        <span className="sm:inline">Excel</span>
                      </button>
                    </div>
                  </div>

                  {/* Cards de Resumo - Mobile Optimized */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ShoppingCart className="w-4 h-4 md:w-5 md:h-5 text-emerald-400 flex-shrink-0" />
                        <p className="text-xs text-gray-400 uppercase font-semibold truncate">Pedidos</p>
                      </div>
                      <p className="text-2xl md:text-3xl font-bold text-white">{salesData.total_orders || 0}</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-green-400 flex-shrink-0" />
                        <p className="text-xs text-gray-400 uppercase font-semibold truncate">Receita</p>
                      </div>
                      <p className="text-lg md:text-2xl font-bold text-white truncate">R$ {(salesData.total_revenue || 0).toFixed(2)}</p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-purple-400 flex-shrink-0" />
                        <p className="text-xs text-gray-400 uppercase font-semibold truncate">Ticket</p>
                      </div>
                      <p className="text-lg md:text-2xl font-bold text-white truncate">R$ {(salesData.average_ticket || 0).toFixed(2)}</p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="w-4 h-4 md:w-5 md:h-5 text-orange-400 flex-shrink-0" />
                        <p className="text-xs text-gray-400 uppercase font-semibold truncate">Itens</p>
                      </div>
                      <p className="text-2xl md:text-3xl font-bold text-white">{salesData.total_items || 0}</p>
                    </div>
                  </div>

                  {/* Por Tipo - Mobile Friendly */}
                  {salesData.by_delivery_type && Object.keys(salesData.by_delivery_type).length > 0 && (
                    <div className="glass-card p-4">
                      <h3 className="text-base md:text-lg font-bold text-white mb-3">Por Tipo</h3>
                      <div className="space-y-2">
                        {Object.entries(salesData.by_delivery_type).map(([type, data]) => (
                          <div key={type} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                            <div>
                              <p className="text-sm text-gray-400 capitalize">{type}</p>
                              <p className="text-base md:text-lg font-bold text-white">{data.count} pedidos</p>
                            </div>
                            <p className="text-lg md:text-xl font-bold text-green-400">R$ {data.revenue.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Top Products */}
              {activeTab === 'products' && productsData && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-white">Produtos Mais Vendidos</h2>
                    <div className="flex gap-2">
                      <button
                        onClick={() => exportToCSV(productsData, 'produtos_mais_vendidos')}
                        className="glass-button-primary flex items-center gap-2"
                      >
                        <Download size={18} />
                        CSV
                      </button>
                      <button
                        onClick={() => exportToExcel(productsData, 'produtos_mais_vendidos', 'Top Produtos')}
                        className="glass-button-primary flex items-center gap-2"
                      >
                        <Download size={18} />
                        Excel
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {productsData.map((product, index) => (
                      <div key={index} className="glass-card p-4 flex items-center justify-between hover:border-green-500/30 transition-all">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                            index === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500' :
                            index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
                            index === 2 ? 'bg-gradient-to-br from-orange-600 to-red-600' :
                            'bg-gradient-to-br from-emerald-500 to-cyan-500'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="text-white font-semibold">{product.product_name}</p>
                            <p className="text-sm text-gray-400">{product.quantity} unidades vendidas</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-400">R$ {product.revenue.toFixed(2)}</p>
                          <p className="text-sm text-gray-400">em receita</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* By Category */}
              {activeTab === 'categories' && categoriesData && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-white">Vendas por Categoria</h2>
                    <div className="flex gap-2">
                      <button
                        onClick={() => exportToCSV(categoriesData, 'vendas_por_categoria')}
                        className="glass-button-primary flex items-center gap-2"
                      >
                        <Download size={18} />
                        CSV
                      </button>
                      <button
                        onClick={() => exportToExcel(categoriesData, 'vendas_por_categoria', 'Categorias')}
                        className="glass-button-primary flex items-center gap-2"
                      >
                        <Download size={18} />
                        Excel
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {categoriesData.map((category, index) => (
                      <div key={index} className="glass-card p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                            <Tag className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-white">{category.category_name}</h3>
                            <p className="text-sm text-gray-400">{category.products_count} produtos</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Quantidade Vendida:</span>
                            <span className="text-white font-semibold">{category.total_quantity}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Receita Total:</span>
                            <span className="text-green-400 font-bold text-lg">R$ {category.revenue.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Ticket Médio:</span>
                            <span className="text-white font-semibold">R$ {category.average_price.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly Comparison */}
              {activeTab === 'monthly' && monthlyData && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-white">Comparativo Mensal</h2>
                    <div className="flex gap-2">
                      <button
                        onClick={() => exportToCSV(monthlyData, 'comparativo_mensal')}
                        className="glass-button-primary flex items-center gap-2"
                      >
                        <Download size={18} />
                        CSV
                      </button>
                      <button
                        onClick={() => exportToExcel(monthlyData, 'comparativo_mensal', 'Mensal')}
                        className="glass-button-primary flex items-center gap-2"
                      >
                        <Download size={18} />
                        Excel
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {monthlyData.map((month, index) => {
                      const prevMonth = monthlyData[index + 1];
                      const growth = prevMonth ? ((month.revenue - prevMonth.revenue) / prevMonth.revenue * 100) : 0;
                      
                      return (
                        <div key={index} className="glass-card p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-white" />
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-white">{month.month}</h3>
                                <p className="text-sm text-gray-400">{month.year}</p>
                              </div>
                            </div>
                            {growth !== 0 && (
                              <div className={`px-3 py-1 rounded-lg ${growth > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                {growth > 0 ? '↑' : '↓'} {Math.abs(growth).toFixed(1)}%
                              </div>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <p className="text-sm text-gray-400">Pedidos</p>
                              <p className="text-2xl font-bold text-white">{month.orders}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-400">Receita</p>
                              <p className="text-2xl font-bold text-green-400">R$ {month.revenue.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-400">Ticket Médio</p>
                              <p className="text-2xl font-bold text-white">R$ {month.average_ticket.toFixed(2)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!loading && (
                (activeTab === 'sales' && !salesData) ||
                (activeTab === 'products' && !productsData) ||
                (activeTab === 'categories' && !categoriesData) ||
                (activeTab === 'monthly' && !monthlyData)
              ) && (
                <div className="text-center py-12">
                  <TrendingUp className="w-12 h-12 md:w-16 md:h-16 text-gray-600 mx-auto mb-3" />
                  <p className="text-sm md:text-base text-gray-400">Nenhum dado disponível</p>
                  <p className="text-xs text-gray-500 mt-1">Selecione outro período</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// Settings Content Component
const SettingsContent = () => {
  const { restaurant } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // General Settings
  const [generalSettings, setGeneralSettings] = useState({
    name: restaurant?.name || '',
    subdomain: restaurant?.subdomain || '',
    phone: restaurant?.phone || '',
    minimum_order: restaurant?.minimum_order || 15.00,
    delivery_time: restaurant?.delivery_time || ''
  });

  // Schedule Settings
  const [scheduleSettings, setScheduleSettings] = useState({
    auto_open_close: false,
    lunch_break_enabled: false,
    lunch_break_start: '12:00',
    lunch_break_end: '14:00',
    schedule: {
      monday: { enabled: true, open: '10:00', close: '22:00', lunch_break: false },
      tuesday: { enabled: true, open: '10:00', close: '22:00', lunch_break: false },
      wednesday: { enabled: true, open: '10:00', close: '22:00', lunch_break: false },
      thursday: { enabled: true, open: '10:00', close: '22:00', lunch_break: false },
      friday: { enabled: true, open: '10:00', close: '22:00', lunch_break: false },
      saturday: { enabled: true, open: '10:00', close: '23:00', lunch_break: false },
      sunday: { enabled: true, open: '11:00', close: '21:00', lunch_break: false }
    }
  });

  // Scheduling Settings (Agendamento de Pedidos)
  const [schedulingSettings, setSchedulingSettings] = useState({
    scheduling_enabled: false,
    scheduling_when_open: false,
    scheduling_max_days: 7
  });

  // Account Settings
  const [accountSettings, setAccountSettings] = useState({
    email: restaurant?.email || '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Menu Appearance Settings
  const [appearanceSettings, setAppearanceSettings] = useState({
    primary_color: '#2ECC71',
    secondary_color: '#ef4444',
    layout: 'list', // 'list' or 'grid'
    logo_url: null
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  // Payment Methods
  const [paymentMethods, setPaymentMethods] = useState({
    dinheiro: true,
    cartao: true,
    pix: true,
    pix_key: '',
    pix_mp: false,
    vale_refeicao: false,
    vale_alimentacao: false,
    cartao_bandeiras: [],
    vale_refeicao_bandeiras: [],
    vale_alimentacao_bandeiras: []
  });

  // Opções de bandeiras disponíveis
  const bandeirasCartao = [
    { id: 'visa', nome: 'Visa' },
    { id: 'mastercard', nome: 'Mastercard' },
    { id: 'elo', nome: 'Elo' },
    { id: 'amex', nome: 'American Express' },
    { id: 'hipercard', nome: 'Hipercard' },
    { id: 'diners', nome: 'Diners Club' }
  ];

  const bandeirasVale = [
    { id: 'sodexo', nome: 'Sodexo' },
    { id: 'alelo', nome: 'Alelo' },
    { id: 'ticket', nome: 'Ticket' },
    { id: 'vr', nome: 'VR' },
    { id: 'ben', nome: 'Ben Visa Vale' },
    { id: 'greencard', nome: 'Green Card' },
    { id: 'cooper', nome: 'Cooper Card' }
  ];

  // Impression Settings
  const [impressionSettings, setImpressionSettings] = useState({
    configured: false,
    active: false,
    token: null
  });
  const [generatingToken, setGeneratingToken] = useState(false);
  const [downloadingConfig, setDownloadingConfig] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);

  // Carregar configurações de impressão
  useEffect(() => {
    if (activeTab === 'impression') {
      loadImpressionSettings();
    }
  }, [activeTab]);

  const loadImpressionSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/impression/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImpressionSettings(response.data);
    } catch (err) {
      console.error('Error loading impression settings:', err);
    }
  };

  const handleGenerateToken = async () => {
    try {
      setGeneratingToken(true);
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/impression/generate-token`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setImpressionSettings(prev => ({
        ...prev,
        configured: true,
        token: response.data.token,
        active: response.data.active
      }));
      
      setSuccess('Token gerado com sucesso! 🎉');
    } catch (err) {
      setError('Erro ao gerar token de impressão');
      console.error(err);
    } finally {
      setGeneratingToken(false);
    }
  };

  const handleToggleImpression = async () => {
    try {
      setError('');
      setSuccess('');
      
      const newActiveState = !impressionSettings.active;
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/impression/toggle`, 
        { active: newActiveState },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setImpressionSettings(prev => ({
        ...prev,
        active: newActiveState
      }));
      
      setSuccess(newActiveState ? 'Impressão automática ativada! 🖨️' : 'Impressão automática desativada');
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Configure um token de impressão primeiro');
      } else {
        setError('Erro ao alternar impressão automática');
      }
      console.error(err);
    }
  };

  const handleDownloadConfig = async () => {
    try {
      setDownloadingConfig(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/impression/download-config`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text'  // Receber como texto puro, não parsear como JSON
      });
      
      // O backend já retorna o JSON no formato correto - NÃO reformatar!
      // Criar blob direto com o conteúdo recebido
      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'login.json';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccess('Arquivo login.json baixado com sucesso! 📥');
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Gere um token de impressão primeiro');
      } else {
        setError('Erro ao baixar configuração');
      }
      console.error(err);
    } finally {
      setDownloadingConfig(false);
    }
  };

  const copyTokenToClipboard = () => {
    if (impressionSettings.token) {
      navigator.clipboard.writeText(impressionSettings.token);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    }
  };

  const tabs = [
    { id: 'general', label: 'Geral', icon: Sliders },
    { id: 'schedule', label: 'Horários', icon: Clock },
    { id: 'account', label: 'Conta', icon: UserCog },
    { id: 'appearance', label: 'Aparência', icon: Palette },
    { id: 'payment', label: 'Pagamento', icon: DollarSign },
    { id: 'impression', label: 'Impressão Automática', icon: Printer }
  ];

  const handleSaveGeneral = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put(`${API}/restaurant/settings`, generalSettings);
      setSuccess('Configurações gerais salvas com sucesso!');
    } catch (err) {
      setError('Erro ao salvar configurações gerais');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put(`${API}/restaurant/schedule`, scheduleSettings);
      setSuccess('Horários de funcionamento salvos com sucesso!');
    } catch (err) {
      setError('Erro ao salvar horários');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveScheduling = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put(`${API}/restaurant/scheduling`, schedulingSettings);
      setSuccess('Configurações de agendamento salvas com sucesso!');
    } catch (err) {
      setError('Erro ao salvar configurações de agendamento');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAccount = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      if (accountSettings.new_password && accountSettings.new_password !== accountSettings.confirm_password) {
        setError('As senhas não coincidem');
        setLoading(false);
        return;
      }

      await axios.put(`${API}/restaurant/account`, {
        email: accountSettings.email,
        current_password: accountSettings.current_password,
        new_password: accountSettings.new_password
      });
      
      setSuccess('Configurações da conta atualizadas com sucesso!');
      setAccountSettings({ ...accountSettings, current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar conta');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAppearance = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put(`${API}/restaurant/appearance`, appearanceSettings);
      setSuccess('Configurações de aparência salvas com sucesso!');
    } catch (err) {
      setError('Erro ao salvar aparência');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Formato de arquivo não suportado. Use JPG, PNG ou WebP.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Arquivo muito grande. Tamanho máximo: 5MB');
      return;
    }

    try {
      setUploadingLogo(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/restaurant/upload-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      const logoUrl = response.data.logo_url;
      setAppearanceSettings(prev => ({ ...prev, logo_url: logoUrl }));
      setLogoPreview(logoUrl);
      setSuccess('Logo enviada com sucesso! 🎉');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar logo');
      console.error(err);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Tem certeza que deseja remover a logo?')) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      await axios.delete(`${API}/restaurant/logo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setAppearanceSettings(prev => ({ ...prev, logo_url: null }));
      setLogoPreview(null);
      setSuccess('Logo removida com sucesso!');
    } catch (err) {
      setError('Erro ao remover logo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePayment = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put(`${API}/restaurant/payment-methods`, paymentMethods);
      setSuccess('Formas de pagamento atualizadas com sucesso!');
    } catch (err) {
      setError('Erro ao atualizar formas de pagamento');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get(`${API}/restaurant/settings`);
        const data = response.data;
        
        setGeneralSettings({
          name: data.name || '',
          subdomain: data.subdomain || '',
          phone: data.phone || '',
          minimum_order: data.minimum_order || 15.00,
          delivery_time: data.delivery_time || ''
        });

        if (data.schedule) {
          setScheduleSettings(data.schedule);
        }

        // Load scheduling settings (agendamento)
        if (data.scheduling) {
          setSchedulingSettings(data.scheduling);
        }

        setAccountSettings({
          ...accountSettings,
          email: data.email || restaurant?.email || ''
        });

        if (data.appearance) {
          setAppearanceSettings(data.appearance);
          if (data.appearance.logo_url) {
            setLogoPreview(data.appearance.logo_url);
          }
        }
        
        // Also check for logo_url directly in restaurant data
        if (data.logo_url) {
          setAppearanceSettings(prev => ({ ...prev, logo_url: data.logo_url }));
          setLogoPreview(data.logo_url);
        }

        if (data.payment_methods) {
          setPaymentMethods(data.payment_methods);
        }
      } catch (err) {
        console.error('Error loading settings:', err);
      }
    };

    loadSettings();
  }, []);

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header - Mobile optimized */}
      <div className="px-1">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Configurações</h1>
        <p className="text-sm text-gray-400">Gerencie as configurações do seu estabelecimento</p>
      </div>

      {/* Tabs - Scroll horizontal otimizado para mobile */}
      <div className="relative -mx-4 sm:mx-0">
        <div 
          className="flex space-x-1 sm:space-x-2 border-b border-white/10 overflow-x-auto scrollbar-hide snap-x snap-proximity px-4 sm:px-0 pb-px" 
          style={{ 
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
            scrollPaddingLeft: '16px',
            scrollPaddingRight: '16px'
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setError('');
                setSuccess('');
              }}
              className={`flex items-center justify-center space-x-1.5 sm:space-x-2 px-3 sm:px-4 py-3.5 sm:py-3 font-medium transition-all duration-200 relative whitespace-nowrap snap-start min-h-[48px] flex-shrink-0 ${
                activeTab === tab.id
                  ? 'text-emerald-400 scale-105'
                  : 'text-gray-400 hover:text-gray-300 active:scale-95'
              }`}
              data-testid={`settings-tab-${tab.id}`}
            >
              <tab.icon size={18} className="w-[18px] h-[18px] flex-shrink-0" />
              <span className="text-xs sm:text-sm font-semibold">{tab.label}</span>
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-t-full shadow-lg shadow-emerald-400/50" />
              )}
            </button>
          ))}
        </div>
        
        {/* Indicadores de scroll mais visíveis para mobile */}
        <div className="absolute top-0 left-0 w-12 h-full bg-gradient-to-r from-gray-900 via-gray-900/80 to-transparent pointer-events-none sm:hidden z-10" />
        <div className="absolute top-0 right-0 w-12 h-full bg-gradient-to-l from-gray-900 via-gray-900/80 to-transparent pointer-events-none sm:hidden z-10" />
      </div>

      {/* Success/Error Messages - Mobile optimized */}
      {success && (
        <div className="bg-green-500/20 border-2 border-green-500/50 text-green-400 px-5 py-4 rounded-xl text-sm sm:text-base font-medium shadow-lg">
          ✓ {success}
        </div>
      )}
      {error && (
        <div className="bg-red-500/20 border-2 border-red-500/50 text-red-400 px-5 py-4 rounded-xl text-sm sm:text-base font-medium shadow-lg">
          ✗ {error}
        </div>
      )}

      {/* General Settings Tab - Mobile optimized */}
      {activeTab === 'general' && (
        <div className="glass-card">
          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-6 sm:mb-8">Configurações Gerais</h2>
          <div className="space-y-6 sm:space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div>
                <label className="block text-base font-medium text-gray-300 mb-3">
                  Nome do Estabelecimento
                </label>
                <input
                  type="text"
                  className="glass-input w-full text-base sm:text-sm"
                  style={{ minHeight: '48px', padding: '12px 16px' }}
                  value={generalSettings.name}
                  onChange={(e) => setGeneralSettings({ ...generalSettings, name: e.target.value })}
                  placeholder="Nome do seu restaurante"
                  data-testid="general-name-input"
                />
              </div>

              <div>
                <label className="block text-base font-medium text-gray-300 mb-3">
                  Subdomínio
                </label>
                <div className="flex items-stretch">
                  <input
                    type="text"
                    className="glass-input flex-1 rounded-r-none text-base sm:text-sm"
                    style={{ minHeight: '48px', padding: '12px 16px' }}
                    value={generalSettings.subdomain}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, subdomain: e.target.value })}
                    placeholder="seurestaurante"
                    data-testid="general-subdomain-input"
                  />
                  <span className="bg-white/5 border border-l-0 border-white/10 px-4 py-3 rounded-r-xl text-gray-400 text-sm flex items-center whitespace-nowrap">
                    .menu.com
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-base font-medium text-gray-300 mb-3">
                  Telefone/WhatsApp
                </label>
                <input
                  type="text"
                  className="glass-input w-full text-base sm:text-sm"
                  style={{ minHeight: '48px', padding: '12px 16px' }}
                  value={generalSettings.phone}
                  onChange={(e) => setGeneralSettings({ ...generalSettings, phone: e.target.value })}
                  placeholder="+55 (00) 00000-0000"
                  data-testid="general-phone-input"
                />
              </div>

              <div>
                <label className="block text-base font-medium text-gray-300 mb-3">
                  Pedido Mínimo (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  className="glass-input w-full text-base sm:text-sm"
                  style={{ minHeight: '48px', padding: '12px 16px' }}
                  value={generalSettings.minimum_order}
                  onChange={(e) => setGeneralSettings({ ...generalSettings, minimum_order: parseFloat(e.target.value) })}
                  placeholder="15.00"
                  data-testid="general-minimum-order-input"
                />
              </div>

              <div>
                <label className="block text-base font-medium text-gray-300 mb-3">
                  Tempo de Entrega
                </label>
                <input
                  type="text"
                  className="glass-input w-full text-base sm:text-sm"
                  style={{ minHeight: '48px', padding: '12px 16px' }}
                  value={generalSettings.delivery_time}
                  onChange={(e) => setGeneralSettings({ ...generalSettings, delivery_time: e.target.value })}
                  placeholder="Ex: 30-45 min"
                  data-testid="general-delivery-time-input"
                />
                <p className="text-sm text-gray-500 mt-2">Este tempo será exibido no perfil da loja no menu público</p>
              </div>
            </div>

            <div className="flex justify-end pt-4 sm:pt-6 border-t border-white/10">
              <button
                onClick={handleSaveGeneral}
                disabled={loading}
                className="glass-button-primary w-full sm:w-auto"
                style={{ minHeight: '48px', padding: '12px 32px', fontSize: '16px' }}
                data-testid="general-save-button"
              >
                {loading ? 'Salvando...' : 'Salvar Configurações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Settings Tab - Mobile optimized com layout card */}
      {activeTab === 'schedule' && (
        <div className="glass-card">
          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-6 sm:mb-8">Horários de Funcionamento</h2>
          
          {/* Auto Open/Close Toggle - Mobile optimized */}
          <div className="mb-8 p-5 sm:p-6 bg-white/5 rounded-xl border border-white/10">
            <div className="flex items-start sm:items-center justify-between gap-4 mb-4">
              <div className="flex-1">
                <Label htmlFor="schedule-auto" className="text-white font-semibold mb-2 text-base sm:text-lg block cursor-pointer">
                  Abertura/Fechamento Automático
                </Label>
                <p className="text-gray-400 text-sm leading-relaxed">
                  O estabelecimento abrirá e fechará automaticamente nos horários configurados
                </p>
              </div>
              <div className="flex-shrink-0" style={{ minWidth: '48px', minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Switch
                  id="schedule-auto"
                  checked={scheduleSettings.auto_open_close}
                  onCheckedChange={(checked) => setScheduleSettings({ ...scheduleSettings, auto_open_close: checked })}
                  data-testid="schedule-auto-toggle"
                />
              </div>
            </div>
            {scheduleSettings.auto_open_close && (
              <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                <div className="flex items-center text-emerald-400 text-sm">
                  <Clock size={18} className="mr-2 flex-shrink-0" />
                  <span>O sistema controlará automaticamente o status do estabelecimento</span>
                </div>
              </div>
            )}
          </div>

          {/* Lunch Break Toggle - Pausa para Almoço */}
          <div className="mb-8 p-5 sm:p-6 bg-white/5 rounded-xl border border-white/10">
            <div className="flex items-start sm:items-center justify-between gap-4 mb-4">
              <div className="flex-1">
                <Label htmlFor="lunch-break-toggle" className="text-white font-semibold mb-2 text-base sm:text-lg block cursor-pointer">
                  Pausa para Almoço
                </Label>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Habilite se o estabelecimento fecha durante o horário de almoço
                </p>
              </div>
              <div className="flex-shrink-0" style={{ minWidth: '48px', minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Switch
                  id="lunch-break-toggle"
                  checked={scheduleSettings.lunch_break_enabled}
                  onCheckedChange={(checked) => setScheduleSettings({ ...scheduleSettings, lunch_break_enabled: checked })}
                  data-testid="lunch-break-toggle"
                />
              </div>
            </div>
            
            {scheduleSettings.lunch_break_enabled && (
              <div className="mt-4 space-y-4">
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                  <div className="flex items-center text-amber-400 text-sm mb-3">
                    <Clock size={18} className="mr-2 flex-shrink-0" />
                    <span>Configure o horário padrão da pausa. Você pode personalizar por dia abaixo.</span>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Início da Pausa</label>
                      <input
                        type="time"
                        className="glass-input w-full text-base sm:text-sm"
                        style={{ minHeight: '48px', padding: '12px 16px' }}
                        value={scheduleSettings.lunch_break_start}
                        onChange={(e) => setScheduleSettings({ ...scheduleSettings, lunch_break_start: e.target.value })}
                        data-testid="lunch-break-start"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Fim da Pausa</label>
                      <input
                        type="time"
                        className="glass-input w-full text-base sm:text-sm"
                        style={{ minHeight: '48px', padding: '12px 16px' }}
                        value={scheduleSettings.lunch_break_end}
                        onChange={(e) => setScheduleSettings({ ...scheduleSettings, lunch_break_end: e.target.value })}
                        data-testid="lunch-break-end"
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      const newSchedule = { ...scheduleSettings.schedule };
                      Object.keys(newSchedule).forEach(day => {
                        if (newSchedule[day].enabled) {
                          newSchedule[day] = { ...newSchedule[day], lunch_break: true };
                        }
                      });
                      setScheduleSettings({ ...scheduleSettings, schedule: newSchedule });
                    }}
                    className="mt-4 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-sm font-medium transition-all"
                  >
                    Aplicar pausa para todos os dias ativos
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Weekly Schedule - Layout card para mobile */}
          <div className="space-y-4 sm:space-y-5">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-4">Horários por Dia da Semana</h3>
            
            {Object.entries({
              monday: 'Segunda-feira',
              tuesday: 'Terça-feira',
              wednesday: 'Quarta-feira',
              thursday: 'Quinta-feira',
              friday: 'Sexta-feira',
              saturday: 'Sábado',
              sunday: 'Domingo'
            }).map(([day, label]) => (
              <div 
                key={day} 
                className={`p-5 sm:p-6 rounded-xl border-2 transition-all duration-200 ${
                  scheduleSettings.schedule[day].enabled 
                    ? 'bg-white/5 border-white/10 hover:border-white/20' 
                    : 'bg-gray-800/30 border-gray-800/50'
                }`}
              >
                {/* Header do card - Nome do dia e toggle */}
                <div className="flex items-center justify-between mb-5 sm:mb-4">
                  <div className="flex items-center space-x-4">
                    <div style={{ minWidth: '48px', minHeight: '48px', display: 'flex', alignItems: 'center' }}>
                      <Switch
                        id={`schedule-${day}`}
                        checked={scheduleSettings.schedule[day].enabled}
                        onCheckedChange={(checked) => 
                          setScheduleSettings({
                            ...scheduleSettings,
                            schedule: {
                              ...scheduleSettings.schedule,
                              [day]: { ...scheduleSettings.schedule[day], enabled: checked }
                            }
                          })
                        }
                        data-testid={`schedule-${day}-toggle`}
                      />
                    </div>
                    <Label 
                      htmlFor={`schedule-${day}`}
                      className={`font-semibold text-base sm:text-lg cursor-pointer ${
                        scheduleSettings.schedule[day].enabled ? 'text-white' : 'text-gray-500'
                      }`}
                    >
                      {label}
                    </Label>
                  </div>
                  {!scheduleSettings.schedule[day].enabled && (
                    <span className="text-gray-500 text-sm italic px-3 py-1 bg-gray-800/50 rounded-lg">Fechado</span>
                  )}
                </div>

                {/* Horários - Layout vertical em mobile */}
                {scheduleSettings.schedule[day].enabled && (
                  <div className="space-y-4">
                    <div className="space-y-4 sm:space-y-0 sm:flex sm:items-center sm:space-x-4 pl-0 sm:pl-16">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-400 mb-2">Horário de Abertura</label>
                        <input
                          type="time"
                          className="glass-input w-full text-base sm:text-sm"
                          style={{ minHeight: '48px', padding: '12px 16px' }}
                          value={scheduleSettings.schedule[day].open}
                          onChange={(e) => 
                            setScheduleSettings({
                              ...scheduleSettings,
                              schedule: {
                                ...scheduleSettings.schedule,
                                [day]: { ...scheduleSettings.schedule[day], open: e.target.value }
                              }
                            })
                          }
                          data-testid={`schedule-${day}-open`}
                        />
                      </div>
                      
                      <div className="hidden sm:flex items-center text-gray-500 self-end pb-3">—</div>
                      
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-400 mb-2">Horário de Fechamento</label>
                        <input
                          type="time"
                          className="glass-input w-full text-base sm:text-sm"
                          style={{ minHeight: '48px', padding: '12px 16px' }}
                          value={scheduleSettings.schedule[day].close}
                          onChange={(e) => 
                            setScheduleSettings({
                              ...scheduleSettings,
                              schedule: {
                                ...scheduleSettings.schedule,
                                [day]: { ...scheduleSettings.schedule[day], close: e.target.value }
                              }
                            })
                          }
                          data-testid={`schedule-${day}-close`}
                        />
                      </div>
                    </div>
                    
                    {/* Checkbox de Pausa para Almoço por dia */}
                    {scheduleSettings.lunch_break_enabled && (
                      <div className="pl-0 sm:pl-16 mt-3">
                        <label className="flex items-center space-x-3 cursor-pointer p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg hover:bg-amber-500/15 transition-colors">
                          <input
                            type="checkbox"
                            checked={scheduleSettings.schedule[day].lunch_break || false}
                            onChange={(e) => 
                              setScheduleSettings({
                                ...scheduleSettings,
                                schedule: {
                                  ...scheduleSettings.schedule,
                                  [day]: { ...scheduleSettings.schedule[day], lunch_break: e.target.checked }
                                }
                              })
                            }
                            className="w-5 h-5 rounded border-amber-500/50 text-amber-500 focus:ring-amber-500/50 bg-transparent"
                            data-testid={`schedule-${day}-lunch-break`}
                          />
                          <span className="text-sm text-amber-400">
                            Pausa para almoço ({scheduleSettings.lunch_break_start} - {scheduleSettings.lunch_break_end})
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Quick Actions - Mobile friendly */}
          <div className="mt-8 p-5 sm:p-6 bg-white/5 rounded-xl border border-white/10">
            <h4 className="text-white font-semibold mb-4 text-base">Ações Rápidas</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={() => {
                  const newSchedule = { ...scheduleSettings.schedule };
                  Object.keys(newSchedule).forEach(day => {
                    newSchedule[day] = { enabled: true, open: '10:00', close: '22:00' };
                  });
                  setScheduleSettings({ ...scheduleSettings, schedule: newSchedule });
                }}
                className="px-4 py-3 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white rounded-xl text-sm font-medium transition-all duration-200 active:scale-95"
                style={{ minHeight: '48px' }}
              >
                Aplicar 10h-22h para todos
              </button>
              
              <button
                onClick={() => {
                  const newSchedule = { ...scheduleSettings.schedule };
                  ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].forEach(day => {
                    newSchedule[day] = { enabled: true, open: '10:00', close: '22:00' };
                  });
                  newSchedule.saturday = { enabled: true, open: '10:00', close: '23:00' };
                  newSchedule.sunday = { enabled: true, open: '11:00', close: '21:00' };
                  setScheduleSettings({ ...scheduleSettings, schedule: newSchedule });
                }}
                className="px-4 py-3 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white rounded-xl text-sm font-medium transition-all duration-200 active:scale-95"
                style={{ minHeight: '48px' }}
              >
                Horário comercial padrão
              </button>

              <button
                onClick={() => {
                  const newSchedule = { ...scheduleSettings.schedule };
                  Object.keys(newSchedule).forEach(day => {
                    newSchedule[day] = { enabled: false, open: '10:00', close: '22:00' };
                  });
                  setScheduleSettings({ ...scheduleSettings, schedule: newSchedule });
                }}
                className="px-4 py-3 bg-red-500/20 hover:bg-red-500/30 active:bg-red-500/40 text-red-400 rounded-xl text-sm font-medium transition-all duration-200 active:scale-95"
                style={{ minHeight: '48px' }}
              >
                Desativar todos
              </button>
            </div>
          </div>

          <div className="flex justify-end mt-8 pt-6 border-t border-white/10">
            <button
              onClick={handleSaveSchedule}
              disabled={loading}
              className="glass-button-primary w-full sm:w-auto"
              style={{ minHeight: '48px', padding: '12px 32px', fontSize: '16px' }}
              data-testid="schedule-save-button"
            >
              {loading ? 'Salvando...' : 'Salvar Horários'}
            </button>
          </div>

          {/* Scheduling Section - Agendamento de Pedidos */}
          <div className="mt-10 pt-8 border-t border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <Calendar className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Agendamento de Pedidos</h3>
                <p className="text-sm text-gray-400">Permita que clientes agendem pedidos para horários futuros</p>
              </div>
            </div>

            {/* Enable scheduling when closed */}
            <div className="space-y-4">
              <div className="p-5 bg-white/5 rounded-xl border border-white/10">
                <div className="flex items-start sm:items-center justify-between gap-4 mb-3">
                  <div className="flex-1">
                    <Label htmlFor="scheduling-enabled" className="text-white font-semibold text-base block cursor-pointer">
                      Permitir agendamento quando fechado
                    </Label>
                    <p className="text-gray-400 text-sm mt-1">
                      Clientes podem agendar pedidos mesmo quando a loja está fechada
                    </p>
                  </div>
                  <div className="flex-shrink-0" style={{ minWidth: '48px', minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Switch
                      id="scheduling-enabled"
                      checked={schedulingSettings.scheduling_enabled}
                      onCheckedChange={(checked) => setSchedulingSettings({ ...schedulingSettings, scheduling_enabled: checked })}
                      data-testid="scheduling-enabled-toggle"
                    />
                  </div>
                </div>
              </div>

              {/* Enable scheduling when open */}
              {schedulingSettings.scheduling_enabled && (
                <div className="p-5 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-start sm:items-center justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <Label htmlFor="scheduling-when-open" className="text-white font-semibold text-base block cursor-pointer">
                        Permitir agendamento quando aberto
                      </Label>
                      <p className="text-gray-400 text-sm mt-1">
                        Clientes também podem agendar pedidos enquanto a loja está aberta
                      </p>
                    </div>
                    <div className="flex-shrink-0" style={{ minWidth: '48px', minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Switch
                        id="scheduling-when-open"
                        checked={schedulingSettings.scheduling_when_open}
                        onCheckedChange={(checked) => setSchedulingSettings({ ...schedulingSettings, scheduling_when_open: checked })}
                        data-testid="scheduling-when-open-toggle"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Max days for scheduling */}
              {schedulingSettings.scheduling_enabled && (
                <div className="p-5 bg-white/5 rounded-xl border border-white/10">
                  <Label htmlFor="scheduling-max-days" className="text-white font-semibold text-base block mb-3">
                    Máximo de dias para agendamento
                  </Label>
                  <p className="text-gray-400 text-sm mb-4">
                    Quantos dias no futuro o cliente pode agendar
                  </p>
                  <select
                    id="scheduling-max-days"
                    className="glass-input w-full text-base"
                    style={{ minHeight: '48px', padding: '12px 16px' }}
                    value={schedulingSettings.scheduling_max_days}
                    onChange={(e) => setSchedulingSettings({ ...schedulingSettings, scheduling_max_days: parseInt(e.target.value) })}
                    data-testid="scheduling-max-days-select"
                  >
                    <option value={3}>3 dias</option>
                    <option value={5}>5 dias</option>
                    <option value={7}>7 dias</option>
                    <option value={14}>14 dias</option>
                    <option value={30}>30 dias</option>
                  </select>
                </div>
              )}

              {/* Info box */}
              {schedulingSettings.scheduling_enabled && (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-emerald-300">
                      <p className="font-medium mb-1">Como funciona:</p>
                      <ul className="list-disc list-inside space-y-1 text-emerald-300/80">
                        <li>Quando a loja estiver fechada, o cliente verá opção de agendar</li>
                        <li>Os horários disponíveis seguem o funcionamento configurado acima</li>
                        <li>Os pedidos agendados aparecem com destaque no painel de pedidos</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={handleSaveScheduling}
                disabled={loading}
                className="glass-button-primary w-full sm:w-auto"
                style={{ minHeight: '48px', padding: '12px 32px', fontSize: '16px' }}
                data-testid="scheduling-save-button"
              >
                {loading ? 'Salvando...' : 'Salvar Agendamento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Settings Tab - Mobile optimized */}
      {activeTab === 'account' && (
        <div className="glass-card">
          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-6 sm:mb-8">Configurações da Conta</h2>
          <div className="space-y-6 sm:space-y-8">
            <div>
              <label className="block text-base font-medium text-gray-300 mb-3">
                Email
              </label>
              <input
                type="email"
                className="glass-input w-full text-base sm:text-sm"
                style={{ minHeight: '48px', padding: '12px 16px' }}
                value={accountSettings.email}
                onChange={(e) => setAccountSettings({ ...accountSettings, email: e.target.value })}
                placeholder="seu@email.com"
                data-testid="account-email-input"
              />
            </div>

            <div className="border-t border-white/10 pt-6 sm:pt-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-5 sm:mb-6">Alterar Senha</h3>
              <div className="space-y-5 sm:space-y-6">
                <div>
                  <label className="block text-base font-medium text-gray-300 mb-3">
                    Senha Atual
                  </label>
                  <input
                    type="password"
                    className="glass-input w-full text-base sm:text-sm"
                    style={{ minHeight: '48px', padding: '12px 16px' }}
                    value={accountSettings.current_password}
                    onChange={(e) => setAccountSettings({ ...accountSettings, current_password: e.target.value })}
                    placeholder="Digite sua senha atual"
                    data-testid="account-current-password-input"
                  />
                </div>

                <div>
                  <label className="block text-base font-medium text-gray-300 mb-3">
                    Nova Senha
                  </label>
                  <input
                    type="password"
                    className="glass-input w-full text-base sm:text-sm"
                    style={{ minHeight: '48px', padding: '12px 16px' }}
                    value={accountSettings.new_password}
                    onChange={(e) => setAccountSettings({ ...accountSettings, new_password: e.target.value })}
                    placeholder="Digite a nova senha"
                    data-testid="account-new-password-input"
                  />
                </div>

                <div>
                  <label className="block text-base font-medium text-gray-300 mb-3">
                    Confirmar Nova Senha
                  </label>
                  <input
                    type="password"
                    className="glass-input w-full text-base sm:text-sm"
                    style={{ minHeight: '48px', padding: '12px 16px' }}
                    value={accountSettings.confirm_password}
                    onChange={(e) => setAccountSettings({ ...accountSettings, confirm_password: e.target.value })}
                    placeholder="Confirme a nova senha"
                    data-testid="account-confirm-password-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4 sm:pt-6 border-t border-white/10">
              <button
                onClick={handleSaveAccount}
                disabled={loading}
                className="glass-button-primary w-full sm:w-auto"
                style={{ minHeight: '48px', padding: '12px 32px', fontSize: '16px' }}
                data-testid="account-save-button"
              >
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Appearance Settings Tab - Mobile optimized */}
      {activeTab === 'appearance' && (
        <div className="glass-card">
          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-6 sm:mb-8">Aparência do Menu Público</h2>
          <div className="space-y-6 sm:space-y-8">
            
            {/* Logo Upload Section */}
            <div>
              <label className="block text-base font-semibold text-gray-300 mb-4">
                Logomarca do Restaurante
              </label>
              <div className="bg-white/5 border-2 border-white/10 rounded-xl p-6">
                {logoPreview ? (
                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    <div className="relative w-48 h-48 bg-white rounded-full overflow-hidden flex items-center justify-center shadow-lg">
                      <img 
                        src={logoPreview} 
                        alt="Logo Preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 space-y-4">
                      <p className="text-gray-300 text-sm">Logo atual do seu restaurante (será exibida em formato circular)</p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <label className="glass-button-primary cursor-pointer text-center">
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={handleLogoUpload}
                            className="hidden"
                            disabled={uploadingLogo}
                          />
                          {uploadingLogo ? 'Enviando...' : 'Alterar Logo'}
                        </label>
                        <button
                          onClick={handleRemoveLogo}
                          className="px-6 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-xl font-medium transition-all duration-200 border border-red-500/30"
                          disabled={loading}
                        >
                          Remover Logo
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-24 h-24 mx-auto bg-white/10 rounded-xl flex items-center justify-center mb-4">
                      <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-gray-300 mb-2">Nenhuma logo cadastrada</p>
                    <p className="text-gray-400 text-sm mb-6">Adicione a logo do seu restaurante para aparecer no menu público</p>
                    <label className="glass-button-primary cursor-pointer inline-block">
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={uploadingLogo}
                      />
                      {uploadingLogo ? 'Enviando...' : 'Enviar Logo'}
                    </label>
                    <p className="text-gray-500 text-xs mt-4">JPG, PNG ou WebP - Máximo 5MB</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-base font-semibold text-gray-300 mb-4">
                Layout do Menu
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => setAppearanceSettings({ ...appearanceSettings, layout: 'list' })}
                  className={`p-5 sm:p-6 rounded-xl border-2 transition-all duration-200 active:scale-95 ${
                    appearanceSettings.layout === 'list'
                      ? 'border-emerald-500 bg-emerald-500/20 shadow-lg'
                      : 'border-white/10 bg-white/5 hover:border-white/20 active:bg-white/10'
                  }`}
                  style={{ minHeight: '120px' }}
                  data-testid="appearance-layout-list"
                >
                  <div className="text-left">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-white/10 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 bg-white/20 rounded-full w-3/4" />
                        <div className="h-2 bg-white/10 rounded-full w-1/2" />
                      </div>
                    </div>
                    <p className="text-white font-semibold text-base mb-1">Layout em Lista</p>
                    <p className="text-gray-400 text-sm">Produtos em lista vertical</p>
                  </div>
                </button>

                <button
                  onClick={() => setAppearanceSettings({ ...appearanceSettings, layout: 'grid' })}
                  className={`p-5 sm:p-6 rounded-xl border-2 transition-all duration-200 active:scale-95 ${
                    appearanceSettings.layout === 'grid'
                      ? 'border-emerald-500 bg-emerald-500/20 shadow-lg'
                      : 'border-white/10 bg-white/5 hover:border-white/20 active:bg-white/10'
                  }`}
                  style={{ minHeight: '120px' }}
                  data-testid="appearance-layout-grid"
                >
                  <div className="text-left">
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="aspect-square bg-white/10 rounded-lg" />
                      ))}
                    </div>
                    <p className="text-white font-semibold text-base mb-1">Layout em Grade</p>
                    <p className="text-gray-400 text-sm">Produtos em grade</p>
                  </div>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
              <div>
                <label className="block text-base font-semibold text-gray-300 mb-4">
                  Cor Primária
                </label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-4">
                    <input
                      type="color"
                      className="w-16 h-16 sm:w-14 sm:h-14 rounded-xl cursor-pointer border-2 border-white/10 hover:border-white/20 transition-all"
                      value={appearanceSettings.primary_color}
                      onChange={(e) => setAppearanceSettings({ ...appearanceSettings, primary_color: e.target.value })}
                      data-testid="appearance-primary-color"
                    />
                    <input
                      type="text"
                      className="glass-input flex-1 text-base sm:text-sm"
                      style={{ minHeight: '48px', padding: '12px 16px' }}
                      value={appearanceSettings.primary_color}
                      onChange={(e) => setAppearanceSettings({ ...appearanceSettings, primary_color: e.target.value })}
                      placeholder="#2ECC71"
                    />
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed">Cor do header e botões principais</p>
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold text-gray-300 mb-4">
                  Cor Secundária
                </label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-4">
                    <input
                      type="color"
                      className="w-16 h-16 sm:w-14 sm:h-14 rounded-xl cursor-pointer border-2 border-white/10 hover:border-white/20 transition-all"
                      value={appearanceSettings.secondary_color}
                      onChange={(e) => setAppearanceSettings({ ...appearanceSettings, secondary_color: e.target.value })}
                      data-testid="appearance-secondary-color"
                    />
                    <input
                      type="text"
                      className="glass-input flex-1 text-base sm:text-sm"
                      style={{ minHeight: '48px', padding: '12px 16px' }}
                      value={appearanceSettings.secondary_color}
                      onChange={(e) => setAppearanceSettings({ ...appearanceSettings, secondary_color: e.target.value })}
                      placeholder="#ef4444"
                    />
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed">Cor de destaques e badges</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4 sm:pt-6 border-t border-white/10">
              <button
                onClick={handleSaveAppearance}
                disabled={loading}
                className="glass-button-primary w-full sm:w-auto"
                style={{ minHeight: '48px', padding: '12px 32px', fontSize: '16px' }}
                data-testid="appearance-save-button"
              >
                {loading ? 'Salvando...' : 'Salvar Aparência'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Methods Tab - Mobile optimized com cards */}
      {activeTab === 'payment' && (
        <div className="glass-card">
          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-6 sm:mb-8">Formas de Pagamento Aceitas</h2>
          <div className="space-y-4">
            {Object.entries({
              dinheiro: { label: 'Dinheiro', emoji: '💵' },
              cartao: { label: 'Cartão de Crédito/Débito', emoji: '💳' },
              pix: { label: 'PIX (Manual)', emoji: '🔷' },
              pix_mp: { label: 'PIX Automático (Mercado Pago)', emoji: '🚀' },
              vale_refeicao: { label: 'Vale Refeição', emoji: '🍽️' },
              vale_alimentacao: { label: 'Vale Alimentação', emoji: '🛒' }
            }).map(([key, { label, emoji }]) => (
              <div 
                key={key} 
                className={`flex items-center justify-between p-5 sm:p-6 rounded-xl border-2 transition-all duration-200 ${
                  paymentMethods[key]
                    ? 'bg-white/5 border-white/10 hover:border-white/20'
                    : 'bg-gray-800/30 border-gray-800/50'
                }`}
                style={{ minHeight: '80px' }}
              >
                <Label htmlFor={`payment-${key}`} className="flex items-center space-x-4 flex-1 cursor-pointer">
                  <div className="w-12 h-12 sm:w-10 sm:h-10 flex items-center justify-center bg-white/10 rounded-xl text-2xl">
                    {emoji}
                  </div>
                  <div className="flex-1">
                    <span className={`font-semibold text-base sm:text-lg block ${
                      paymentMethods[key] ? 'text-white' : 'text-gray-500'
                    }`}>
                      {label}
                    </span>
                    <span className={`text-sm ${
                      paymentMethods[key] ? 'text-green-400' : 'text-gray-600'
                    }`}>
                      {paymentMethods[key] ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                </Label>
                <div style={{ minWidth: '48px', minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Switch
                    id={`payment-${key}`}
                    checked={paymentMethods[key]}
                    onCheckedChange={(checked) => setPaymentMethods({ ...paymentMethods, [key]: checked })}
                    data-testid={`payment-${key}-toggle`}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Campo para Chave PIX - aparece quando PIX manual está ativo */}
          {paymentMethods.pix && (
            <div className="mt-6 p-5 bg-purple-500/10 border border-purple-500/30 rounded-xl">
              <div className="flex items-start space-x-3 mb-4">
                <div className="text-purple-400 text-xl mt-0.5">🔑</div>
                <div className="flex-1">
                  <p className="text-purple-400 text-sm font-medium mb-1">Chave PIX</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Cadastre sua chave PIX para que os clientes possam copiar e fazer o pagamento.
                  </p>
                </div>
              </div>
              <input
                type="text"
                value={paymentMethods.pix_key || ''}
                onChange={(e) => setPaymentMethods({ ...paymentMethods, pix_key: e.target.value })}
                placeholder="CPF, CNPJ, E-mail, Telefone ou Chave Aleatória"
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="pix-key-input"
              />
              <p className="text-gray-500 text-xs mt-2">
                Exemplo: 12345678901 (CPF) | email@exemplo.com | (11) 99999-9999
              </p>
            </div>
          )}

          {/* Bandeiras de Cartão - aparece quando Cartão está ativo */}
          {paymentMethods.cartao && (
            <div className="mt-6 p-5 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
              <div className="flex items-start space-x-3 mb-4">
                <div className="text-emerald-400 text-xl mt-0.5">💳</div>
                <div className="flex-1">
                  <p className="text-emerald-400 text-sm font-medium mb-1">Bandeiras de Cartão Aceitas</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Selecione as bandeiras de cartão que você aceita (opcional)
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {bandeirasCartao.map((bandeira) => (
                  <button
                    key={bandeira.id}
                    type="button"
                    onClick={() => {
                      const current = paymentMethods.cartao_bandeiras || [];
                      const updated = current.includes(bandeira.id)
                        ? current.filter(b => b !== bandeira.id)
                        : [...current, bandeira.id];
                      setPaymentMethods({ ...paymentMethods, cartao_bandeiras: updated });
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      (paymentMethods.cartao_bandeiras || []).includes(bandeira.id)
                        ? 'bg-emerald-500 text-white'
                        : 'bg-white/10 text-gray-400 hover:bg-white/20'
                    }`}
                  >
                    {bandeira.nome}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Bandeiras de Vale Refeição - aparece quando Vale Refeição está ativo */}
          {paymentMethods.vale_refeicao && (
            <div className="mt-6 p-5 bg-orange-500/10 border border-orange-500/30 rounded-xl">
              <div className="flex items-start space-x-3 mb-4">
                <div className="text-orange-400 text-xl mt-0.5">🍽️</div>
                <div className="flex-1">
                  <p className="text-orange-400 text-sm font-medium mb-1">Bandeiras de Vale Refeição Aceitas</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Selecione as bandeiras de vale refeição que você aceita (opcional)
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {bandeirasVale.map((bandeira) => (
                  <button
                    key={bandeira.id}
                    type="button"
                    onClick={() => {
                      const current = paymentMethods.vale_refeicao_bandeiras || [];
                      const updated = current.includes(bandeira.id)
                        ? current.filter(b => b !== bandeira.id)
                        : [...current, bandeira.id];
                      setPaymentMethods({ ...paymentMethods, vale_refeicao_bandeiras: updated });
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      (paymentMethods.vale_refeicao_bandeiras || []).includes(bandeira.id)
                        ? 'bg-orange-500 text-white'
                        : 'bg-white/10 text-gray-400 hover:bg-white/20'
                    }`}
                  >
                    {bandeira.nome}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Bandeiras de Vale Alimentação - aparece quando Vale Alimentação está ativo */}
          {paymentMethods.vale_alimentacao && (
            <div className="mt-6 p-5 bg-green-500/10 border border-green-500/30 rounded-xl">
              <div className="flex items-start space-x-3 mb-4">
                <div className="text-green-400 text-xl mt-0.5">🛒</div>
                <div className="flex-1">
                  <p className="text-green-400 text-sm font-medium mb-1">Bandeiras de Vale Alimentação Aceitas</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Selecione as bandeiras de vale alimentação que você aceita (opcional)
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {bandeirasVale.map((bandeira) => (
                  <button
                    key={bandeira.id}
                    type="button"
                    onClick={() => {
                      const current = paymentMethods.vale_alimentacao_bandeiras || [];
                      const updated = current.includes(bandeira.id)
                        ? current.filter(b => b !== bandeira.id)
                        : [...current, bandeira.id];
                      setPaymentMethods({ ...paymentMethods, vale_alimentacao_bandeiras: updated });
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      (paymentMethods.vale_alimentacao_bandeiras || []).includes(bandeira.id)
                        ? 'bg-green-500 text-white'
                        : 'bg-white/10 text-gray-400 hover:bg-white/20'
                    }`}
                  >
                    {bandeira.nome}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 p-5 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
            <div className="flex items-start space-x-3">
              <div className="text-emerald-400 text-xl mt-0.5">ℹ️</div>
              <div className="flex-1">
                <p className="text-emerald-400 text-sm font-medium mb-1">Dica Importante</p>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Os clientes verão apenas as formas de pagamento ativas durante o checkout. Certifique-se de ativar pelo menos uma forma de pagamento.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-8 pt-6 border-t border-white/10">
            <button
              onClick={handleSavePayment}
              disabled={loading}
              className="glass-button-primary w-full sm:w-auto"
              style={{ minHeight: '48px', padding: '12px 32px', fontSize: '16px' }}
              data-testid="payment-save-button"
            >
              {loading ? 'Salvando...' : 'Salvar Formas de Pagamento'}
            </button>
          </div>

          {/* Mercado Pago Configuration Section */}
          <div className="mt-8 pt-8 border-t border-white/10">
            <h3 className="text-xl font-semibold text-white mb-4">Configuração Mercado Pago</h3>
            <p className="text-gray-400 text-sm mb-6">
              Configure seu Access Token do Mercado Pago para receber pagamentos PIX diretamente na sua conta
            </p>
            
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 mb-6">
              <div className="flex items-start space-x-3">
                <div className="text-emerald-400 text-xl mt-0.5">ℹ️</div>
                <div className="flex-1 text-sm text-gray-300">
                  <p className="font-semibold text-emerald-400 mb-2">Como obter seu Access Token:</p>
                  <ol className="space-y-1 ml-4 list-decimal">
                    <li>Acesse <a href="https://www.mercadopago.com.br/developers" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">Mercado Pago Developers</a></li>
                    <li>Entre na sua conta</li>
                    <li>Vá em "Suas integrações" &gt; "Credenciais"</li>
                    <li>Copie seu "Access Token" de produção</li>
                    <li>Cole abaixo para começar a receber pagamentos</li>
                  </ol>
                </div>
              </div>
            </div>

            <MercadoPagoSettings />
          </div>
        </div>
      )}

      {/* Impressão Automática Tab */}
      {activeTab === 'impression' && (
        <div className="glass-card">
          <div className="mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">Impressão Automática de Pedidos</h2>
            <p className="text-gray-400 text-sm">
              Configure a impressão automática para receber pedidos diretamente na sua impressora térmica
            </p>
          </div>

          {/* Status Card */}
          <div className="mb-6 p-5 bg-gradient-to-br from-purple-500/10 to-emerald-500/10 border border-white/10 rounded-xl">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${
                  impressionSettings.active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                }`}>
                  <Printer className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-white font-semibold text-lg">Status:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      impressionSettings.active 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                    }`}>
                      {impressionSettings.active ? '✓ Ativo' : '○ Inativo'}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    {impressionSettings.configured 
                      ? 'Token configurado e pronto para uso' 
                      : 'Configure um token para começar'}
                  </p>
                </div>
              </div>
              {impressionSettings.configured && (
                <Switch
                  id="impression-active"
                  checked={impressionSettings.active}
                  onCheckedChange={handleToggleImpression}
                  className="scale-125"
                  data-testid="impression-toggle"
                />
              )}
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <button
              onClick={handleGenerateToken}
              disabled={generatingToken}
              className="flex items-center justify-center space-x-2 p-4 bg-gradient-to-r from-emerald-500 to-purple-600 hover:from-emerald-600 hover:to-purple-700 text-white rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="generate-token-button"
            >
              <Zap className="w-5 h-5" />
              <span>{generatingToken ? 'Gerando...' : impressionSettings.configured ? 'Gerar Novo Token' : 'Gerar Token de Impressão'}</span>
            </button>

            <button
              onClick={handleDownloadConfig}
              disabled={!impressionSettings.configured || downloadingConfig}
              className="flex items-center justify-center space-x-2 p-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="download-config-button"
            >
              <Download className="w-5 h-5" />
              <span>{downloadingConfig ? 'Baixando...' : 'Baixar login.json'}</span>
            </button>
          </div>

          {/* Token Display */}
          {impressionSettings.token && (
            <div className="mb-8 p-5 bg-white/5 border border-white/10 rounded-xl">
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Token de Impressão
              </label>
              <div className="flex items-center space-x-2 bg-gray-900/50 border border-white/10 rounded-lg p-3">
                <code className="flex-1 text-white font-mono text-sm break-all">
                  {impressionSettings.token.substring(0, 12)}...{impressionSettings.token.substring(impressionSettings.token.length - 6)}
                </code>
                <button
                  onClick={copyTokenToClipboard}
                  className="flex-shrink-0 p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Copiar token"
                  data-testid="copy-token-button"
                >
                  {tokenCopied ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <Copy className="w-5 h-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Instruções de Uso */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Como Configurar a Impressão Automática</h3>
            
            <div className="space-y-3">
              <div className="flex items-start space-x-3 p-4 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-emerald-500/20 text-emerald-400 rounded-full font-bold">
                  1
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">Gere o Token de Impressão</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Clique no botão "Gerar Token de Impressão" acima para criar um token único para seu restaurante
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-green-500/20 text-green-400 rounded-full font-bold">
                  2
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">Baixe o Arquivo de Configuração</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Clique em "Baixar login.json" para obter o arquivo de configuração
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-purple-500/20 text-purple-400 rounded-full font-bold">
                  3
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">Configure o Executável</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Coloque o arquivo login.json na mesma pasta do executável de impressão (.exe)
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-orange-500/20 text-orange-400 rounded-full font-bold">
                  4
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">Execute e Ative</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Execute o programa de impressão e ative a impressão automática usando o toggle acima
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Informações Importantes */}
          <div className="mt-8 p-5 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
            <div className="flex items-start space-x-3">
              <div className="text-yellow-400 text-xl mt-0.5">⚠️</div>
              <div className="flex-1">
                <p className="text-yellow-400 text-sm font-medium mb-1">Informações Importantes</p>
                <ul className="text-gray-300 text-sm space-y-1 leading-relaxed">
                  <li>• O executável verifica novos pedidos a cada 5 segundos</li>
                  <li>• Apenas pedidos com status "Preparando" são impressos automaticamente</li>
                  <li>• Após impressão, o pedido é marcado como "Impresso" no sistema</li>
                  <li>• Mantenha o executável rodando enquanto quiser receber impressões automáticas</li>
                  <li>• Certifique-se de que sua impressora térmica está conectada e configurada</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// WhatsApp Content Component
const WhatsAppContent = () => {
  const [activeTab, setActiveTab] = useState('connection');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testMessage, setTestMessage] = useState({ number: '', text: 'Teste de mensagem do Meucardapiozap! 🚀' });
  const [sendingTest, setSendingTest] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  
  // AI Config states
  const [aiConfig, setAiConfig] = useState({
    ai_provider: 'meucardapiozap',
    ai_api_key: '',
    ai_model: 'meucardapiozap-default',
    personality_prompt: `Lembre-se sempre que antes da primeira mensagem que você enviar o cliente já recebeu a mensagem padrão de saudação, então sua primeira mensagem deve ser algo simples como: "se precisar de ajuda, estou aqui!"

Seja extremamente objetiva nas respostas. Evite textos longos, sua função é responder dúvidas de forma clara e objetiva.

Caso o cliente não consiga fazer o pedido pelo cardápio digital, você tem autorização pra recolher pedidos no whatsapp. Depois do cliente responder, recolha pedido. Depois do cliente responder, endereço de entrega. E depois do cliente responder a última dúvida, pergunte forma de pagamento, e por último peça para que o cliente confirme o pedido e salve no sistema interno.

Converse como uma pessoa normal, de forma clara e curta. Cada pergunta deve ter no máximo 10 palavras, não pergunte tudo de uma única vez. Fale como uma pessoa, nada de robotizado.

Não pergunte quantos produtos o cliente deseja. Tente agir como um humano desleixado agiria.`,
    enabled: true,
    menu_image_url: '',
    menu_images: [],  // Array de até 3 imagens
    send_menu_images_enabled: true,  // Toggle para habilitar/desabilitar envio
    response_delay: 15  // Delay de resposta em segundos (padrão: 15s)
  });
  const [savedAiConfig, setSavedAiConfig] = useState(null);
  const [savingAi, setSavingAi] = useState(false);
  const [showAiKey, setShowAiKey] = useState(false);
  const [reloadingAi, setReloadingAi] = useState(false);
  const [uploadingMenuImage, setUploadingMenuImage] = useState(false);

  useEffect(() => {
    loadStatus();
    loadAiConfig();
    
    // Cleanup quando sair da aba WhatsApp
    return () => {
      // Parar o polling
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      // Nota: O QR code será limpo automaticamente pois o componente é desmontado
      // Quando voltar, o loadStatus() será chamado novamente
    };
  }, []);
  
  // Limpar QR code quando mudar de sub-aba (connection -> outras)
  useEffect(() => {
    if (activeTab !== 'connection') {
      // Usuário saiu da aba de conexão, limpar QR code
      setQrCode(null);
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
  }, [activeTab]);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/whatsapp/status`);
      setStatus(response.data);
      
      // NÃO carregar QR code automaticamente do loadStatus
      // O QR code só deve ser mostrado quando o usuário clicar em "Conectar" ou "Reconectar"
      // Isso evita mostrar QR codes antigos/desatualizados
      
      // Se conectou, parar polling
      if (response.data.connected && pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    } catch (error) {
      console.error('Error loading status:', error);
      setStatus({ connected: false, status: 'disconnected' });
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    // Parar polling anterior se existir
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    
    console.log('🔄 Iniciando polling de status...');
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/whatsapp/status`);
        console.log('📊 Status polling:', response.data);
        setStatus(response.data);
        
        // Se recebemos um QR code na resposta de status, atualizar o estado
        if (response.data.status === 'qrcode' && response.data.qr_code) {
          setQrCode(prevQrCode => {
            // Atualizar se não temos QR code ou se é diferente
            if (!prevQrCode || prevQrCode !== response.data.qr_code) {
              console.log('📱 QR Code recebido/atualizado via polling');
              return response.data.qr_code;
            }
            return prevQrCode;
          });
        }
        
        if (response.data.connected) {
          console.log('✅ WhatsApp conectado! Parando polling.');
          clearInterval(interval);
          setPollingInterval(null);
          setQrCode(null);
          toast.success('✅ WhatsApp conectado com sucesso!');
        }
      } catch (error) {
        console.error('❌ Polling error:', error);
      }
    }, 1500); // Check every 1.5 seconds for faster response
    
    setPollingInterval(interval);
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setQrCode(null); // Limpar QR code anterior
      console.log('🔄 Conectando WhatsApp...');
      const response = await axios.post(`${API}/whatsapp/connect`);
      console.log('✅ Resposta recebida:', response.data);
      
      if (response.data.qr_code) {
        console.log('✅ QR Code encontrado, definindo estado...');
        setQrCode(response.data.qr_code);
        setStatus({ connected: false, status: 'qrcode' });
        toast.success('QR Code gerado! Escaneie com o WhatsApp');
        startPolling();
      } else {
        // QR code pode vir via webhook, iniciar polling para buscar
        console.warn('⚠️ QR Code não veio na resposta, iniciando polling...');
        setStatus({ connected: false, status: 'qrcode' });
        startPolling();
        toast.info('Aguarde o QR Code ser gerado...');
      }
    } catch (error) {
      console.error('❌ Error connecting:', error);
      toast.error(error.response?.data?.detail || 'Erro ao conectar WhatsApp');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Deseja realmente desconectar o WhatsApp?')) return;
    
    try {
      setDisconnecting(true);
      await axios.delete(`${API}/whatsapp/disconnect`);
      
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
      
      setQrCode(null);
      setStatus({ connected: false, status: 'disconnected' });
      toast.success('WhatsApp desconectado com sucesso');
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Erro ao desconectar');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSendTest = async () => {
    if (!testMessage.number || !testMessage.text) {
      toast.error('Preencha o número e a mensagem');
      return;
    }
    
    try {
      setSendingTest(true);
      await axios.post(`${API}/whatsapp/send-test`, testMessage);
      toast.success('✅ Mensagem enviada com sucesso!');
      setTestMessage({ number: '', text: 'Teste de mensagem do Meucardapiozap! 🚀' });
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setSendingTest(false);
    }
  };

  const handleReconnect = async () => {
    try {
      setConnecting(true);
      setQrCode(null); // Limpar QR code anterior
      console.log('🔄 Reconectando WhatsApp...');
      const response = await axios.post(`${API}/whatsapp/reconnect`);
      console.log('✅ Resposta de reconexão:', response.data);
      
      if (response.data.qr_code) {
        console.log('✅ Novo QR Code gerado');
        setQrCode(response.data.qr_code);
        setStatus({ connected: false, status: 'qrcode' });
        toast.success('Novo QR Code gerado! Escaneie para reconectar');
        startPolling();
      } else {
        // QR code pode vir via webhook, iniciar polling para buscar
        console.warn('⚠️ QR Code não veio na resposta, iniciando polling...');
        setStatus({ connected: false, status: 'qrcode' });
        startPolling();
        toast.info('Aguarde o QR Code ser gerado...');
      }
    } catch (error) {
      console.error('❌ Error reconnecting:', error);
      toast.error(error.response?.data?.detail || 'Erro ao reconectar WhatsApp');
    } finally {
      setConnecting(false);
    }
  };



  // AI Configuration functions
  const loadAiConfig = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/ai-config`);
      setSavedAiConfig(response.data);
      
      if (response.data.configured) {
        setAiConfig(prev => ({
          ...prev,
          ai_provider: response.data.ai_provider,
          ai_model: response.data.ai_model,
          personality_prompt: response.data.personality_prompt,
          enabled: response.data.enabled,
          menu_image_url: response.data.menu_image_url || '',
          menu_images: response.data.menu_images || [],
          send_menu_images_enabled: response.data.send_menu_images_enabled !== false,
          response_delay: response.data.response_delay ?? 15
        }));
      }
    } catch (error) {
      console.error('Error loading AI config:', error);
    }
  };

  const handleSaveAiConfig = async () => {
    try {
      setSavingAi(true);
      const payload = {
        ai_provider: aiConfig.ai_provider,
        ai_model: aiConfig.ai_model,
        personality_prompt: aiConfig.personality_prompt,
        enabled: aiConfig.enabled,
        menu_image_url: aiConfig.menu_images?.length > 0 ? aiConfig.menu_images[0] : null,  // Legacy: null se não tem imagens
        menu_images: aiConfig.menu_images || [],  // Novo: array de imagens (pode ser vazio)
        send_menu_images_enabled: aiConfig.send_menu_images_enabled !== false,  // Toggle para envio
        response_delay: aiConfig.response_delay ?? 15  // Delay de resposta em segundos (0 = instantâneo)
      };
      
      // Include API key only if provided
      if (aiConfig.ai_api_key && aiConfig.ai_api_key.trim() !== '') {
        payload.ai_api_key = aiConfig.ai_api_key;
      } else if (savedAiConfig?.ai_api_key_masked) {
        // If already has a key saved, don't overwrite
        payload.ai_api_key = savedAiConfig.ai_api_key_masked.includes('*') ? undefined : savedAiConfig.ai_api_key_masked;
      }
      
      // Remove undefined fields
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
      
      await axios.post(`${API}/whatsapp/ai-config`, payload);
      toast.success('✅ Configurações de IA salvas com sucesso!');
      await loadAiConfig();
      setAiConfig(prev => ({ ...prev, ai_api_key: '' }));
      setShowAiKey(false);
    } catch (error) {
      console.error('Error saving AI config:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSavingAi(false);
    }
  };

  const handleReloadAi = async (clearHistory = false) => {
    const confirmMessage = clearHistory
      ? '🔄 Isso irá reiniciar a IA e limpar TODO o histórico de conversas. Deseja continuar?'
      : '🔄 Isso irá reiniciar a IA para aplicar as novas configurações. Deseja continuar?';
    
    if (!window.confirm(confirmMessage)) return;

    try {
      setReloadingAi(true);
      const response = await axios.post(`${API}/whatsapp/ai-reload?clear_history=${clearHistory}`);
      
      if (clearHistory) {
        toast.success('✅ IA reiniciada e histórico limpo!');
      } else {
        toast.success('✅ IA atualizada! Mudanças aplicadas.');
      }
      
      console.log('AI Reload Stats:', response.data.stats);
    } catch (error) {
      console.error('Error reloading AI:', error);
      toast.error(error.response?.data?.detail || 'Erro ao reiniciar IA');
    } finally {
      setReloadingAi(false);
    }
  };


  const aiProviders = [
    { 
      value: 'meucardapiozap', 
      label: '🚀 Meucardapiozap AI (Recomendado)', 
      models: ['gpt-4o-mini'],
      isGlobal: true  // Flag para indicar que usa chave global do sistema
    },
    { 
      value: 'emergent_custom', 
      label: '🔑 Emergent (Chave Própria)', 
      models: ['gpt-4o-mini', 'gpt-4o', 'gpt-4-turbo'],
      isEmergentCustom: true  // Flag para indicar que usa chave Emergent do lojista
    },
    { 
      value: 'gemini', 
      label: '🤖 Gemini (Google)', 
      models: ['gemini-2.0-flash-exp', 'gemini-1.5-pro', 'gemini-1.5-flash']
    },
    { 
      value: 'openai', 
      label: '⚡ OpenAI (Direta)', 
      models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo']
    },
    { 
      value: 'deepseek', 
      label: '🧠 DeepSeek', 
      models: ['deepseek-chat', 'deepseek-coder']
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">WhatsApp - Evolution API</h1>
        <p className="text-gray-400 mt-1">Conecte seu WhatsApp e configure a IA</p>
      </div>

      {/* Tabs - Layout responsivo para mobile */}
      <div className="glass-card p-2 sm:p-0">
        {/* Mobile: Grid 2x2 */}
        <div className="grid grid-cols-2 gap-2 sm:hidden">
          <button
            onClick={() => setActiveTab('connection')}
            className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
              activeTab === 'connection'
                ? 'bg-green-500/20 border-2 border-green-500 text-green-400'
                : 'bg-gray-800/50 border-2 border-transparent text-gray-400 hover:bg-gray-700/50'
            }`}
          >
            <MessageSquare size={28} className="mb-2" />
            <span className="text-xs font-medium text-center">Conexão</span>
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
              activeTab === 'ai'
                ? 'bg-purple-500/20 border-2 border-purple-500 text-purple-400'
                : 'bg-gray-800/50 border-2 border-transparent text-gray-400 hover:bg-gray-700/50'
            }`}
          >
            <Zap size={28} className="mb-2" />
            <span className="text-xs font-medium text-center">Config. IA</span>
          </button>
          <button
            onClick={() => setActiveTab('greeting')}
            className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
              activeTab === 'greeting'
                ? 'bg-emerald-500/20 border-2 border-emerald-500 text-emerald-400'
                : 'bg-gray-800/50 border-2 border-transparent text-gray-400 hover:bg-gray-700/50'
            }`}
          >
            <MessageSquare size={28} className="mb-2" />
            <span className="text-xs font-medium text-center">Saudação</span>
          </button>
          <button
            onClick={() => setActiveTab('away')}
            className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
              activeTab === 'away'
                ? 'bg-amber-500/20 border-2 border-amber-500 text-amber-400'
                : 'bg-gray-800/50 border-2 border-transparent text-gray-400 hover:bg-gray-700/50'
            }`}
          >
            <Moon size={28} className="mb-2" />
            <span className="text-xs font-medium text-center">Ausência</span>
          </button>
        </div>

        {/* Desktop: Tabs horizontais */}
        <div className="hidden sm:flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('connection')}
            className={`flex-1 px-6 py-4 font-medium transition-all ${
              activeTab === 'connection'
                ? 'text-white border-b-2 border-green-500 bg-green-500/10'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <MessageSquare size={20} />
              <span>Conexão WhatsApp</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`flex-1 px-6 py-4 font-medium transition-all ${
              activeTab === 'ai'
                ? 'text-white border-b-2 border-purple-500 bg-purple-500/10'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Zap size={20} />
              <span>Configurar IA</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('greeting')}
            className={`flex-1 px-6 py-4 font-medium transition-all ${
              activeTab === 'greeting'
                ? 'text-white border-b-2 border-emerald-500 bg-emerald-500/10'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <MessageSquare size={20} />
              <span>Saudação</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('away')}
            className={`flex-1 px-6 py-4 font-medium transition-all ${
              activeTab === 'away'
                ? 'text-white border-b-2 border-amber-500 bg-amber-500/10'
                : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Moon size={20} />
              <span>Ausência</span>
            </div>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="glass-card flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Connection Tab */}
          {activeTab === 'connection' && (
            <>
          {/* Status Card */}
          <div className={`glass-card border-2 ${
            status?.connected 
              ? 'border-green-500/30 bg-gradient-to-br from-green-500/10 to-emerald-500/10'
              : 'border-gray-700'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-1">
                  Status da Conexão
                </h3>
                <p className="text-gray-400 text-sm">
                  {status?.connected ? '🟢 WhatsApp conectado e funcionando' : 
                   status?.status === 'qrcode' ? '🟡 Aguardando escaneamento do QR Code' :
                   '🔴 Nenhuma instância conectada'}
                </p>
              </div>
              <div className={`w-4 h-4 rounded-full ${
                status?.connected ? 'bg-green-500 animate-pulse' : 
                status?.status === 'qrcode' ? 'bg-yellow-500 animate-pulse' :
                'bg-gray-600'
              }`} />
            </div>

            {status?.connected ? (
              <div className="space-y-4">
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Instância</p>
                      <p className="text-white font-medium">{status.instance_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Número</p>
                      <p className="text-white font-medium">{status.phone || 'Carregando...'}</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {disconnecting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      Desconectando...
                    </>
                  ) : (
                    <>
                      <XCircle size={20} />
                      Desconectar Instância
                    </>
                  )}
                </button>
              </div>
            ) : (status?.status === 'device_removed' || status?.status === 'disconnected') && !qrCode ? (
              <div className="space-y-4">
                <div className="bg-yellow-500/10 border-2 border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-yellow-400 font-semibold mb-1">WhatsApp Desconectado</h4>
                      <p className="text-gray-300 text-sm">
                        A conexão foi perdida. Isso pode acontecer se o WhatsApp foi desconectado do celular ou por inatividade.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleReconnect}
                  disabled={connecting}
                  className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-bold py-4 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {connecting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      Reconectando...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Reconectar WhatsApp
                    </>
                  )}
                </button>
              </div>
            ) : !status?.connected && !qrCode ? (
              <button
                onClick={handleConnect}
                disabled={connecting}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold py-4 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {connecting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    Conectando...
                  </>
                ) : (
                  <>
                    <MessageSquare size={20} />
                    Conectar WhatsApp
                  </>
                )}
              </button>
            ) : null}
          </div>

          {/* QR Code Card */}
          {qrCode && !status?.connected && (
            <div className="glass-card border-2 border-yellow-500/30 bg-gradient-to-br from-yellow-500/10 to-orange-500/10">
              <h3 className="text-xl font-semibold text-white mb-4">
                📱 Escaneie o QR Code
              </h3>
              
              <div className="bg-white p-6 rounded-xl mx-auto w-fit mb-4">
                <img 
                  key={qrCode.substring(0, 50)}
                  src={qrCode}
                  alt="QR Code"
                  className="w-64 h-64"
                />
              </div>

              <div className="text-center space-y-2">
                <p className="text-yellow-400 font-medium">
                  🔄 Aguardando leitura do QR Code...
                </p>
                <p className="text-gray-300 text-sm">
                  Abra o WhatsApp no seu celular e escaneie este código
                </p>
                <p className="text-gray-400 text-xs">
                  Verificando conexão automaticamente a cada 3 segundos...
                </p>
              </div>
            </div>
          )}

          {/* Test Message Card - Only show when NOT connected (no Evolution API) */}
          {(() => {
            const shouldShow = !status?.connected && status?.status === 'disconnected';
            console.log('🔍 DEBUG - Mensagem Manual:', {
              connected: status?.connected,
              status: status?.status,
              shouldShow: shouldShow,
              fullStatus: status
            });
            return shouldShow;
          })() && (
            <div className="glass-card">
              <h3 className="text-xl font-semibold text-white mb-4">
                📱 Enviar Mensagem Manual
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                ⚠️ WhatsApp não está conectado via API. Use este formulário para enviar mensagens manualmente.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Número (com DDI e DDD)
                  </label>
                  <input
                    type="tel"
                    value={testMessage.number}
                    onChange={(e) => setTestMessage({...testMessage, number: e.target.value.replace(/\D/g, '')})}
                    placeholder="5531999999999"
                    className="glass-input w-full"
                    maxLength="13"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    ⚠️ <strong>Importante:</strong> Digite um número válido e registrado no WhatsApp
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Formato: 5531999999999 (55 = Brasil, 31 = DDD, 999999999 = número com 9 dígitos)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Mensagem
                  </label>
                  <textarea
                    value={testMessage.text}
                    onChange={(e) => setTestMessage({...testMessage, text: e.target.value})}
                    rows={3}
                    className="glass-input w-full resize-none"
                    placeholder="Digite sua mensagem de teste..."
                  />
                </div>

                <button
                  onClick={handleSendTest}
                  disabled={sendingTest || !testMessage.number || !testMessage.text}
                  className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-bold py-3 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sendingTest ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <MessageSquare size={20} />
                      Enviar Mensagem
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="glass-card">
            <h3 className="text-lg font-semibold text-white mb-3">📋 Como Conectar</h3>
            <ol className="space-y-2 text-gray-300">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                <span>Clique em "Conectar WhatsApp"</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <span>Aguarde o QR Code ser gerado</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <span>Abra o WhatsApp no seu celular → Configurações → Aparelhos conectados → Conectar um aparelho</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-sm font-bold">4</span>
                <span>Escaneie o QR Code exibido</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-sm font-bold">5</span>
                <span>Aguarde a confirmação da conexão (automática)</span>
              </li>
            </ol>
          </div>

          {/* Info Card */}
          <div className="glass-card bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border-2 border-emerald-500/30">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">💡 Informações Importantes</h4>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Cada loja pode ter apenas 1 instância ativa</li>
                  <li>• A conexão é gerenciada via Evolution API</li>
                  <li>• O nome da instância é gerado automaticamente</li>
                  <li>• Use a mensagem de teste para verificar o funcionamento</li>
                </ul>
              </div>
            </div>
          </div>
          </>
          )}

          {/* AI Configuration Tab */}
          {activeTab === 'ai' && (
            <div className="space-y-6">
              {/* AI Status */}
              {savedAiConfig?.configured && (
                <div className="glass-card border-2 border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-pink-500/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white">IA Configurada</h3>
                      <p className="text-purple-300 text-sm">
                        Provider: <span className="font-semibold">{savedAiConfig.ai_provider}</span>
                        {savedAiConfig.ai_model && ` • Modelo: ${savedAiConfig.ai_model}`}
                      </p>
                    </div>
                    {savedAiConfig.enabled && (
                      <div className="flex items-center gap-2 bg-green-500/20 px-3 py-1 rounded-full">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        <span className="text-green-400 text-sm font-medium">Ativo</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* AI Configuration Form */}
              <div className="glass-card space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-1">Configurar IA para Pedidos</h3>
                  <p className="text-gray-400 text-sm">Configure qual IA irá processar os pedidos via WhatsApp</p>
                </div>

                {/* Provider Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Provider de IA
                  </label>
                  <select
                    value={aiConfig.ai_provider}
                    onChange={(e) => {
                      const provider = aiProviders.find(p => p.value === e.target.value);
                      setAiConfig({
                        ...aiConfig,
                        ai_provider: e.target.value,
                        ai_model: provider?.models[0] || ''
                      });
                    }}
                    className="glass-input w-full"
                  >
                    {aiProviders.map(provider => (
                      <option key={provider.value} value={provider.value}>
                        {provider.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Model Selection - mostrar apenas para providers que precisam */}
                {aiConfig.ai_provider !== 'meucardapiozap' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Modelo
                    </label>
                    <select
                      value={aiConfig.ai_model}
                      onChange={(e) => setAiConfig({...aiConfig, ai_model: e.target.value})}
                      className="glass-input w-full"
                    >
                      {aiProviders.find(p => p.value === aiConfig.ai_provider)?.models.map(model => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                    </select>
                    {aiConfig.ai_provider === 'emergent_custom' && (
                      <p className="text-xs text-purple-400 mt-1">
                        💡 GPT-4o mini é recomendado por ser rápido e econômico
                      </p>
                    )}
                  </div>
                )}

                {/* API Key - Condicional baseado no provider */}
                {aiConfig.ai_provider === 'meucardapiozap' ? (
                  /* Se for Meucardapiozap AI, mostra banner de sucesso sem campo de API */
                  <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <label className="text-sm font-medium text-green-300">
                        ✅ Meucardapiozap AI Ativado
                      </label>
                    </div>
                    
                    <div className="text-xs text-green-200 bg-green-900/30 rounded p-3">
                      <strong>🚀 Você está usando Meucardapiozap AI!</strong><br/><br/>
                      • IA pronta para uso com GPT-4o mini<br/>
                      • Tecnologia de ponta para atendimento ao cliente<br/>
                      • Processamento rápido e inteligente de pedidos<br/><br/>
                      <span className="text-green-300">Nenhuma API Key necessária - tudo já está configurado!</span>
                    </div>
                  </div>
                ) : aiConfig.ai_provider === 'emergent_custom' ? (
                  /* Se for Emergent com chave própria */
                  <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Key className="w-5 h-5 text-purple-400" />
                      <label className="text-sm font-medium text-purple-300">
                        🔑 Chave Universal Emergent {savedAiConfig?.configured && savedAiConfig.ai_provider === 'emergent_custom' && '(Configurada)'}
                      </label>
                    </div>
                    
                    <div className="mb-3 text-xs text-purple-200 bg-purple-900/30 rounded p-2">
                      <strong>Use sua própria Chave Universal Emergent</strong><br/>
                      Esta opção permite usar sua conta Emergent pessoal com GPT-4o mini.<br/>
                      <span className="text-purple-300">💡 Obtenha sua chave em: Perfil → Chave Universal</span>
                    </div>
                    
                    <div className="relative">
                      {savedAiConfig?.configured && savedAiConfig.ai_provider === 'emergent_custom' && !showAiKey ? (
                        <div className="flex gap-2">
                          <div className="flex-1 bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2">
                            <span className="text-gray-400 text-sm font-mono">
                              {savedAiConfig.ai_api_key_masked || '••••••••••••'}
                            </span>
                          </div>
                          <button
                            onClick={() => setShowAiKey(true)}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all text-sm"
                          >
                            Alterar Key
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="password"
                            value={aiConfig.ai_api_key}
                            onChange={(e) => setAiConfig({...aiConfig, ai_api_key: e.target.value})}
                            placeholder="Cole sua Chave Universal Emergent (sk-emergent-...)"
                            className="glass-input w-full"
                            required
                          />
                          {showAiKey && (
                            <button
                              onClick={() => {
                                setShowAiKey(false);
                                setAiConfig(prev => ({ ...prev, ai_api_key: '', ai_provider: 'meucardapiozap', ai_model: 'gpt-4o-mini' }));
                              }}
                              className="mt-2 text-xs text-green-400 hover:text-green-300"
                            >
                              ← Voltar para Meucardapiozap AI (sem API Key)
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Se for outro provider, mostra campo para API Key */
                  <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Key className="w-5 h-5 text-yellow-400" />
                      <label className="text-sm font-medium text-yellow-300">
                        API Key {savedAiConfig?.configured && savedAiConfig.ai_provider !== 'meucardapiozap' && '(Configurada)'} - OBRIGATÓRIA
                      </label>
                    </div>
                    
                    <div className="mb-3 text-xs text-yellow-200 bg-yellow-900/30 rounded p-2">
                      ⚠️ <strong>Você selecionou um provider externo</strong><br/>
                      É necessário fornecer sua própria API Key para usar {aiProviders.find(p => p.value === aiConfig.ai_provider)?.label}.<br/>
                      <span className="text-green-300">💡 Dica: Selecione "Meucardapiozap AI" para usar sem API Key!</span>
                    </div>
                    
                    <div className="relative">
                      {savedAiConfig?.configured && savedAiConfig.ai_provider === aiConfig.ai_provider && !showAiKey ? (
                        <div className="flex gap-2">
                          <div className="flex-1 bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2">
                            <span className="text-gray-400 text-sm font-mono">
                              {savedAiConfig.ai_api_key_masked || '••••••••••••'}
                            </span>
                          </div>
                          <button
                            onClick={() => setShowAiKey(true)}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all text-sm"
                          >
                            Alterar Key
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="password"
                            value={aiConfig.ai_api_key}
                            onChange={(e) => setAiConfig({...aiConfig, ai_api_key: e.target.value})}
                            placeholder="Cole sua API Key aqui"
                            className="glass-input w-full"
                            required
                          />
                          {showAiKey && (
                            <button
                              onClick={() => {
                                setShowAiKey(false);
                                setAiConfig(prev => ({ ...prev, ai_api_key: '', ai_provider: 'meucardapiozap', ai_model: 'gpt-4o-mini' }));
                              }}
                              className="mt-2 text-xs text-green-400 hover:text-green-300"
                            >
                              ← Voltar para Meucardapiozap AI (sem API Key)
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Obtenha sua key em: 
                      {aiConfig.ai_provider === 'gemini' && ' ai.google.dev'}
                      {aiConfig.ai_provider === 'openai' && ' platform.openai.com'}
                      {aiConfig.ai_provider === 'deepseek' && ' platform.deepseek.com'}
                    </p>
                  </div>
                )}

                {/* Personality Prompt */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                    🎭 Personalidade da IA
                    <span className="text-xs text-purple-400 bg-purple-900/30 px-2 py-1 rounded">Personalizável</span>
                  </label>
                  <textarea
                    value={aiConfig.personality_prompt}
                    onChange={(e) => setAiConfig({...aiConfig, personality_prompt: e.target.value})}
                    rows={8}
                    className="glass-input w-full resize-none font-mono text-sm"
                    placeholder="Defina como a IA deve se comportar ao atender os clientes..."
                  />
                  <div className="mt-2 space-y-2">
                    <p className="text-xs text-gray-400">
                      💡 <strong>Dica:</strong> A IA terá acesso automático a:
                    </p>
                    <ul className="text-xs text-gray-500 ml-4 space-y-1">
                      <li>✅ Cardápio completo com preços e categorias</li>
                      <li>✅ Horários de funcionamento do restaurante</li>
                      <li>✅ Formas de pagamento aceitas</li>
                      <li>✅ Taxas de entrega por bairro</li>
                      <li>✅ Histórico de pedidos do cliente</li>
                      <li>✅ Link do cardápio público online</li>
                    </ul>
                    <details className="mt-3">
                      <summary className="text-xs text-purple-400 cursor-pointer hover:text-purple-300">
                        📝 Ver exemplos de personalidades
                      </summary>
                      <div className="mt-2 p-3 bg-gray-900/50 rounded-lg text-xs text-gray-400 space-y-2">
                        <div>
                          <strong className="text-green-400">Formal e Profissional:</strong>
                          <p className="mt-1 italic">"Seja educado, formal e extremamente profissional. Use sempre 'senhor' e 'senhora'. Mantenha tom sóbrio e elegante."</p>
                        </div>
                        <div>
                          <strong className="text-emerald-400">Descontraído e Amigável:</strong>
                          <p className="mt-1 italic">"Seja super descontraído e amigável! Use emojis, gírias modernas e deixe o cliente à vontade. Seja como um amigo ajudando a escolher comida 😋"</p>
                        </div>
                        <div>
                          <strong className="text-yellow-400">Especialista Gourmet:</strong>
                          <p className="mt-1 italic">"Você é um especialista em gastronomia. Faça descrições elaboradas dos pratos, sugira harmonizações e destaque ingredientes premium. Eduque o cliente sobre a culinária."</p>
                        </div>
                        <div>
                          <strong className="text-red-400">Vendedor Persuasivo:</strong>
                          <p className="mt-1 italic">"Seja persuasivo e focado em vendas! Sempre sugira combos, upsell produtos adicionais, destaque promoções e crie senso de urgência."</p>
                        </div>
                      </div>
                    </details>
                  </div>
                </div>

                {/* Menu Images Upload - Up to 3 images */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                    <ImageIcon size={16} className="text-green-400" />
                    Imagens do Cardápio (WhatsApp) - Até 3 imagens
                  </label>
                  <p className="text-xs text-gray-500">
                    Quando o cliente pedir o cardápio, estas imagens serão enviadas automaticamente.
                  </p>
                  
                  {/* Grid de imagens existentes */}
                  <div className="grid grid-cols-3 gap-2">
                    {(aiConfig.menu_images || []).map((imageUrl, index) => (
                      <div key={index} className="relative">
                        <img 
                          src={imageUrl} 
                          alt={`Cardápio ${index + 1}`} 
                          className="w-full h-24 object-cover rounded-lg border border-gray-700"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newImages = [...(aiConfig.menu_images || [])];
                            newImages.splice(index, 1);
                            setAiConfig({...aiConfig, menu_images: newImages});
                          }}
                          className="absolute top-1 right-1 p-1 bg-red-500/80 rounded-full hover:bg-red-500"
                        >
                          <X size={12} />
                        </button>
                        <span className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                          {index + 1}
                        </span>
                      </div>
                    ))}
                    
                    {/* Botão de adicionar (se menos de 3 imagens) */}
                    {(aiConfig.menu_images || []).length < 3 && (
                      <div className="border-2 border-dashed border-gray-600 rounded-lg h-24 flex items-center justify-center">
                        <input
                          type="file"
                          accept="image/*"
                          id="menu-image-upload"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            setUploadingMenuImage(true);
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              formData.append('category', 'menu');
                              
                              const response = await axios.post(`${API}/upload`, formData);
                              const currentImages = aiConfig.menu_images || [];
                              setAiConfig({
                                ...aiConfig, 
                                menu_images: [...currentImages, response.data.url]
                              });
                              toast.success(`✅ Imagem ${currentImages.length + 1} carregada!`);
                            } catch (error) {
                              console.error('Error uploading menu image:', error);
                              toast.error('Erro ao fazer upload da imagem');
                            } finally {
                              setUploadingMenuImage(false);
                              e.target.value = '';
                            }
                          }}
                        />
                        <label 
                          htmlFor="menu-image-upload"
                          className="cursor-pointer flex flex-col items-center gap-1 text-gray-400 hover:text-gray-300 p-2"
                        >
                          {uploadingMenuImage ? (
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-400" />
                          ) : (
                            <>
                              <Upload size={20} />
                              <span className="text-xs text-center">
                                {(aiConfig.menu_images || []).length === 0 ? 'Adicionar' : '+'}
                              </span>
                            </>
                          )}
                        </label>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>💡 <strong>Palavras-chave:</strong> "cardápio", "menu", "o que tem"</span>
                    <span>{(aiConfig.menu_images || []).length}/3 imagens</span>
                  </div>
                  
                  {/* Toggle para habilitar/desabilitar envio de imagens */}
                  {(aiConfig.menu_images || []).length > 0 && (
                    <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg mt-2">
                      <div>
                        <p className="text-white text-sm font-medium">Enviar imagens automaticamente</p>
                        <p className="text-gray-400 text-xs">Quando desabilitado, as imagens não serão enviadas mesmo que o cliente peça</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAiConfig({...aiConfig, send_menu_images_enabled: !aiConfig.send_menu_images_enabled})}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          aiConfig.send_menu_images_enabled !== false ? 'bg-green-500' : 'bg-gray-600'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          aiConfig.send_menu_images_enabled !== false ? 'translate-x-7' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Response Delay Configuration */}
                <div className="bg-gradient-to-br from-cyan-500/10 to-emerald-500/10 border border-cyan-500/30 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <label className="text-sm font-medium text-cyan-300">
                      ⏱️ Tempo de Espera para Resposta
                    </label>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max="60"
                      step="5"
                      value={aiConfig.response_delay ?? 15}
                      onChange={(e) => setAiConfig({...aiConfig, response_delay: parseInt(e.target.value)})}
                      className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                    />
                    <div className="flex items-center gap-1 bg-cyan-900/50 px-3 py-2 rounded-lg min-w-[80px] justify-center">
                      <span className="text-cyan-300 font-bold text-lg">
                        {aiConfig.response_delay === 0 ? '⚡' : (aiConfig.response_delay ?? 15)}
                      </span>
                      <span className="text-cyan-400 text-sm">
                        {aiConfig.response_delay === 0 ? 'Instantâneo' : 'seg'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-gray-900/50 rounded-lg p-3 text-xs space-y-2">
                    <p className="text-gray-300">
                      <strong className="text-cyan-400">💡 Como funciona:</strong> Quando o cliente envia uma mensagem, a IA aguarda este tempo antes de responder.
                    </p>
                    <p className="text-gray-400">
                      Se o cliente enviar mais mensagens durante esse período, o timer reinicia e todas as mensagens são agrupadas em uma única resposta.
                    </p>
                    <p className="text-gray-500 italic">
                      Isso evita que a IA responda várias vezes quando o cliente envia mensagens em sequência rápida.
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 pt-1">
                    <span>⚡ Instantâneo (0s)</span>
                    <span>Recomendado: 15-20s</span>
                    <span>🐢 Lento (60s)</span>
                  </div>
                </div>

                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Habilitar IA</p>
                    <p className="text-gray-400 text-sm">Processar pedidos automaticamente via IA</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={aiConfig.enabled}
                      onChange={(e) => setAiConfig({...aiConfig, enabled: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>

                {/* Save Button */}
                <button
                  onClick={handleSaveAiConfig}
                  disabled={savingAi}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-4 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingAi ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      Salvar Configurações de IA
                    </>
                  )}
                </button>

                {/* Reload AI Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleReloadAi(false)}
                    disabled={reloadingAi}
                    className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                  >
                    {reloadingAi ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Atualizando...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={16} />
                        Atualizar IA
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => handleReloadAi(true)}
                    disabled={reloadingAi}
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                  >
                    {reloadingAi ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Reiniciando...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={16} />
                        Reiniciar + Limpar
                      </>
                    )}
                  </button>
                </div>

                {/* Info about reload buttons */}
                <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-lg p-4 text-sm">
                  <p className="text-emerald-300 font-semibold mb-2">💡 Quando usar estes botões?</p>
                  <div className="space-y-2 text-emerald-200 text-xs">
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-400">🔄</span>
                      <div>
                        <strong>Atualizar IA:</strong> Use após salvar novas configurações (personalidade, produtos, horários). A IA aplicará as mudanças nas próximas mensagens.
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-orange-400">🔄</span>
                      <div>
                        <strong>Reiniciar + Limpar:</strong> Use se a IA estiver confusa ou com respostas erradas. Apaga TODO o histórico de conversas e reinicia do zero.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info Card */}
              <div className="glass-card">
                <h3 className="text-lg font-semibold text-white mb-3">ℹ️ Como Funciona</h3>
                <div className="space-y-3 text-sm text-gray-300">
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <p className="font-semibold text-white mb-1">📱 Recebimento de Pedidos</p>
                    <p>A IA processará as mensagens recebidas no WhatsApp e identificará pedidos automaticamente</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <p className="font-semibold text-white mb-1">🤖 Processamento Inteligente</p>
                    <p>Extrai itens, quantidades, endereço e forma de pagamento das mensagens dos clientes</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <p className="font-semibold text-white mb-1">✅ Confirmação Automática</p>
                    <p>A IA confirma os pedidos com o cliente antes de salvar no sistema</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <p className="font-semibold text-white mb-1">🔔 Notificações de Status</p>
                    <p>Envia atualizações automáticas sobre o status do pedido diretamente no WhatsApp</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Greeting Message Tab */}
          {activeTab === 'greeting' && (
            <GreetingMessageSettings />
          )}

          {/* Away Message Tab */}
          {activeTab === 'away' && (
            <AwayMessageSettings />
          )}
        </div>
      )}
    </div>
  );
};

// WhatsAppContent component ends here

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Main App Component
function App() {
  // Check if we're on a store subdomain
  const storeSubdomain = extractSubdomain();
  const isOnStoreSubdomain = isStoreSubdomain();
  
  // If on a store subdomain, render only the public menu
  if (isOnStoreSubdomain && storeSubdomain) {
    return (
      <div className="App">
        <Toaster position="top-right" expand={false} richColors />
        <BrowserRouter>
          <Routes>
            {/* All routes on store subdomain show the public menu */}
            <Route path="*" element={<PublicMenu subdomain={storeSubdomain} />} />
          </Routes>
        </BrowserRouter>
      </div>
    );
  }
  
  // Componente para redirecionar baseado no estado de autenticação
  // Para PWA/atalho: pula a landing page e vai direto pro login ou dashboard
  const SmartHomeRoute = () => {
    const token = localStorage.getItem('token');
    const savedRestaurant = localStorage.getItem('restaurant');
    
    // Verificar se é um acesso via PWA (standalone mode)
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.navigator.standalone === true ||
                  document.referrer.includes('android-app://');
    
    // Se tem credenciais salvas, vai direto pro dashboard
    if (token && savedRestaurant) {
      return <Navigate to="/dashboard" replace />;
    }
    
    // Se é PWA ou tem indicação de acesso direto, vai pro login
    if (isPWA) {
      return <Navigate to="/login" replace />;
    }
    
    // Caso contrário, mostra a landing page normal
    return <LandingPage />;
  };

  // Normal app routing for main domain
  return (
    <AuthProvider>
      <div className="App">
        <Toaster position="top-right" expand={false} richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<SmartHomeRoute />} />
            <Route path="/login" element={<LoginPage />} />
            {/* Legacy route - will be redirected by backend in production */}
            <Route path="/menu/:subdomain" element={<PublicMenuRoute />} />
            {/* Rota /cardapio/:subdomain */}
            <Route path="/cardapio/:subdomain" element={<PublicMenuRoute />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
